import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './context/AuthContext';
import { auth, db, functions } from './firebase';
import ReaderOrders from './ReaderOrders';
import ReaderOrderHistory from './ReaderOrderHistory';
import ReaderAllOrders from './ReaderAllOrders';
import ReadingHistoryImport from './ReadingHistoryImport';
import ReaderBookshelf from './ReaderBookshelf';
import ReaderLibrary from './ReaderLibrary';
import ReaderResume from './ReaderResume';
import { callClaude } from './anthropic';
import { normaliseStatus } from './constants/arcStatus';
import { bucketProductType } from './lib/productCategory';
import { computeBlockShipping } from './lib/shopShipping';
import {
  createUserWithEmailAndPassword, signInWithEmailAndPassword,
  signOut, onAuthStateChanged, sendPasswordResetEmail, sendEmailVerification,
  updateProfile
} from 'firebase/auth';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { Card } from './components/ui/card';
import { Badge } from './components/ui/badge';
import { Textarea } from './components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './components/ui/dialog';
import {
  collection, collectionGroup, addDoc, query, where, onSnapshot, doc,
  getDocs, setDoc, getDoc, updateDoc, deleteDoc, serverTimestamp, increment,
  orderBy, limit, arrayUnion
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import {
  BookOpen, Star, Gift, LogOut, User, Award, TrendingUp, Download,
  ExternalLink, CheckCircle, Clock, Mail, MessageCircle, Heart, Send,
  Package, Trash2, Pin, ChevronLeft, ChevronRight, Plus, X, Tag, MoreVertical,
  Calendar as CalendarIcon, FileText, Image as ImageIcon, ArrowLeft, Copy,
  Bell, Printer, Edit3, Inbox, RefreshCw, Sparkles, ChevronDown, ChevronUp, ThumbsDown, Info,
  Headphones, ShoppingCart, Loader2, BookText, CreditCard, Minus, MapPin
} from 'lucide-react';
import { BOOK_TAXONOMY, cycleTagToggle, resolveBookTags } from './lib/bookTaxonomy';
import { normaliseCampaignStatus, ACTIVE_CAMPAIGN_STATUSES } from './lib/campaignStatus';
import AvailableArcs from './author/AvailableArcs';
import SeriesPicker, { buildSeriesOptions, getRelevantAuthorIds } from './reader/SeriesPicker';

/* ─────────────────────────────────────────────
   COLOUR HELPERS
───────────────────────────────────────────── */
const EVENT_COLORS = {
  launch:          { dot: 'bg-teal-400',  badge: 'bg-slate-100 text-slate-800',  label: '🚀 Launch' },
  'review-deadline': { dot: 'bg-red-400',   badge: 'bg-red-100 text-red-800',      label: '📝 Review Deadline' },
  'street-team':   { dot: 'bg-purple-400', badge: 'bg-purple-100 text-purple-800', label: '⭐ Street Team' },
  other:           { dot: 'bg-blue-400',   badge: 'bg-blue-100 text-blue-800',     label: '📌 Note' },
};

const FALLBACK_COVER = 'https://images.squarespace-cdn.com/content/68247682061cc776f130e7ed/552ee632-8a04-43da-b258-118af34f36ae/9781918400045.png?content-type=image%2Fpng';
const TEST_AUTHOR_ID = 'rkfAMFsjezU5SgVj8eiU7PvsgJM2';

// Resolve authorId from URL → campaign context → reader-relationship lookup.
// Returns null if nothing usable is available, or if the only candidate is the test account.
function resolveAuthorIdFromContext({ campaignsByBook, readerData, bookIdHint } = {}) {
  let candidate = '';
  try {
    candidate = (new URLSearchParams(window.location.search).get('authorId') || '').trim();
  } catch { /* no window */ }
  if (candidate && candidate !== TEST_AUTHOR_ID) return candidate;

  if (bookIdHint && campaignsByBook && Array.isArray(campaignsByBook[bookIdHint])) {
    const candidate = campaignsByBook[bookIdHint].find(c => c.authorId && c.authorId !== TEST_AUTHOR_ID);
    if (candidate) return candidate.authorId;
  }

  const sel = readerData?.selectedAuthorId;
  if (sel && sel !== TEST_AUTHOR_ID) return sel;

  return null;
}

function redirectToSelectAuthor() {
  const returnTo = encodeURIComponent(window.location.pathname + window.location.search);
  window.location.href = `/reader/select-author?returnTo=${returnTo}`;
}

const stripBookSuffix = (title) => (title || '').replace(/\s*-\s*book\s*\d+$/i, '');
const stripBlurbEmoji = (text) => (text || '').replace(/[\u{1F7E7}\u{1F7E5}\u{1F7E6}\u{1F7E8}\u{1F7E9}\u{1F7EA}\u{1F7EB}\u{2B1B}\u{2B1C}\u{25FC}\u{25FB}\u{25FE}\u{25FD}]+/gu, '').trim();

const PLATFORMS = ['Amazon', 'Goodreads', 'Instagram', 'TikTok', 'Fable', 'Storygraph', 'BookBub', 'Blog', 'Other'];
const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAY_NAMES   = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

/* ─────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────── */

export default function ReaderPortal() {
  const { isAuthor: hasAuthorRole } = useAuth();

  /* ── AUTH ── */
  const [currentReader, setCurrentReader]   = useState(null);
  const [isLoading, setIsLoading]           = useState(true);
  const [isSignup, setIsSignup]             = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('signup') === 'true';
  });
  const [email, setEmail]                   = useState('');
  const [password, setPassword]             = useState('');
  const [name, setName]                     = useState('');
  const [authError, setAuthError]           = useState('');
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [resetEmail, setResetEmail]         = useState('');
  const pendingSignupNameRef = useRef(null); // carries signup form name to the onAuthStateChanged useEffect
  const [showPassword, setShowPassword]     = useState(false);
  const [resetMessage, setResetMessage]     = useState('');
  const [resetError, setResetError]         = useState('');

  /* ── PRE-AUTH LANDING ── */
  // null = show landing, 'login'/'signup'/'buy' = show auth form
  const [landingIntent, setLandingIntent]   = useState(null);
  const [landingBook, setLandingBook]       = useState(null);
  const [landingAuthorName, setLandingAuthorName] = useState('Julieanna Mac');
  const [landingHasProducts, setLandingHasProducts] = useState(true); // default true so button stays enabled until we know
  const pendingBuyIntentRef = useRef(false); // persists "buy" intent across auth state change

  /* ── GUEST MODE ── */
  const [signupMode, setSignupMode]         = useState(null); // null | 'register' | 'guest'
  const [guestData, setGuestData]           = useState(null);
  const [isGuestUser, setIsGuestUser]       = useState(false);
  const [guestAccessSettings, setGuestAccessSettings] = useState(null);
  const [emailVerified, setEmailVerified]   = useState(false);
  const [verifyCheckCount, setVerifyCheckCount] = useState(0);
  const [resendStatus, setResendStatus]     = useState('');
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeName, setUpgradeName]       = useState('');
  const [upgradePassword, setUpgradePassword] = useState('');
  const [upgradeError, setUpgradeError]     = useState('');
  const [upgrading, setUpgrading]           = useState(false);
  const [guestForm, setGuestForm]           = useState({
    name: '', email: '', password: '',
    shippingAddress: { line1: '', line2: '', city: '', postalCode: '', country: '' },
  });

  /* ── FIRESTORE DATA ── */
  const [readerData, setReaderData]         = useState(null);
  const [allBooks, setAllBooks]             = useState([]);
  const [myReviews, setMyReviews]           = useState([]);
  const [rewards, setRewards]               = useState([]);
  const [myRedemptions, setMyRedemptions]   = useState([]);
  const [arcRequests, setArcRequests]       = useState([]);
  const [latestAnnouncement, setLatestAnnouncement] = useState(null);
  const [authorAnnouncements, setAuthorAnnouncements] = useState([]);
  const [authorsMap, setAuthorsMap]         = useState({}); // { [authorId]: { id, name, penName, ... } }
  const [showSeriesSwitcher, setShowSeriesSwitcher] = useState(false);
  const [posts, setPosts]                   = useState([]);
  const [postComments, setPostComments]     = useState({});
  const [postLikes, setPostLikes]           = useState({});

  /* ── NAVIGATION ── */
  const [activeView, setActiveView]         = useState(() => {
    const hash = window.location.hash.replace('#', '');
    const validViews = ['home', 'profile', 'available-arcs', 'library', 'orders', 'calendar', 'messages', 'book-detail'];
    return validViews.includes(hash) ? hash : 'home';
  });
  // 'home' | 'calendar' | 'arc-log' | 'community' | 'profile' | 'book-detail'
  const [selectedBookId, setSelectedBookId] = useState(null);
  const [bookDetailTab, setBookDetailTab]   = useState('overview');

  /* ── REVIEW FORM ── */
  const [selectedBook, setSelectedBook]     = useState('');
  const [reviewLinks, setReviewLinks]       = useState({
    amazon: '', goodreads: '', instagram: '', tiktok: '',
    blog: '', bookbub: '', storygraph: '', netgalley: '', fable: ''
  });
  const [readerComments, setReaderComments]   = useState('');
  const [reviewRating, setReviewRating]       = useState(0);
  const [hoverRating, setHoverRating]         = useState(0);
  const [quickSummary, setQuickSummary]       = useState('');
  const [lovedMost, setLovedMost]             = useState('');
  const [lovedMostDetails, setLovedMostDetails] = useState('');
  const [favoriteCharacterMoment, setFavoriteCharacterMoment] = useState('');
  const [targetAudience, setTargetAudience]   = useState([]);
  const [fullReview, setFullReview]           = useState('');
  const [favoriteQuote, setFavoriteQuote]     = useState('');
  const [privateNotes, setPrivateNotes]       = useState('');
  const [readerPath, setReaderPath]           = useState('');

  /* ── PROFILE ── */
  const [profileForm, setProfileForm] = useState({
    firstName: '', lastName: '', eReaderEmail: '', phoneNumber: '',
    shippingAddress: { line1: '', line2: '', city: '', postalCode: '', country: '' },
    socialMedia:     { instagram: '', tiktok: '', goodreads: '', amazon: '', other: '' },
    preferences:     { favoriteGenres: [], favoriteTropes: '' }
  });
  const [deviceEmails, setDeviceEmails] = useState({ kindleEmail: '', koboEmail: '' });
  const [saveMessage, setSaveMessage] = useState('');

  /* ── ARC REQUEST MODAL (legacy — kept for backward compat, not rendered) ── */
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestingBook, setRequestingBook]     = useState(null);
  const [requestMessage, setRequestMessage]     = useState('');

  /* ── SELECT BOOK MODAL (new 3-option flow) ── */
  const [selectBookModal, setSelectBookModal]           = useState(null); // book object or null
  const [selectBookOption, setSelectBookOption]         = useState(null); // 1, 2, or null
  const [selectBookMessage, setSelectBookMessage]       = useState('');
  const [selectBookSubmitting, setSelectBookSubmitting] = useState(false);
  const [selectBookSuccess, setSelectBookSuccess]       = useState(null); // success message or null
  const [campaignsByBook, setCampaignsByBook]           = useState({}); // bookId → Array<{ campaignId, authorId, authorName, campaignStatus }>
  const [campaignAppStatuses, setCampaignAppStatuses]   = useState({}); // bookId → status string

  /* ── COMMUNITY ── */
  const [showCreatePost, setShowCreatePost]   = useState(false);
  const [newPostContent, setNewPostContent]   = useState('');
  const [showCommentsFor, setShowCommentsFor] = useState(null);
  const [newComment, setNewComment]           = useState({});

  /* ── DIRECT MESSAGES (thread-based) ── */
  const [dmMessages, setDmMessages]         = useState([]);
  const [dmInput, setDmInput]               = useState('');
  const [dmUnreadCount, setDmUnreadCount]   = useState(0);
  const [myThreads, setMyThreads]           = useState([]);
  const [selectedThreadId, setSelectedThreadId] = useState(null);
  const [selectedThreadMessages, setSelectedThreadMessages] = useState([]);
  const [deleteThreadId, setDeleteThreadId] = useState(null);
  const [showDeleteMenu, setShowDeleteMenu] = useState(null);
  const [composeStep, setComposeStep]       = useState(null); // null | 'type' | 'book' | 'platform' | 'compose'
  const [composeType, setComposeType]       = useState(null); // 'book_feedback' | 'platform_support'
  const [composeBookId, setComposeBookId]   = useState(null);
  const [composePlatformType, setComposePlatformType] = useState('general'); // 'general' | 'author_related'
  const [composePlatformAuthorId, setComposePlatformAuthorId] = useState(null);
  const [composeText, setComposeText]       = useState('');
  const [isSendingCompose, setIsSendingCompose] = useState(false);
  const [myApprovedApps, setMyApprovedApps] = useState([]); // campaign applications with authorId

  /* ── CALENDAR ── */
  const [calendarDate, setCalendarDate]       = useState(new Date());
  const [selectedDay, setSelectedDay]         = useState(null);
  const [manualEvents, setManualEvents]       = useState([]);
  const [showAddEvent, setShowAddEvent]       = useState(false);
  const [calendarViewMode, setCalendarViewMode] = useState('calendar');
  const [calendarFilter, setCalendarFilter]   = useState('all');
  const [newEvent, setNewEvent]               = useState({
    bookSelection: '', customTitle: '', launchDate: '', reviewDate: '',
    streetTeamDate: '', streetTeamRequested: false, note: '',
    type: 'other', date: '',
  });

  /* ── ARC LOG ── */
  const [arcLogEntries, setArcLogEntries]   = useState([]);
  const [showAddArc, setShowAddArc]         = useState(false);
  const [newArcEntry, setNewArcEntry]       = useState({
    bookTitle: '', author: '', isbn: '', reviewedOn: '', platforms: [], receivedVia: ''
  });

  /* ── FOR YOU — DATABASE-FIRST RECOMMENDATIONS ── */
  const [forYouPrefs, setForYouPrefs]           = useState({});        // reader preferences (want/avoid)
  const [forYouPrefsForm, setForYouPrefsForm]   = useState({});
  const [forYouShowPrefs, setForYouShowPrefs]   = useState(false);
  const [forYouSavingPrefs, setForYouSavingPrefs] = useState(false);
  const [forYouExpandedSections, setForYouExpandedSections] = useState({});
  const [forYouResults, setForYouResults]       = useState([]);        // matched books from DB
  const [forYouAiResults, setForYouAiResults]   = useState([]);        // Claude fallback results
  const [forYouLoading, setForYouLoading]       = useState(false);
  const [forYouError, setForYouError]           = useState('');
  const [forYouDbCount, setForYouDbCount]       = useState(0);         // total DB matches
  const [forYouAllBooks, setForYouAllBooks]     = useState([]);        // all books for CSV import
  const [forYouShowImport, setForYouShowImport] = useState(false);
  const [bookshelfRefreshKey, setBookshelfRefreshKey] = useState(0);
  const [showResume, setShowResume] = useState(false);
  // Tag a Book flow
  const [tagSearchQuery, setTagSearchQuery]     = useState('');
  const [tagSearchResults, setTagSearchResults] = useState([]);
  const [tagSearching, setTagSearching]         = useState(false);
  const [tagBookId, setTagBookId]               = useState(null);      // book being tagged
  const [tagBookData, setTagBookData]           = useState(null);      // metadata of book being tagged
  const [tagForm, setTagForm]                   = useState({});
  const [tagExpandedSections, setTagExpandedSections] = useState({});
  const [tagSubmitting, setTagSubmitting]       = useState(false);
  const [tagMessage, setTagMessage]             = useState('');
  const [tagMySubmission, setTagMySubmission]   = useState(null);

  /* ── BOOK DETAIL: PURCHASE OPTIONS ── */
  const [bookDetailEbooks, setBookDetailEbooks]       = useState([]);
  const [bookDetailAudiobooks, setBookDetailAudiobooks] = useState([]);
  const [bookDetailProducts, setBookDetailProducts]   = useState([]);
  /* ── INLINE SHOP CART (physical products) ── */
  const [shopCart, setShopCart]                       = useState({}); // { [productId]: quantity }
  const [shopCheckingOut, setShopCheckingOut]         = useState(false);
  const [shopEditAddress, setShopEditAddress]         = useState(false);
  const [shopAddress, setShopAddress]                 = useState({ line1: '', line2: '', city: '', postalCode: '', country: '' });
  const [promoCode, setPromoCode]                     = useState('');
  const [promoApplied, setPromoApplied]               = useState(null); // { code, discountMode, discountType, discountValue, productOverrides, id }
  const [promoError, setPromoError]                   = useState('');
  const [promoValidating, setPromoValidating]         = useState(false);
  const [bookDetailLoadingShop, setBookDetailLoadingShop] = useState(false);
  const [bookDetailClaimingId, setBookDetailClaimingId] = useState(null);
  const [portalWaitlistStatus, setPortalWaitlistStatus] = useState({}); // { [productId]: 'on' | 'joining' }

  /* ── DERIVED ── */
  // "Your ARC Books" shows books the reader has access to via ANY approval path:
  // approved campaign application, booksReceived array, or approved arcRequest
  const approvedArcBookIds = new Set([
    ...myApprovedApps.map(a => a.bookId),
    ...(readerData?.booksReceived || []),
    ...arcRequests.filter(r => r.status === 'approved').map(r => r.bookId),
  ]);

  /* ── SERIES (= AUTHOR) SELECTION ──
     Series in this codebase = author. One author = one series. The reader
     picks which series they want to engage with, and every downstream view
     filters to that author. The selection is persisted on the reader doc as
     `selectedAuthorId`. */
  const urlAuthorId = (() => {
    try { return new URLSearchParams(window.location.search).get('authorId') || null; }
    catch { return null; }
  })();
  const activeAuthorId = urlAuthorId || readerData?.selectedAuthorId || null;
  const filterByActiveAuthor = (list) =>
    activeAuthorId ? list.filter(item => item && item.authorId === activeAuthorId) : list;

  const myBooksAll    = allBooks.filter(b => approvedArcBookIds.has(b.id));
  // My Books / My Library is always platform-wide — a reader's accepted ARCs
  // from any author should always be visible, regardless of activeAuthorId.
  const myBooks       = myBooksAll;

  // Launched books from the active series that the reader does NOT already
  // have approved access to. These are surfaced in "Your ARC Books" with a
  // "Buy this Book" CTA (or a "Pending" badge if the reader has a pending
  // application). Always filtered by activeAuthorId so the series picker rules.
  const purchasableBooks = filterByActiveAuthor(
    allBooks.filter(b => b.status === 'launched' && !approvedArcBookIds.has(b.id))
  );
  const availableArcs = filterByActiveAuthor(allBooks.filter(b => {
    if (!(b.arcAvailable === true || b.ARC === 'Yes')) return false;
    // Exclude books whose own author's campaign is already live (launched).
    // Closed campaigns no longer enter campaignsByBook (filtered at construction).
    const camps = campaignsByBook[b.id] || [];
    const ownAuthorCamp = camps.find(c => c.authorId === b.authorId);
    if (ownAuthorCamp && ownAuthorCamp.campaignStatus === 'live') return false;
    return true;
  }));
  const filteredApprovedApps = filterByActiveAuthor(myApprovedApps);

  // Authors the reader has *some* relationship with — used to decide whether
  // to auto-select a series or show the picker.
  const relevantAuthorIds = getRelevantAuthorIds({ allBooks, myApprovedApps, readerData, arcRequests });

  // Series options for the picker. If the reader has any relevant authors,
  // restrict to those; otherwise show every author with reader-facing books.
  const seriesOptions = buildSeriesOptions(
    allBooks,
    authorsMap,
    relevantAuthorIds.size > 0 ? relevantAuthorIds : null
  );

  // The picker should appear when the reader is signed in, has loaded their
  // reader doc, has not yet picked a series, AND there is more than one option
  // for them. (If there's only one, the auto-select effect below picks it.)
  const needsSeriesPicker =
    !!readerData && !isGuestUser && !activeAuthorId && seriesOptions.length > 1;

  const activeSeriesOption = activeAuthorId
    ? seriesOptions.find(o => o.authorId === activeAuthorId) ||
      buildSeriesOptions(allBooks, authorsMap, new Set([activeAuthorId]))[0] ||
      null
    : null;

  const selectedBookObj = allBooks.find(b => b.id === selectedBookId);
  const books = availableArcs; // alias used by review form

  // Profile completeness: has address AND at least one social media handle
  const hasAddress = !!(profileForm.shippingAddress?.line1 && profileForm.shippingAddress?.city && profileForm.shippingAddress?.postalCode);
  const hasSocial = !!(profileForm.socialMedia?.instagram || profileForm.socialMedia?.tiktok || profileForm.socialMedia?.goodreads || profileForm.socialMedia?.amazon || profileForm.socialMedia?.other);
  const profileComplete = hasAddress && hasSocial;

  /* ── MY LIBRARY SUB-TAB ── */
  const [librarySubTab, setLibrarySubTab] = useState('current-arcs');

  /* ── ANNOUNCEMENT DISMISSAL ── */
  const [announcementDismissed, setAnnouncementDismissed] = useState(false);

  /* Post-auth redirect: if user signed up/logged in via "Buy Now", go to book detail */
  useEffect(() => {
    if (currentReader && pendingBuyIntentRef.current) {
      pendingBuyIntentRef.current = false;
      setSelectedBookId('book1');
      setActiveView('book-detail');
      setBookDetailTab('overview');
    }
  }, [currentReader]);

  /* Pre-select book when navigating to review tab from a specific book */
  useEffect(() => {
    if (activeView === 'book-detail' && bookDetailTab === 'review' && selectedBookId) {
      setSelectedBook(selectedBookId);
    }
  }, [activeView, bookDetailTab, selectedBookId]);

  /* Load purchase options (ebooks, audiobooks, physical products) for book detail */
  useEffect(() => {
    if (activeView !== 'book-detail' || !selectedBookId) {
      setBookDetailEbooks([]);
      setBookDetailAudiobooks([]);
      setBookDetailProducts([]);
      return;
    }
    const bookApp = myApprovedApps.find(a => a.bookId === selectedBookId);
    const authorId = bookApp?.authorId || selectedBookObj?.authorId || 'uzMXPEok4AVIw8luvjAeNLlWy743';
    setBookDetailLoadingShop(true);
    const unsubs = [];

    // Ebooks linked to this book
    const ebQ = query(collection(db, 'authors', authorId, 'ebooks'), where('bookId', '==', selectedBookId));
    unsubs.push(onSnapshot(ebQ, snap => {
      const items = [];
      snap.forEach(d => {
        const data = { id: d.id, ...d.data(), authorId };
        if (data.status === 'live' || data.preOrder?.preOrderEnabled) items.push(data);
      });
      setBookDetailEbooks(items);
      setBookDetailLoadingShop(false);
    }, () => setBookDetailLoadingShop(false)));

    // Audiobooks linked to this book
    const abQ = query(collection(db, 'authors', authorId, 'audiobooks'), where('bookId', '==', selectedBookId));
    unsubs.push(onSnapshot(abQ, snap => {
      const items = [];
      snap.forEach(d => {
        const data = { id: d.id, ...d.data(), authorId };
        if (data.status === 'live' || data.preOrder?.preOrderEnabled) items.push(data);
      });
      setBookDetailAudiobooks(items);
    }, () => {}));

    // Physical products for this book
    unsubs.push(onSnapshot(
      collection(db, 'authors', authorId, 'books', selectedBookId, 'products'),
      snap => {
        const items = [];
        snap.forEach(d => {
          const data = d.data();
          if (data.available) items.push({ id: d.id, ...data });
        });
        setBookDetailProducts(items);
      },
      () => {}
    ));

    return () => unsubs.forEach(u => u());
  }, [activeView, selectedBookId, myApprovedApps, selectedBookObj]);

  /* Load waitlist status for current reader's products */
  useEffect(() => {
    if (!currentReader || activeView !== 'book-detail' || !selectedBookId) {
      setPortalWaitlistStatus({});
      return;
    }
    const bookApp = myApprovedApps.find(a => a.bookId === selectedBookId);
    const authorId = bookApp?.authorId || selectedBookObj?.authorId || 'uzMXPEok4AVIw8luvjAeNLlWy743';
    const q = query(
      collection(db, 'authors', authorId, 'waitlist'),
      where('email', '==', currentReader.email)
    );
    const unsub = onSnapshot(q, (snap) => {
      const status = {};
      snap.forEach(d => {
        const data = d.data();
        if (data.productId) status[data.productId] = 'on';
      });
      setPortalWaitlistStatus(status);
    });
    return unsub;
  }, [currentReader, activeView, selectedBookId, myApprovedApps, selectedBookObj]);

  const joinPortalWaitlist = async (product, authorId) => {
    if (!currentReader || !authorId || portalWaitlistStatus[product.id]) return;
    setPortalWaitlistStatus(prev => ({ ...prev, [product.id]: 'joining' }));
    try {
      const waitlistDocId = `${currentReader.uid}_${product.id}`;
      await setDoc(doc(db, 'authors', authorId, 'waitlist', waitlistDocId), {
        readerId: currentReader.uid,
        name: readerData?.name || currentReader.displayName || currentReader.email,
        email: currentReader.email,
        productId: product.id,
        productName: product.name,
        bookId: selectedBookId || null,
        timestamp: serverTimestamp(),
      });
      setPortalWaitlistStatus(prev => ({ ...prev, [product.id]: 'on' }));
    } catch (err) {
      console.error('Waitlist join error:', err);
      setPortalWaitlistStatus(prev => {
        const next = { ...prev };
        delete next[product.id];
        return next;
      });
    }
  };

  /* ── INLINE SHOP HELPERS ── */
  // Per-product/per-zone shipping has been replaced by a single block-of-five
  // charge for the whole order (see computeBlockShipping / shopTotals below),
  // matching the authoritative server calc in createOrderCheckout.
  const shopHasItems = Object.keys(shopCart).length > 0;

  // Helper: calculate discounted price given original price, discount type, and discount value
  const calcDiscountedPrice = (originalPrice, discountType, discountValue) => {
    if (!discountValue || discountValue <= 0) return originalPrice;
    if (discountType === 'percentage') {
      return Math.round(originalPrice * (1 - discountValue / 100) * 100) / 100;
    }
    // fixed
    return Math.max(0, Math.round((originalPrice - discountValue) * 100) / 100);
  };

  // Helper: get effective price for a product (with promo applied). The
  // category bucket is derived per-product so mixed carts (ebook + physical)
  // each pick up their own perProductDiscounts entry.
  const getPromoPrice = (product) => {
    if (!promoApplied) return null;
    const mode = promoApplied.discountMode || 'all';
    if (mode === 'per_product') {
      const overrides = promoApplied.productOverrides || {};
      const hasOverrides = Object.keys(overrides).length > 0;
      const ppd = promoApplied.perProductDiscounts || {};
      const bucket = bucketProductType(product);
      const categoryAmount = ppd[bucket];
      const ov = overrides[product.id];
      if (ov) {
        // Product is whitelisted: apply category discount for its bucket.
        if (categoryAmount !== undefined) {
          return calcDiscountedPrice(Number(product.price), promoApplied.discountType, categoryAmount);
        }
        return null;
      }
      if (hasOverrides) return null; // whitelist exists but this product is not on it
      // No whitelist at all: every product of this bucket gets the discount.
      if (categoryAmount !== undefined) {
        return calcDiscountedPrice(Number(product.price), promoApplied.discountType, categoryAmount);
      }
      return null;
    }
    // "all" mode
    return calcDiscountedPrice(Number(product.price), promoApplied.discountType, promoApplied.discountValue);
  };

  // Helper: get discount value for a given product type (ebook/audiobook/physical)
  const getPromoDiscountForType = (productType) => {
    if (!promoApplied) return null;
    const mode = promoApplied.discountMode || 'all';
    if (mode === 'all') return { type: promoApplied.discountType, value: promoApplied.discountValue };
    // per_product — look up category
    const ppd = promoApplied.perProductDiscounts || {};
    if (ppd[productType] !== undefined) return { type: promoApplied.discountType, value: ppd[productType] };
    return null;
  };

  // Cart totals. Discount applies to the product subtotal only. Shipping is a
  // single block-of-five charge (one block per five physical books) mirroring
  // the authoritative server calc in createOrderCheckout, so this preview
  // matches what the reader is charged. Free-shipping promos zero it.
  const shopTotals = (() => {
    let subtotal = 0, originalSubtotal = 0;
    const items = [];
    const cartProducts = [];
    for (const [pid, qty] of Object.entries(shopCart)) {
      const p = bookDetailProducts.find(x => x.id === pid);
      if (!p) continue;
      const promoPrice = getPromoPrice(p);
      const effectivePrice = promoPrice != null ? promoPrice : p.price;
      originalSubtotal += p.price * qty;
      subtotal += effectivePrice * qty;
      cartProducts.push({ product: p, quantity: qty });
      items.push({ product: p, qty, effectivePrice, originalPrice: p.price });
    }
    const addr = shopEditAddress ? shopAddress : readerData?.shippingAddress;
    const breakdown = computeBlockShipping(cartProducts, addr?.country);
    const shipping = promoApplied?.freeShipping ? 0 : breakdown.shippingCost;
    const discount = originalSubtotal - subtotal;
    return {
      subtotal, shipping, discount, total: subtotal + shipping, items, originalSubtotal,
      physicalUnits: breakdown.physicalUnits,
      shippingUnits: breakdown.shippingUnits,
      ratePerBlock: breakdown.ratePerBlock,
    };
  })();

  const validatePromoCode = async (detailAuthorId, productType) => {
    if (!promoCode.trim()) return;
    setPromoValidating(true);
    setPromoError('');
    try {
      const validate = httpsCallable(functions, 'validatePromoCode');
      const result = await validate({
        authorId: detailAuthorId,
        code: promoCode.trim(),
        productType: productType || 'physical',
      });
      const data = result.data;
      console.log('[Portal] validatePromoCode response:', data);
      if (data.valid) {
        setPromoApplied({
          id: data.promoCodeId,
          code: data.code,
          discountMode: data.discountMode || 'all',
          discountType: data.discountType,
          discountValue: data.discountValue,
          perProductDiscounts: data.perProductDiscounts || {},
          productOverrides: data.productOverrides || {},
          freeShipping: !!data.freeShipping,
        });
        setPromoError('');
      } else {
        setPromoError(data.reason || 'Invalid or expired promo code');
        setPromoApplied(null);
      }
    } catch (err) {
      console.error('Promo validation error:', err);
      setPromoError(err.message || 'Could not validate code');
    } finally {
      setPromoValidating(false);
    }
  };

  const removePromoCode = () => {
    setPromoApplied(null);
    setPromoCode('');
    setPromoError('');
  };
  const shopHasAddress = () => {
    const addr = shopEditAddress ? shopAddress : readerData?.shippingAddress;
    return !!(addr?.line1 && addr?.city && addr?.postalCode && addr?.country);
  };
  const shopDisplayName = (name) => {
    return name.replace(/^DELAYED\s*\([^)]*\)\s*-?\s*/i, '');
  };
  const shopIsDelayed = (name) => /^DELAYED\s*\(/i.test(name);
  const handleShopCheckout = async (detailAuthorId) => {
    if (!shopHasItems || !shopHasAddress()) return;
    setShopCheckingOut(true);
    try {
      const addr = shopEditAddress ? shopAddress : readerData.shippingAddress;
      const items = Object.entries(shopCart).map(([productId, quantity]) => ({
        productId, bookId: selectedBookId, quantity,
      }));
      const createOrderCheckout = httpsCallable(functions, 'createOrderCheckout');
      const result = await createOrderCheckout({
        items, bookId: selectedBookId, authorId: detailAuthorId,
        shippingAddress: addr,
        readerName: readerData?.name || readerData?.firstName || currentReader.email,
        ...(promoApplied ? { promoCodeId: promoApplied.id, promoCode: promoApplied.code } : {}),
      });
      if (result.data?.sessionUrl) window.location.href = result.data.sessionUrl;
    } catch (err) {
      console.error('Checkout error:', err);
      alert('Error starting checkout: ' + (err.message || 'Please try again'));
    } finally { setShopCheckingOut(false); }
  };

  /* ══════════════════════════════════════════════════
     USE EFFECTS — DATA LOADING
  ══════════════════════════════════════════════════ */

  // Load featured book for pre-auth landing page. Always keyed off the
  // ?authorId= URL param — no silent fallback to a default tenant.
  useEffect(() => {
    // Known data quirk: Julieanna's book1 has an empty coverUrl field; we
    // patch it in only when the URL param explicitly identifies that author.
    const JULIEANNA_KNOWN_ID = 'uzMXPEok4AVIw8luvjAeNLlWy743';
    const BORN_FROM_THE_BROKEN_FALLBACK =
      'https://images.squarespace-cdn.com/content/68247682061cc776f130e7ed/552ee632-8a04-43da-b258-118af34f36ae/9781918400045.png?content-type=image%2Fpng';
    const urlAuthor = (() => {
      try { return new URLSearchParams(window.location.search).get('authorId') || null; }
      catch { return null; }
    })();
    if (!urlAuthor) {
      // No author context — leave landingBook null so the landing UI
      // surfaces the author-selection path instead of silently steering
      // every visitor to one tenant.
      setLandingBook(null);
      return;
    }
    const effectiveAuthor = urlAuthor;

    async function load() {
      try {
        const booksSnap = await getDocs(collection(db, 'authors', effectiveAuthor, 'books'));
        const books = booksSnap.docs.map(d => ({ id: d.id, authorId: effectiveAuthor, ...d.data() }));
        // Prefer launched > pre_launch_active > any non-archived.
        const pick =
          books.find(b => b.status === 'launched') ||
          books.find(b => b.status === 'pre_launch_active') ||
          books.find(b => b.status !== 'archived' && b.status !== 'draft') ||
          books[0] ||
          null;
        if (pick) {
          let coverUrl = pick.coverUrl && pick.coverUrl.trim ? pick.coverUrl.trim() : (pick.coverUrl || '');
          if (!coverUrl && effectiveAuthor === JULIEANNA_KNOWN_ID) {
            coverUrl = BORN_FROM_THE_BROKEN_FALLBACK;
          }
          if (!coverUrl) {
            const withCover = books.find(b => (b.coverUrl || '').trim && b.coverUrl.trim());
            if (withCover) coverUrl = withCover.coverUrl.trim();
          }
          setLandingBook({ ...pick, coverUrl });
        } else {
          console.warn('[ReaderPortal] No books for author', effectiveAuthor);
        }

        // Resolve author display name
        try {
          const brandingSnap = await getDoc(doc(db, 'authors', effectiveAuthor, 'settings', 'branding'));
          const authorSnap = await getDoc(doc(db, 'authors', effectiveAuthor));
          const b = brandingSnap.exists() ? brandingSnap.data() : {};
          const a = authorSnap.exists() ? authorSnap.data() : {};
          const name = b.displayName || a.penName || a.name || 'Author';
          setLandingAuthorName(name);
        } catch (err) {
          console.warn('[ReaderPortal] Branding/author fetch failed:', err?.message || err);
        }
      } catch (err) {
        console.warn('[ReaderPortal] Landing book fetch failed:', err?.message || err);
      }
    }
    load();
  }, []);

  // Check whether the landing book has any products — used to disable the Buy Now CTA if not.
  useEffect(() => {
    if (!landingBook?.id || !landingBook?.authorId) return;
    try {
      const prodsP = getDocs(collection(db, 'authors', landingBook.authorId, 'books', landingBook.id, 'products'));
      if (prodsP && typeof prodsP.then === 'function') {
        prodsP.then(snap => {
          setLandingHasProducts(!snap.empty);
        }).catch(() => setLandingHasProducts(true));
      }
    } catch { /* ignore in test env */ }
  }, [landingBook?.id, landingBook?.authorId]);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      setCurrentReader(user);
      if (user) {
        setEmailVerified(user.emailVerified);
        // Check if this is a guest user
        try {
          const guestSnap = await getDoc(doc(db, 'guests', user.uid));
          if (guestSnap.exists() && !guestSnap.data().upgradedToPortal) {
            setIsGuestUser(true);
            setGuestData({ id: guestSnap.id, ...guestSnap.data() });
          } else {
            setIsGuestUser(false);
            setGuestData(null);
          }
        } catch {
          setIsGuestUser(false);
        }
      } else {
        setEmailVerified(false);
        setIsGuestUser(false);
        setGuestData(null);
      }
      setIsLoading(false);
    });
    return () => unsub();
  }, []);

  /* Poll for email verification every 5 seconds (if not yet verified) */
  useEffect(() => {
    if (!currentReader || emailVerified) return;
    const interval = setInterval(async () => {
      try {
        await currentReader.reload();
        if (currentReader.emailVerified) {
          setEmailVerified(true);
          if (isGuestUser) {
            await updateDoc(doc(db, 'guests', currentReader.uid), { emailVerified: true });
          }
        }
      } catch { /* ignore */ }
    }, 5000);
    return () => clearInterval(interval);
  }, [currentReader, emailVerified, isGuestUser]);

  /* Load guest access settings for the relevant author */
  useEffect(() => {
    if (!currentReader || !isGuestUser) { setGuestAccessSettings(null); return; }
    const refAuthorId = guestData?.authorId || 'uzMXPEok4AVIw8luvjAeNLlWy743';
    getDoc(doc(db, 'authors', refAuthorId, 'settings', 'guestAccess')).then(snap => {
      if (snap.exists()) {
        setGuestAccessSettings(snap.data());
      } else {
        // Defaults: all on
        setGuestAccessSettings({ ebooks: true, audiobooks: true, physical: true });
      }
    }).catch(() => {
      setGuestAccessSettings({ ebooks: true, audiobooks: true, physical: true });
    });
  }, [currentReader, isGuestUser, guestData]);

  /* Reader document (auto-create if missing, auto-link if unlinked) — skip for guest users */
  useEffect(() => {
    if (!currentReader || isGuestUser) { setReaderData(null); return; }
    // emailCorrespondence is always stored lowercase (see create path below).
    // Lowercase the auth email here so a mixed-case sign-in still finds the doc.
    const normalisedEmail = (currentReader.email || '').toLowerCase();
    const q = query(collection(db, 'readers'), where('emailCorrespondence', '==', normalisedEmail));
    const unsub = onSnapshot(q, async (snap) => {
      // Consume the pending signup name (set by handleSignup before auth was created)
      const signupName = pendingSignupNameRef.current;
      pendingSignupNameRef.current = null;
      const displayName = signupName || currentReader.displayName || '';

      if (!snap.empty) {
        const d = snap.docs[0];
        const data = { id: d.id, ...d.data() };
        setReaderData(data);
        setProfileForm({
          firstName:       data.firstName || '',
          lastName:        data.lastName  || '',
          eReaderEmail:    data.eReaderEmail || '',
          phoneNumber:     data.phoneNumber || '',
          shippingAddress: data.shippingAddress || { line1:'', line2:'', city:'', postalCode:'', country:'' },
          socialMedia:     data.socialMedia || { instagram:'', tiktok:'', goodreads:'', amazon:'', other:'' },
          preferences:     data.preferences || { favoriteGenres:[], favoriteTropes:'' }
        });
        // Auto-link: if this reader doc isn't linked to the current auth user, link it now
        // (handles readers pre-created by Julie who then sign up via the portal)
        if (data.authUid !== currentReader.uid) {
          try {
            const linkName = displayName || data.name || '';
            await updateDoc(doc(db, 'readers', d.id), {
              authUid: currentReader.uid,
              accountLinked: true,
              selfSignupDate: data.selfSignupDate || new Date().toLocaleDateString(),
              ...(linkName && {
                name: linkName,
                firstName: linkName.split(' ')[0] || data.firstName || '',
                lastName: linkName.split(' ').slice(1).join(' ') || data.lastName || '',
              }),
            });
          } catch (err) { console.error('Failed to link reader:', err); }
        }
      } else {
        // No reader doc for this email — create one, but only with a resolved authorId.
        const resolvedAuthorId = resolveAuthorIdFromContext({ campaignsByBook });
        if (!resolvedAuthorId) {
          console.warn('[orphan-prevention] no authorId for new reader — redirecting to select-author', {
            authUid: currentReader.uid, email: currentReader.email,
          });
          redirectToSelectAuthor();
          return;
        }
        let urlBookId = '';
        try { urlBookId = (new URLSearchParams(window.location.search).get('bookId') || '').trim(); } catch {}
        const resolvedBookId = urlBookId || null;
        try {
          const newName = displayName || currentReader.email.split('@')[0];
          await addDoc(collection(db, 'readers'), {
            name: newName,
            firstName: newName.split(' ')[0] || '',
            lastName:  newName.split(' ').slice(1).join(' ') || '',
            emailCorrespondence: currentReader.email.toLowerCase(),
            status: 'Noted ARC interest',
            signupDate: new Date().toLocaleDateString(),
            selfSignupDate: new Date().toLocaleDateString(),
            totalPoints: 0,
            authUid: currentReader.uid,
            createdAt: serverTimestamp(),
            source: 'reader_portal',
            authorId: resolvedAuthorId,
            ...(resolvedBookId ? { bookId: resolvedBookId } : {}),
            shippingAddress: { line1:'', line2:'', city:'', postalCode:'', country:'' },
            socialMedia:     { instagram:'', tiktok:'', goodreads:'', amazon:'', other:'' },
            preferences:     { favoriteGenres:[], favoriteTropes:'' }
          });
        } catch (err) { console.error('Failed to create reader:', err); }
      }
    });
    return () => unsub();
  }, [currentReader, isGuestUser]);

  /* Load device emails. kindleEmail prefers readers/{id}.eReaderEmail
     (canonical home post-migration); falls back to readerProfiles for
     legacy data not yet migrated. koboEmail still lives on readerProfiles. */
  useEffect(() => {
    if (!currentReader) return;
    const profileRef = doc(db, 'readerProfiles', currentReader.uid);
    const unsub = onSnapshot(profileRef, (snap) => {
      const profileData = snap.exists() ? snap.data() : {};
      setDeviceEmails({
        kindleEmail: readerData?.eReaderEmail || profileData.kindleEmail || '',
        koboEmail: profileData.koboEmail || '',
      });
    });
    return () => unsub();
  }, [currentReader, readerData?.eReaderEmail]);

  /* All books — read from author-scoped books via collectionGroup */
  useEffect(() => {
    const unsub = onSnapshot(collectionGroup(db, 'books'), (snap) => {
      const list = [];
      snap.forEach(d => {
        const path = d.ref.path;
        const parts = path.split('/');
        if (parts[0] === 'authors' && parts.length === 4) {
          list.push({ id: d.id, ...d.data(), authorId: parts[1] });
        }
      });
      setAllBooks(list);
    });
    return () => unsub();
  }, []);

  /* Authors directory — used by the series picker + switcher to display
     each author's pen name and metadata. */
  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'authors'), (snap) => {
      const map = {};
      snap.forEach(d => { map[d.id] = { id: d.id, ...d.data() }; });
      setAuthorsMap(map);
    }, () => { /* read may be denied for some accounts — picker will fall back gracefully */ });
    return () => unsub();
  }, []);

  /* Auto-select a series when the reader has access to exactly one author.
     Persists the choice on the reader doc so it survives reloads. */
  const relevantAuthorIdsKey = Array.from(relevantAuthorIds).sort().join('|');
  useEffect(() => {
    if (!readerData || isGuestUser) return;
    if (readerData.selectedAuthorId) return;
    if (!relevantAuthorIdsKey) return;
    const ids = relevantAuthorIdsKey.split('|');
    if (ids.length !== 1) return;
    const onlyAuthorId = ids[0];
    if (!onlyAuthorId) return;
    updateDoc(doc(db, 'readers', readerData.id), { selectedAuthorId: onlyAuthorId })
      .catch(err => console.error('Failed to auto-select series:', err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readerData?.id, readerData?.selectedAuthorId, isGuestUser, relevantAuthorIdsKey]);

  /* My reviews — query across all bookReviews collections (top-level + author-scoped) */
  useEffect(() => {
    if (!currentReader) return;
    // Query top-level bookReviews (legacy)
    const legacyQ = query(collection(db, 'bookReviews'), where('readerEmail', '==', currentReader.email));
    // Query all tenant-scoped bookReviews via collectionGroup
    const tenantQ = query(collectionGroup(db, 'bookReviews'), where('readerEmail', '==', currentReader.email));
    const combined = new Map();
    const update = () => setMyReviews(Array.from(combined.values()));
    const unsub1 = onSnapshot(legacyQ, (snap) => {
      snap.forEach(d => combined.set(d.id, { id: d.id, ...d.data() }));
      // Remove deleted legacy docs
      const legacyIds = new Set(); snap.forEach(d => legacyIds.add(d.id));
      for (const [k, v] of combined) { if (!v.authorId && !legacyIds.has(k)) combined.delete(k); }
      update();
    });
    const unsub2 = onSnapshot(tenantQ, (snap) => {
      snap.forEach(d => combined.set(d.id, { id: d.id, ...d.data() }));
      // Remove deleted tenant docs
      const tenantIds = new Set(); snap.forEach(d => tenantIds.add(d.id));
      for (const [k, v] of combined) { if (v.authorId && !tenantIds.has(k)) combined.delete(k); }
      update();
    }, () => { /* collectionGroup may fail if no index yet — legacy query still works */ });
    return () => { unsub1(); unsub2(); };
  }, [currentReader]);

  /* One-time migration: move top-level bookReviews to author tenant path */
  useEffect(() => {
    if (!currentReader) return;
    const AUTHOR_ID = 'uzMXPEok4AVIw8luvjAeNLlWy743';
    const migrationKey = 'bookReviews_migrated_v2';
    if (localStorage.getItem(migrationKey)) return;
    (async () => {
      try {
        const snap = await getDocs(collection(db, 'bookReviews'));
        if (snap.empty) { localStorage.setItem(migrationKey, 'true'); return; }
        console.log(`[Migration] Found ${snap.size} top-level bookReviews to migrate`);
        for (const d of snap.docs) {
          const data = d.data();
          await setDoc(doc(db, 'authors', AUTHOR_ID, 'bookReviews', d.id), { ...data, authorId: AUTHOR_ID });
          await deleteDoc(doc(db, 'bookReviews', d.id));
          console.log(`[Migration] Moved ${d.id} (${data.readerName || '?'}, ${data.bookTitle || '?'})`);
        }
        console.log(`[Migration] Done — moved ${snap.size} reviews to authors/${AUTHOR_ID}/bookReviews`);
        localStorage.setItem(migrationKey, 'true');
      } catch (err) { console.error('[Migration] Error:', err); }
    })();
  }, [currentReader]);

  /* Rewards */
  useEffect(() => {
    if (!currentReader) return;
    const unsub = onSnapshot(collection(db, 'rewards'), (snap) => {
      const list = []; snap.forEach(d => list.push({ id: d.id, ...d.data() })); setRewards(list);
    });
    return () => unsub();
  }, [currentReader]);

  /* Redemptions */
  useEffect(() => {
    if (!readerData) return;
    const q = query(collection(db, 'redemptions'), where('readerId', '==', readerData.id));
    const unsub = onSnapshot(q, (snap) => {
      const list = []; snap.forEach(d => list.push({ id: d.id, ...d.data() })); setMyRedemptions(list);
    });
    return () => unsub();
  }, [readerData]);

  /* ARC requests */
  useEffect(() => {
    if (!readerData) return;
    const q = query(collection(db, 'arcRequests'), where('readerId', '==', readerData.id));
    const unsub = onSnapshot(q, (snap) => {
      const list = []; snap.forEach(d => list.push({ id: d.id, ...d.data() })); setArcRequests(list);
    });
    return () => unsub();
  }, [readerData]);

  /* Campaign data for available ARC books — maps bookId to campaign info + reader's application status */
  useEffect(() => {
    if (!currentReader) return;
    let cancelled = false;
    (async () => {
      try {
        const campaignsSnap = await getDocs(collectionGroup(db, 'campaigns'));
        const cMap = {};
        const sMap = {};
        const camps = [];
        campaignsSnap.forEach(d => {
          const parts = d.ref.path.split('/');
          if (parts[0] === 'authors' && parts.length === 4) {
            camps.push({ id: d.id, authorId: parts[1], ...d.data() });
          }
        });
        for (const camp of camps) {
          if (!camp.bookId) continue;
          const normalisedStatus = normaliseCampaignStatus(camp.status);
          if (!ACTIVE_CAMPAIGN_STATUSES.includes(normalisedStatus)) continue;
          if (!cMap[camp.bookId]) cMap[camp.bookId] = [];
          cMap[camp.bookId].push({
            campaignId: camp.id,
            authorId: camp.authorId,
            authorName: camp.authorName || '',
            campaignStatus: normalisedStatus,
          });
          try {
            const appDoc = await getDoc(doc(db, 'authors', camp.authorId, 'campaigns', camp.id, 'applications', currentReader.uid));
            if (appDoc.exists()) sMap[camp.bookId] = appDoc.data().status;
          } catch { /* skip — reader may not have applied */ }
        }
        if (!cancelled) { setCampaignsByBook(cMap); setCampaignAppStatuses(sMap); }
      } catch (err) { console.error('Failed to load campaign data:', err); }
    })();
    return () => { cancelled = true; };
  }, [currentReader]);

  /* Latest announcement */
  useEffect(() => {
    const q = query(collection(db, 'announcements'), orderBy('createdAt', 'desc'), limit(1));
    const unsub = onSnapshot(q, (snap) => {
      if (!snap.empty) setLatestAnnouncement({ id: snap.docs[0].id, ...snap.docs[0].data() });
    });
    return () => unsub();
  }, []);

  /* Author-level announcements — load from all authors the reader has approved apps with */
  useEffect(() => {
    if (!currentReader || !readerData) return;
    const authorIds = [...new Set(myApprovedApps.map(a => a.authorId))];
    if (authorIds.length === 0) authorIds.push('uzMXPEok4AVIw8luvjAeNLlWy743');
    const unsubs = [];
    const allAnns = {};
    for (const aid of authorIds) {
      const q = query(collection(db, 'authors', aid, 'announcements'), where('active', '==', true), orderBy('createdAt', 'desc'), limit(3));
      const unsub = onSnapshot(q, async (snap) => {
        const anns = [];
        // Get author display name
        let authorName = aid;
        try {
          const brandSnap = await getDoc(doc(db, 'authors', aid, 'settings', 'branding'));
          if (brandSnap.exists()) authorName = brandSnap.data().displayName || authorName;
          else {
            const aSnap = await getDoc(doc(db, 'authors', aid));
            if (aSnap.exists()) authorName = aSnap.data().penName || aSnap.data().name || authorName;
          }
        } catch {}
        snap.forEach(d => {
          const data = { id: d.id, ...d.data(), authorId: aid, authorName };
          // Check if this reader matches the target
          const targets = data.targetStatuses || ['All Readers'];
          const readerStatus = readerData?.status || readerData?.arcStatus || '';
          const matchesStatus = targets.includes('All Readers') || targets.some(t => readerStatus.toLowerCase().includes(t.toLowerCase()));
          const matchesBook = data.bookId === 'all' || (readerData?.booksReceived || []).includes(data.bookId);
          if (matchesStatus && matchesBook) anns.push(data);
        });
        allAnns[aid] = anns;
        setAuthorAnnouncements(Object.values(allAnns).flat().sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0)));
      });
      unsubs.push(unsub);
    }
    return () => unsubs.forEach(u => u());
  }, [currentReader, readerData, myApprovedApps]);

  /* Posts */
  useEffect(() => {
    if (!currentReader) return;
    const q = query(collection(db, 'posts'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(q, (snap) => {
      const list = [];
      snap.forEach(d => list.push({ id: d.id, ...d.data() }));
      setPosts(list.sort((a, b) => {
        if (a.isPinned && !b.isPinned) return -1;
        if (!a.isPinned && b.isPinned) return 1;
        return b.createdAt?.toMillis() - a.createdAt?.toMillis();
      }));
    });
    return () => unsub();
  }, [currentReader]);

  /* Post likes */
  useEffect(() => {
    if (!readerData) return;
    const unsub = onSnapshot(collection(db, 'postLikes'), (snap) => {
      const data = {};
      snap.forEach(d => {
        const like = d.data();
        if (!data[like.postId]) data[like.postId] = { count: 0, liked: false };
        data[like.postId].count++;
        if (like.readerId === readerData.id) data[like.postId].liked = true;
      });
      setPostLikes(data);
    });
    return () => unsub();
  }, [readerData]);

  /* Post comments */
  useEffect(() => {
    if (!currentReader) return;
    const q = query(collection(db, 'postComments'), orderBy('createdAt', 'asc'));
    const unsub = onSnapshot(q, (snap) => {
      const data = {};
      snap.forEach(d => {
        const c = { id: d.id, ...d.data() };
        if (!data[c.postId]) data[c.postId] = [];
        data[c.postId].push(c);
      });
      setPostComments(data);
    });
    return () => unsub();
  }, [currentReader]);

  /* Manual calendar events */
  useEffect(() => {
    if (!readerData) return;
    const q = query(collection(db, 'readerCalendarEvents'), where('readerId', '==', readerData.id));
    const unsub = onSnapshot(q, (snap) => {
      const list = []; snap.forEach(d => list.push({ id: d.id, ...d.data() })); setManualEvents(list);
    });
    return () => unsub();
  }, [readerData]);

  /* ARC log entries (manual) */
  useEffect(() => {
    if (!readerData) return;
    const q = query(collection(db, 'readerArcLog'), where('readerId', '==', readerData.id));
    const unsub = onSnapshot(q, (snap) => {
      const list = []; snap.forEach(d => list.push({ id: d.id, ...d.data() })); setArcLogEntries(list);
    });
    return () => unsub();
  }, [readerData]);

  /* DM messages (legacy — kept for book-detail backward compat) */
  useEffect(() => {
    if (!readerData) return;
    const q = query(
      collection(db, 'directMessages'),
      where('conversationId', '==', readerData.id),
      orderBy('createdAt', 'asc')
    );
    const unsub = onSnapshot(q, (snap) => {
      const list = []; snap.forEach(d => list.push({ id: d.id, ...d.data() }));
      setDmMessages(list);
    });
    return () => unsub();
  }, [readerData]);

  /* Reader's message threads (thread index) */
  useEffect(() => {
    if (!currentReader) return;
    const q = query(
      collection(db, 'readers', currentReader.uid, 'messageThreads'),
      orderBy('updatedAt', 'desc')
    );
    const unsub = onSnapshot(q, (snap) => {
      const list = []; snap.forEach(d => list.push({ id: d.id, ...d.data() }));
      setMyThreads(list);
      setDmUnreadCount(list.filter(t => t.unreadByReader).length);
    });
    return () => unsub();
  }, [currentReader]);

  /* Load messages for selected thread */
  useEffect(() => {
    if (!selectedThreadId) { setSelectedThreadMessages([]); return; }
    const thread = myThreads.find(t => t.id === selectedThreadId);
    if (!thread) return;
    const msgPath = thread.threadType === 'admin'
      ? `adminMessages/${selectedThreadId}/messages`
      : `authors/${thread.authorId}/messages/${selectedThreadId}/messages`;
    const q = query(collection(db, msgPath), orderBy('createdAt', 'asc'));
    const unsub = onSnapshot(q, (snap) => {
      const list = []; snap.forEach(d => list.push({ id: d.id, ...d.data() }));
      setSelectedThreadMessages(list);
    });
    return () => unsub();
  }, [selectedThreadId, myThreads]);

  /* Mark thread as read when opened */
  useEffect(() => {
    if (!selectedThreadId || !currentReader) return;
    const thread = myThreads.find(t => t.id === selectedThreadId);
    if (!thread?.unreadByReader) return;
    // Update reader's index
    updateDoc(doc(db, 'readers', currentReader.uid, 'messageThreads', selectedThreadId), {
      unreadByReader: false,
    }).catch(() => {});
    // Update the source thread
    if (thread.threadType === 'admin') {
      updateDoc(doc(db, 'adminMessages', selectedThreadId), {
        unreadByReader: false,
      }).catch(() => {});
    } else if (thread.authorId) {
      updateDoc(doc(db, 'authors', thread.authorId, 'messages', selectedThreadId), {
        unreadByReader: false,
      }).catch(() => {});
    }
  }, [selectedThreadId, myThreads, currentReader]);

  /* Auto-select thread when viewing book-detail messages tab */
  useEffect(() => {
    if (activeView !== 'book-detail' || bookDetailTab !== 'messages' || !selectedBookId) return;
    const bookThread = myThreads.find(t => t.bookId === selectedBookId && t.threadType !== 'admin');
    if (bookThread && selectedThreadId !== bookThread.id) {
      setSelectedThreadId(bookThread.id);
    }
  }, [activeView, bookDetailTab, selectedBookId, myThreads, selectedThreadId]);

  /* Load approved campaign applications (for compose dropdown + myBooks).
     Uses a collectionGroup query so it finds applications regardless of which
     document ID the application was stored under (uid vs email vs other) —
     we match on the readerId field instead. Falls back to a per-author scan
     only if the collectionGroup query fails (e.g. missing index). */
  useEffect(() => {
    if (!currentReader) return;
    let cancelled = false;
    async function loadApps() {
      const allApps = [];
      const authorNameCache = {};
      const resolveAuthorName = async (authorId) => {
        if (authorNameCache[authorId]) return authorNameCache[authorId];
        try {
          const aSnap = await getDoc(doc(db, 'authors', authorId));
          if (aSnap.exists()) {
            const a = aSnap.data();
            authorNameCache[authorId] = a.penName || a.name || 'Author';
            return authorNameCache[authorId];
          }
        } catch { /* ignore */ }
        return 'Author';
      };

      try {
        // Primary path: single collectionGroup query keyed by readerId (= uid).
        const cgQuery = query(
          collectionGroup(db, 'applications'),
          where('readerId', '==', currentReader.uid),
          where('status', '==', 'approved')
        );
        const snap = await getDocs(cgQuery);
        for (const d of snap.docs) {
          const data = d.data();
          // Path: authors/{authorId}/campaigns/{campaignId}/applications/{appId}
          const parts = d.ref.path.split('/');
          if (parts[0] !== 'authors' || parts.length < 5) continue;
          const authorId = parts[1];
          const campaignId = parts[3];
          const authorName = data.authorName || await resolveAuthorName(authorId);
          allApps.push({
            authorId,
            authorName,
            bookId: data.bookId || campaignId,
            bookTitle: data.bookTitle || 'Untitled',
            campaignId,
          });
        }
      } catch (err) {
        console.warn('Approved apps collectionGroup query failed, falling back to per-author scan:', err?.message || err);
        // Fallback path: per-author scan (slower, requires uid as doc ID).
        try {
          const authorsSnap = await getDocs(collection(db, 'authors'));
          for (const authorDoc of authorsSnap.docs) {
            const authorId = authorDoc.id;
            const authorName = authorDoc.data().penName || authorDoc.data().name || 'Author';
            const campaignsSnap = await getDocs(collection(db, 'authors', authorId, 'campaigns'));
            for (const campDoc of campaignsSnap.docs) {
              const campData = campDoc.data();
              try {
                const appDoc = await getDoc(doc(db, 'authors', authorId, 'campaigns', campDoc.id, 'applications', currentReader.uid));
                if (appDoc.exists() && appDoc.data().status === 'approved') {
                  allApps.push({
                    authorId,
                    authorName,
                    bookId: campData.bookId || campDoc.id,
                    bookTitle: campData.bookTitle || campData.name || 'Untitled',
                    campaignId: campDoc.id,
                  });
                }
              } catch { /* skip */ }
            }
          }
        } catch (fallbackErr) {
          console.error('Failed to load approved apps (fallback):', fallbackErr);
        }
      }

      if (!cancelled) setMyApprovedApps(allApps);
    }
    loadApps();
    return () => { cancelled = true; };
  }, [currentReader]);


  /* ══════════════════════════════════════════════════
     HANDLERS
  ══════════════════════════════════════════════════ */

  const handleLogin = async (e) => {
    e.preventDefault(); setAuthError('');
    try { await signInWithEmailAndPassword(auth, email, password); }
    catch (err) { setAuthError(err.message); }
  };

  const handleSignup = async (e) => {
    e.preventDefault(); setAuthError('');
    try {
      // Store the name from the signup form so the onAuthStateChanged-triggered
      // useEffect can use it when creating/linking the Firestore reader doc.
      // We MUST NOT make any Firestore calls here — after createUserWithEmailAndPassword
      // the Auth SDK fires onIdTokenChanged to sync credentials with Firestore, but
      // that propagation is async. Firestore calls made immediately would race and fail
      // with "Missing or insufficient permissions".
      pendingSignupNameRef.current = name;
      const cred = await createUserWithEmailAndPassword(auth, email, password);
      await updateProfile(cred.user, { displayName: name });
      await sendEmailVerification(cred.user);
      // Set reader role claim (non-blocking)
      httpsCallable(functions, 'setCustomClaims')({ uid: cred.user.uid, role: 'reader' }).catch(() => {});
    } catch (err) { setAuthError(err.message); }
  };

  const isValidEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

  const handlePasswordReset = async (e) => {
    e.preventDefault(); setResetError(''); setResetMessage('');
    if (!resetEmail) { setResetError('Please enter your email address'); return; }
    if (!isValidEmail(resetEmail)) { setResetError('Please enter a valid email address'); return; }
    try {
      await sendPasswordResetEmail(auth, resetEmail);
      setResetMessage('✅ Password reset email sent! Check your inbox.');
      setTimeout(() => { setShowPasswordReset(false); setResetEmail(''); setResetMessage(''); }, 3000);
    } catch (err) {
      if (err.code === 'auth/user-not-found') setResetError('No account found with this email address');
      else setResetError('Error sending reset email. Please try again.');
    }
  };

  /* ── GUEST SIGNUP ── */
  const handleGuestSignup = async (e) => {
    e.preventDefault(); setAuthError('');
    if (!guestForm.name || !guestForm.email || !guestForm.password) {
      setAuthError('Please fill in all required fields.'); return;
    }
    if (guestForm.password.length < 6) {
      setAuthError('Password must be at least 6 characters.'); return;
    }
    try {
      const cred = await createUserWithEmailAndPassword(auth, guestForm.email, guestForm.password);
      // Send verification email
      await sendEmailVerification(cred.user);
      // Resolve authorId from URL params; no hardcoded fallback.
      const params = new URLSearchParams(window.location.search);
      const refAuthorIdRaw = (params.get('authorId') || '').trim();
      if (!refAuthorIdRaw || refAuthorIdRaw === TEST_AUTHOR_ID) {
        setAuthError('Please choose an author to continue.');
        redirectToSelectAuthor();
        return;
      }
      const refAuthorId = refAuthorIdRaw;
      // Create guest document
      await setDoc(doc(db, 'guests', cred.user.uid), {
        name: guestForm.name.trim(),
        email: guestForm.email.toLowerCase(),
        emailVerified: false,
        shippingAddress: guestForm.shippingAddress,
        createdAt: serverTimestamp(),
        authorId: refAuthorId,
        upgradedToPortal: false,
      });
      // Set guest role claim
      httpsCallable(functions, 'setCustomClaims')({ uid: cred.user.uid, role: 'guest' }).catch(() => {});
    } catch (err) { setAuthError(err.message); }
  };

  /* ── EMAIL VERIFICATION HANDLERS ── */
  const handleResendVerification = async () => {
    if (!currentReader) return;
    setResendStatus('sending');
    try {
      await sendEmailVerification(currentReader);
      setResendStatus('sent');
    } catch {
      setResendStatus('error');
    }
  };

  const handleCheckVerification = async () => {
    if (!currentReader) return;
    setVerifyCheckCount(c => c + 1);
    try {
      await currentReader.reload();
      if (currentReader.emailVerified) {
        setEmailVerified(true);
        // Update guest doc if guest
        if (isGuestUser) {
          await updateDoc(doc(db, 'guests', currentReader.uid), { emailVerified: true });
        }
      }
    } catch { /* ignore */ }
  };

  /* ── UPGRADE GUEST TO READER ── */
  const handleUpgradeToReader = async () => {
    setUpgradeError(''); setUpgrading(true);
    try {
      const guestDocSnap = await getDoc(doc(db, 'guests', currentReader.uid));
      const guest = guestDocSnap.exists() ? guestDocSnap.data() : {};
      const normalizedEmail = currentReader.email.toLowerCase();
      // Check for existing reader record
      const q = query(collection(db, 'readers'), where('emailCorrespondence', '==', normalizedEmail));
      const existing = await getDocs(q);
      if (!existing.empty) {
        // Link to existing reader
        await updateDoc(doc(db, 'readers', existing.docs[0].id), {
          name: guest.name || currentReader.displayName || '',
          firstName: (guest.name || '').split(' ')[0] || '',
          lastName: (guest.name || '').split(' ').slice(1).join(' ') || '',
          emailCorrespondence: normalizedEmail,
          authUid: currentReader.uid,
          selfSignupDate: new Date().toLocaleDateString(),
          accountLinked: true,
          shippingAddress: guest.shippingAddress || { line1:'', line2:'', city:'', postalCode:'', country:'' },
        });
      } else {
        // Create new reader — must have a resolved authorId.
        // Guest doc holds authorId set at guest signup; fall back to URL/campaign context.
        const guestAuthorId = (guest.authorId && guest.authorId !== TEST_AUTHOR_ID) ? guest.authorId : null;
        const resolvedAuthorId = guestAuthorId || resolveAuthorIdFromContext();
        if (!resolvedAuthorId) {
          console.warn('[orphan-prevention] no authorId for guest upgrade — redirecting to select-author', {
            authUid: currentReader.uid, email: normalizedEmail,
          });
          setUpgradeError('Please choose an author to continue.');
          redirectToSelectAuthor();
          return;
        }
        const guestBookId = (guest.bookId && typeof guest.bookId === 'string') ? guest.bookId : null;
        await addDoc(collection(db, 'readers'), {
          name: guest.name || '', firstName: (guest.name || '').split(' ')[0] || '',
          lastName: (guest.name || '').split(' ').slice(1).join(' ') || '',
          emailCorrespondence: normalizedEmail,
          status: 'Noted ARC interest', signupDate: new Date().toLocaleDateString(),
          selfSignupDate: new Date().toLocaleDateString(), totalPoints: 0,
          authUid: currentReader.uid, createdAt: serverTimestamp(), source: 'guest_upgrade',
          authorId: resolvedAuthorId,
          ...(guestBookId ? { bookId: guestBookId } : {}),
          shippingAddress: guest.shippingAddress || { line1:'', line2:'', city:'', postalCode:'', country:'' },
          socialMedia: { instagram:'', tiktok:'', goodreads:'', amazon:'', other:'' },
          preferences: { favoriteGenres:[], favoriteTropes:'' }
        });
      }
      // Update guest doc
      await updateDoc(doc(db, 'guests', currentReader.uid), { upgradedToPortal: true });
      // Set reader role (replaces guest)
      await httpsCallable(functions, 'setCustomClaims')({ uid: currentReader.uid, role: 'reader' });
      // Force token refresh and reload
      await currentReader.getIdToken(true);
      setIsGuestUser(false);
      setGuestData(null);
      setShowUpgradeModal(false);
      // Redirect to clean /reader URL (no query params) to avoid re-triggering upgrade
      window.location.href = '/reader';
    } catch (err) {
      setUpgradeError(err.message);
    } finally {
      setUpgrading(false);
    }
  };

  const handleLogout = async () => await signOut(auth);

  /* Claim a free ebook from book detail */
  const handleClaimFreeEbook = async (ebook) => {
    if (!currentReader?.uid || bookDetailClaimingId) return;
    setBookDetailClaimingId(ebook.id);
    try {
      await setDoc(doc(db, 'ebookPurchases', `${currentReader.uid}_${ebook.id}`), {
        uid: currentReader.uid, ebookId: ebook.id, authorId: ebook.authorId,
        title: ebook.title || '', price: 0, claimedFree: true, createdAt: serverTimestamp(),
      });
      window.location.href = `/read/${ebook.id}`;
    } catch (err) {
      console.error('Failed to claim free ebook:', err);
      setBookDetailClaimingId(null);
    }
  };

  /* Claim a free audiobook from book detail */
  const handleClaimFreeAudiobook = async (audiobook) => {
    if (!currentReader?.uid || bookDetailClaimingId) return;
    setBookDetailClaimingId(audiobook.id);
    try {
      await setDoc(doc(db, 'audiobookPurchases', `${currentReader.uid}_${audiobook.id}`), {
        uid: currentReader.uid, audiobookId: audiobook.id, authorId: audiobook.authorId,
        title: audiobook.title || '', price: 0, claimedFree: true, createdAt: serverTimestamp(),
      });
      window.location.href = `/listen/${audiobook.id}`;
    } catch (err) {
      console.error('Failed to claim free audiobook:', err);
      setBookDetailClaimingId(null);
    }
  };

  // Listen for navigation events from child components (e.g. ReaderLibrary empty state)
  useEffect(() => {
    function onNavigate(e) { if (e.detail) setActiveView(e.detail); }
    window.addEventListener('navigate-reader', onNavigate);
    return () => window.removeEventListener('navigate-reader', onNavigate);
  }, []);

  const handleProfileSave = async (e) => {
    e.preventDefault();
    if (!readerData) { setSaveMessage('❌ Profile not loaded. Refresh and try again.'); setTimeout(() => setSaveMessage(''), 5000); return; }
    // Validate device emails if provided
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (deviceEmails.kindleEmail && !emailRegex.test(deviceEmails.kindleEmail)) {
      setSaveMessage('❌ Please enter a valid Kindle email address.'); setTimeout(() => setSaveMessage(''), 5000); return;
    }
    if (deviceEmails.koboEmail && !emailRegex.test(deviceEmails.koboEmail)) {
      setSaveMessage('❌ Please enter a valid Kobo email address.'); setTimeout(() => setSaveMessage(''), 5000); return;
    }
    try {
      // Save main profile to readers collection. kindleEmail is the
      // canonical home for eReaderEmail (post-migration) and is sourced
      // from the device-emails input the user types into.
      await updateDoc(doc(db, 'readers', readerData.id), {
        firstName: profileForm.firstName, lastName: profileForm.lastName,
        eReaderEmail: deviceEmails.kindleEmail.trim().toLowerCase(),
        phoneNumber: profileForm.phoneNumber,
        shippingAddress: profileForm.shippingAddress,
        socialMedia: profileForm.socialMedia, preferences: profileForm.preferences,
        lastUpdated: serverTimestamp()
      });
      // readerProfiles still hosts koboEmail and the display fields. The
      // kindleEmail field is intentionally NOT written here any more.
      await setDoc(doc(db, 'readerProfiles', currentReader.uid), {
        koboEmail: deviceEmails.koboEmail.trim().toLowerCase(),
        displayName: `${profileForm.firstName} ${profileForm.lastName}`.trim(),
        firstName: profileForm.firstName,
        email: currentReader.email,
        lastUpdated: serverTimestamp()
      }, { merge: true });
      setSaveMessage('✅ Profile saved!');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (err) { setSaveMessage(`❌ Error: ${err.message}`); setTimeout(() => setSaveMessage(''), 5000); }
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!selectedBook || !readerData) return;
    const basePoints = 50;
    const book = allBooks.find(b => b.id === selectedBook);
    // Determine which author this book belongs to
    const approvedApp = myApprovedApps.find(a => a.bookId === selectedBook);
    const authorId = approvedApp?.authorId || book?.authorId || 'uzMXPEok4AVIw8luvjAeNLlWy743';
    console.log('[Review Debug] selectedBook:', selectedBook, 'approvedApp:', approvedApp, 'book?.authorId:', book?.authorId, 'resolved authorId:', authorId);
    try {
      const reviewData = {
        readerId: readerData.id, readerEmail: currentReader.email, readerName: readerData.name,
        bookId: selectedBook, bookTitle: book?.title || approvedApp?.bookTitle || 'Unknown',
        authorId,
        reviewLinks, comments: readerComments, rating: reviewRating,
        quickSummary, lovedMost, lovedMostDetails, favoriteCharacterMoment,
        targetAudience, fullReview, favoriteQuote, privateNotes, readerPath,
        pointsEarned: basePoints, status: 'pending', submittedAt: serverTimestamp()
      };
      const reviewPath = `authors/${authorId}/bookReviews`;
      console.log('[Review Debug] Writing to:', reviewPath);
      await addDoc(collection(db, 'authors', authorId, 'bookReviews'), reviewData);
      await updateDoc(doc(db, 'readers', readerData.id), { totalPoints: increment(basePoints) });
      // Reset form
      setSelectedBook(''); setReviewLinks({ amazon:'', goodreads:'', instagram:'', tiktok:'', blog:'', bookbub:'', storygraph:'', netgalley:'', fable:'' });
      setReaderComments(''); setReviewRating(0); setQuickSummary(''); setLovedMost('');
      setLovedMostDetails(''); setFavoriteCharacterMoment(''); setTargetAudience([]);
      setFullReview(''); setFavoriteQuote(''); setPrivateNotes(''); setReaderPath('');
      alert(`Feedback submitted! You earned ${basePoints} points! 🎉`);
    } catch (err) { alert('Error submitting feedback'); }
  };

  const handleRedeemReward = async (reward) => {
    if (!readerData || (readerData.totalPoints || 0) < reward.pointCost) { alert('Not enough points!'); return; }
    if (reward.stock === 'limited' && reward.quantity <= 0) { alert('This reward is out of stock!'); return; }
    try {
      await addDoc(collection(db, 'redemptions'), {
        readerId: readerData.id, readerName: readerData.name, readerEmail: currentReader.email,
        rewardId: reward.id, rewardName: reward.name, pointsSpent: reward.pointCost,
        redeemedAt: serverTimestamp(), status: 'pending'
      });
      await updateDoc(doc(db, 'readers', readerData.id), { totalPoints: increment(-reward.pointCost) });
      if (reward.stock === 'limited') await updateDoc(doc(db, 'rewards', reward.id), { quantity: increment(-1) });
      alert('Reward redeemed successfully!');
    } catch (err) { alert('Error redeeming reward'); }
  };

  const submitArcRequest = async (e) => {
    e.preventDefault();
    if (!requestingBook || !readerData || !requestMessage.trim()) { alert('Please fill in all fields.'); return; }
    try {
      await addDoc(collection(db, 'arcRequests'), {
        readerId: readerData.id, readerName: readerData.name, readerEmail: currentReader.email,
        bookId: requestingBook.id, bookTitle: requestingBook.title, message: requestMessage,
        status: 'pending', requestedAt: serverTimestamp(),
        readerStats: { totalReviews: myReviews.length, totalPoints: readerData.totalPoints || 0 }
      });
      alert('ARC request submitted!');
      setShowRequestModal(false); setRequestMessage(''); setRequestingBook(null);
    } catch (err) { alert('Error submitting request: ' + err.message); }
  };

  const getRequestStatus = (bookId) => {
    // Check tenant-scoped campaign applications first (new path)
    if (campaignAppStatuses[bookId]) return campaignAppStatuses[bookId];
    // Fall back to legacy arcRequests
    const req = arcRequests.find(r => r.bookId === bookId);
    return req ? req.status : null;
  };

  /* ── SELECT BOOK MODAL HANDLERS ── */

  // Option 1: Request ARC + Join Mailing List
  const handleArcAndMailingList = async () => {
    const book = selectBookModal;
    if (!book || !readerData || !selectBookMessage.trim()) return;
    const authorId = book.authorId;
    if (!authorId) { alert('Cannot determine the author for this book. Please refresh and try again.'); return; }
    setSelectBookSubmitting(true);
    try {
      const candidates = campaignsByBook[book.id] || [];
      const campaign = candidates.find(c => c.authorId === authorId);
      if (!campaign) { alert('No active campaign found for this book.'); setSelectBookSubmitting(false); return; }
      // Write tenant-scoped campaign application
      await setDoc(doc(db, 'authors', authorId, 'campaigns', campaign.campaignId, 'applications', currentReader.uid), {
        readerId: currentReader.uid,
        readerName: readerData?.firstName ? `${readerData.firstName} ${readerData.lastName || ''}`.trim() : currentReader.email,
        readerEmail: currentReader.email,
        readerScore: readerData?.score || 0,
        arcsCompleted: readerData?.arcsCompleted || 0,
        genres: readerData?.preferences?.favoriteGenres || [],
        platforms: readerData?.socialMedia
          ? Object.entries(readerData.socialMedia).filter(([, v]) => v).map(([k]) => k)
          : [],
        message: selectBookMessage.trim(),
        status: 'pending',
        appliedAt: serverTimestamp(),
        bookId: book.id,
        bookTitle: book.title || '',
        authorId,
        authorName: campaign.authorName || '',
      });
      // Also sign up for mailing list via Cloud Function
      const joinMailingList = httpsCallable(functions, 'joinMailingList');
      await joinMailingList({
        bookId: book.id,
        bookTitle: book.title || '',
        authorId,
        authorName: campaign.authorName || '',
        name: readerData?.firstName ? `${readerData.firstName} ${readerData.lastName || ''}`.trim() : currentReader.email,
      });
      setCampaignAppStatuses(prev => ({ ...prev, [book.id]: 'pending' }));
      setSelectBookSuccess('ARC request submitted — you\'re on the mailing list!');
      setTimeout(() => { setSelectBookModal(null); setSelectBookOption(null); setSelectBookMessage(''); setSelectBookSuccess(null); }, 2000);
    } catch (err) { alert('Error: ' + err.message); }
    finally { setSelectBookSubmitting(false); }
  };

  // Option 2: Join Mailing List Only
  const handleMailingListOnly = async () => {
    if (!selectBookModal || !readerData) return;
    const book = selectBookModal;
    const candidateAuthorId = book.authorId;
    if (!candidateAuthorId || candidateAuthorId === TEST_AUTHOR_ID) {
      alert('Please choose an author to continue.');
      redirectToSelectAuthor();
      return;
    }
    const authorId = candidateAuthorId;
    const candidates = campaignsByBook[book.id] || [];
    const campaign = candidates.find(c => c.authorId === authorId);
    setSelectBookSubmitting(true);
    try {
      const readerName = readerData?.firstName ? `${readerData.firstName} ${readerData.lastName || ''}`.trim() : currentReader.email;
      // Call Cloud Function to write mailingListSignups (avoids permissions issues)
      const joinMailingList = httpsCallable(functions, 'joinMailingList');
      await joinMailingList({
        bookId: book.id,
        bookTitle: book.title || '',
        authorId,
        authorName: campaign?.authorName || '',
        name: readerName,
      });
      setSelectBookSuccess('You\'re on the mailing list!');
      setTimeout(() => { setSelectBookModal(null); setSelectBookOption(null); setSelectBookSuccess(null); }, 2000);
    } catch (err) { alert('Error: ' + err.message); }
    finally { setSelectBookSubmitting(false); }
  };

  // Option 3: Order / Claim Book — associate with book then navigate to purchase options
  const handleOrderBook = async () => {
    if (!selectBookModal) return;
    const book = selectBookModal;
    const candidateAuthorId = book.authorId;
    if (!candidateAuthorId || candidateAuthorId === TEST_AUTHOR_ID) {
      alert('Please choose an author to continue.');
      redirectToSelectAuthor();
      return;
    }
    const authorId = candidateAuthorId;
    // Add book association before navigating
    if (readerData?.id) {
      const readerName = readerData?.firstName ? `${readerData.firstName} ${readerData.lastName || ''}`.trim() : currentReader.email;
      try {
        // Add book to booksReceived on global reader doc
        await updateDoc(doc(db, 'readers', readerData.id), {
          booksReceived: arrayUnion(book.id),
        });
        // Create per-book reader record
        await setDoc(doc(db, 'authors', authorId, 'books', book.id, 'readers', readerData.id), {
          status: readerData.status || 'Noted ARC interest',
          name: readerName,
          emailCorrespondence: currentReader.email,
          readerId: readerData.id,
          source: 'reader_portal',
          bookId: book.id,
          bookTitle: book.title || '',
          authorId,
          masterReaderId: readerData.id,
          eReaderEmail: readerData.eReaderEmail || '',
          totalPoints: readerData.totalPoints || 0,
          totalReviews: 0,
          signupDate: readerData.signupDate || new Date().toISOString().split('T')[0],
          createdAt: serverTimestamp(),
          lastModified: serverTimestamp(),
        });
      } catch (err) { console.error('Failed to associate book:', err); }
    }
    setSelectedBookId(book.id);
    setActiveView('book-detail');
    setBookDetailTab('overview');
    setSelectBookModal(null);
    setSelectBookOption(null);
  };

  /* Community */
  const handleCreatePost = async (e) => {
    e.preventDefault();
    if (!readerData || !newPostContent.trim()) return;
    try {
      await addDoc(collection(db, 'posts'), {
        type: 'community', authorId: readerData.id,
        authorName: readerData.firstName || readerData.name,
        content: newPostContent, isPinned: false, createdAt: serverTimestamp()
      });
      setNewPostContent(''); setShowCreatePost(false);
    } catch (err) { alert('Error creating post: ' + err.message); }
  };

  const handleToggleLike = async (postId) => {
    if (!readerData) return;
    const q = query(collection(db, 'postLikes'), where('postId','==',postId), where('readerId','==',readerData.id));
    try {
      const snap = await getDocs(q);
      if (snap.empty) {
        await addDoc(collection(db, 'postLikes'), {
          postId, readerId: readerData.id, readerName: readerData.firstName || readerData.name, createdAt: serverTimestamp()
        });
      } else { await deleteDoc(snap.docs[0].ref); }
    } catch (err) { }
  };

  const handleAddComment = async (postId) => {
    if (!readerData || !newComment[postId]?.trim()) return;
    try {
      await addDoc(collection(db, 'postComments'), {
        postId, readerId: readerData.id, readerName: readerData.firstName || readerData.name,
        content: newComment[postId], createdAt: serverTimestamp()
      });
      setNewComment({ ...newComment, [postId]: '' });
    } catch (err) { alert('Error adding comment: ' + err.message); }
  };

  const handleDeletePost    = async (postId)    => { if (!window.confirm('Delete this post?')) return; try { await deleteDoc(doc(db,'posts',postId)); } catch(e){} };
  const handleDeleteComment = async (commentId) => { if (!window.confirm('Delete this comment?')) return; try { await deleteDoc(doc(db,'postComments',commentId)); } catch(e){} };

  /* Calendar */
  const handleAddCalendarEvent = async (e) => {
    e.preventDefault();
    if (!readerData) return;
    const isManual = newEvent.bookSelection === 'manual' || !newEvent.bookSelection;
    try {
      if (isManual) {
        if (!newEvent.customTitle || !newEvent.date) return;
        await addDoc(collection(db, 'readerCalendarEvents'), {
          readerId: readerData.id,
          title: newEvent.customTitle,
          date: newEvent.date,
          type: newEvent.type,
          note: newEvent.note,
          isManual: true,
          createdAt: serverTimestamp()
        });
      } else {
        const book = allBooks.find(b => b.id === newEvent.bookSelection);
        if (!book) return;
        const saves = [];
        if (newEvent.launchDate)
          saves.push(addDoc(collection(db, 'readerCalendarEvents'), {
            readerId: readerData.id, bookId: book.id, bookTitle: book.title,
            title: book.title + ' — Launch Day 🚀', date: newEvent.launchDate,
            type: 'launch', note: newEvent.note, isManual: true, createdAt: serverTimestamp()
          }));
        if (newEvent.reviewDate)
          saves.push(addDoc(collection(db, 'readerCalendarEvents'), {
            readerId: readerData.id, bookId: book.id, bookTitle: book.title,
            title: book.title + ' — Review Deadline', date: newEvent.reviewDate,
            type: 'review-deadline', note: newEvent.note, isManual: true, createdAt: serverTimestamp()
          }));
        if (newEvent.streetTeamDate)
          saves.push(addDoc(collection(db, 'readerCalendarEvents'), {
            readerId: readerData.id, bookId: book.id, bookTitle: book.title,
            title: book.title + ' — Street Team Day', date: newEvent.streetTeamDate,
            type: 'street-team', note: newEvent.note, isManual: true, createdAt: serverTimestamp()
          }));
        if (saves.length === 0) { alert('Please add at least one date.'); return; }
        await Promise.all(saves);
      }
      setNewEvent({ bookSelection: '', customTitle: '', launchDate: '', reviewDate: '',
        streetTeamDate: '', streetTeamRequested: false, note: '', type: 'other', date: '' });
      setShowAddEvent(false);
    } catch (err) { alert('Error saving event: ' + err.message); }
  };

  const handleDeleteCalendarEvent = async (id) => {
    if (!window.confirm('Delete this event?')) return;
    try { await deleteDoc(doc(db, 'readerCalendarEvents', id)); } catch(err) {}
  };

  /* ARC Log */
  const handleAddArcLogEntry = async (e) => {
    e.preventDefault();
    if (!readerData || !newArcEntry.bookTitle) return;
    try {
      await addDoc(collection(db, 'readerArcLog'), {
        readerId: readerData.id, ...newArcEntry, isManual: true, createdAt: serverTimestamp()
      });
      setNewArcEntry({ bookTitle: '', author: '', isbn: '', reviewedOn: '', platforms: [], receivedVia: '' });
      setShowAddArc(false);
    } catch (err) { alert('Error saving entry: ' + err.message); }
  };

  const handleDeleteArcLogEntry = async (id) => {
    if (!window.confirm('Delete this entry?')) return;
    try { await deleteDoc(doc(db, 'readerArcLog', id)); } catch(err) {}
  };

  /* Certificate */
  const handlePrintCertificate = () => {
    const systemEntries = myReviews.map(r => ({
      bookTitle: r.bookTitle,
      author: 'Julie McNairn',
      isbn: '—',
      reviewedOn: r.submittedAt?.toDate?.().toLocaleDateString('en-GB') || '—',
      platforms: Object.keys(r.reviewLinks || {}).filter(p => r.reviewLinks[p]).map(p => p.charAt(0).toUpperCase() + p.slice(1)),
      rating: r.rating || '—',
    }));
    const manualEntries = arcLogEntries.map(e => ({
      bookTitle: e.bookTitle, author: e.author || '—', isbn: e.isbn || '—',
      reviewedOn: e.reviewedOn || '—', platforms: e.platforms || [], rating: '—',
      receivedVia: e.receivedVia || '—'
    }));
    const allEntries = [...systemEntries, ...manualEntries];
    const readerName = `${profileForm.firstName || ''} ${profileForm.lastName || ''}`.trim() || readerData?.name || currentReader?.email;

    const html = `<!DOCTYPE html><html><head><title>ARC Reader Certificate</title>
<style>
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700&family=Lato:wght@400;700&display=swap');
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Lato', sans-serif; background: #fff; color: #1e293b; padding: 40px; }
  .cert-border { border: 8px double #b45309; padding: 40px; min-height: 90vh; }
  .header { text-align: center; margin-bottom: 32px; border-bottom: 2px solid #f59e0b; padding-bottom: 24px; }
  .brand { font-family: 'Playfair Display', serif; font-size: 11px; letter-spacing: 4px; color: #b45309; text-transform: uppercase; margin-bottom: 8px; }
  h1 { font-family: 'Playfair Display', serif; font-size: 36px; color: #1e293b; margin-bottom: 8px; }
  .subtitle { color: #64748b; font-size: 14px; }
  .reader-info { display: flex; justify-content: space-between; margin-bottom: 32px; background: #fffbeb; border: 1px solid #fde68a; border-radius: 8px; padding: 16px 24px; }
  .reader-info div { text-align: center; }
  .reader-info .label { font-size: 11px; text-transform: uppercase; letter-spacing: 1px; color: #92400e; margin-bottom: 4px; }
  .reader-info .value { font-size: 20px; font-weight: 700; color: #1e293b; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 32px; font-size: 13px; }
  th { background: #92400e; color: white; padding: 10px 12px; text-align: left; font-size: 11px; text-transform: uppercase; letter-spacing: 1px; }
  td { padding: 10px 12px; border-bottom: 1px solid #f1f5f9; vertical-align: top; }
  tr:nth-child(even) td { background: #fffbeb; }
  .platform-tag { display: inline-block; background: #fef3c7; color: #92400e; border-radius: 4px; padding: 2px 6px; font-size: 11px; margin: 1px; }
  .footer { text-align: center; color: #94a3b8; font-size: 12px; border-top: 1px solid #f1f5f9; padding-top: 16px; }
  @media print { body { padding: 0; } }
</style></head><body>
<div class="cert-border">
  <div class="header">
    <div class="brand">✦ Narratoria Ltd ✦</div>
    <h1>ARC Reader Portfolio</h1>
    <div class="subtitle">Advance Review Copy Reading Record — Certified ${new Date().toLocaleDateString('en-GB', { year:'numeric', month:'long', day:'numeric' })}</div>
  </div>
  <div class="reader-info">
    <div><div class="label">Reader</div><div class="value">${readerName}</div></div>
    <div><div class="label">Total ARCs</div><div class="value">${allEntries.length}</div></div>
    <div><div class="label">Points Earned</div><div class="value">${readerData?.totalPoints || 0}</div></div>
    <div><div class="label">Member Since</div><div class="value">${readerData?.signupDate || '—'}</div></div>
  </div>
  <table>
    <thead><tr><th>#</th><th>Book Title</th><th>Author</th><th>ISBN</th><th>Received Via</th><th>Reviewed On</th><th>Platforms</th></tr></thead>
    <tbody>
      ${allEntries.map((e, i) => `<tr>
        <td>${i + 1}</td>
        <td><strong>${e.bookTitle}</strong></td>
        <td>${e.author}</td>
        <td>${e.isbn}</td>
        <td>${e.receivedVia || '—'}</td>
        <td>${e.reviewedOn}</td>
        <td>${e.platforms.map(p => `<span class="platform-tag">${p}</span>`).join('') || '—'}</td>
      </tr>`).join('')}
    </tbody>
  </table>
  ${allEntries.length === 0 ? '<p style="text-align:center;color:#94a3b8;padding:32px">No ARC reviews logged yet.</p>' : ''}
  <div class="footer">
    <p>This portfolio was generated from the Narratoria ARC Reader Portal · narratoria.com</p>
    <p style="margin-top:4px">Contact: julieanna.mac.author@gmail.com</p>
  </div>
</div>
<script>window.onload = function() { window.print(); }</script>
</body></html>`;

    const win = window.open('', '_blank');
    win.document.write(html);
    win.document.close();
  };

  /* DMs — legacy send (kept for backward compat in book-detail) */
  const handleSendDm = async () => {
    if (!readerData || !dmInput.trim()) return;
    // Try to route through new thread system if we have an authorId
    const bookApp = myApprovedApps.find(a => a.bookId === selectedBookId);
    if (bookApp) {
      await handleSendBookMessage(bookApp.authorId, selectedBookId, selectedBookObj?.title || '', dmInput.trim());
      setDmInput('');
      return;
    }
    // Fallback to legacy directMessages for books without authorId
    try {
      await addDoc(collection(db, 'directMessages'), {
        conversationId: readerData.id,
        readerId: readerData.id,
        readerName: readerData.firstName || readerData.name,
        readerEmail: currentReader.email,
        content: dmInput.trim(),
        sender: 'reader',
        bookId: selectedBookId || null,
        bookTitle: selectedBookObj?.title || null,
        createdAt: serverTimestamp(),
        readByAuthor: false,
        readByReader: true,
      });
      setDmInput('');
      const unread = dmMessages.filter(m => m.sender === 'author' && !m.readByReader);
      for (const m of unread) {
        await updateDoc(doc(db, 'directMessages', m.id), { readByReader: true });
      }
    } catch (err) { alert('Error sending message: ' + err.message); }
  };

  /* Send book feedback message (Path A) — creates/reuses thread in author subcollection */
  const handleSendBookMessage = async (authorId, bookId, bookTitle, content) => {
    if (!currentReader || !readerData || !content.trim()) return;
    try {
      // Check for existing thread for this reader+book+author
      const existingThread = myThreads.find(t =>
        t.authorId === authorId && t.bookId === bookId && t.threadType !== 'admin'
      );
      let threadId;
      if (existingThread) {
        threadId = existingThread.id;
      } else {
        // Create new thread
        const threadData = {
          authorId,
          readerId: currentReader.uid,
          readerName: readerData.firstName || readerData.name || currentReader.email,
          readerEmail: currentReader.email,
          bookId,
          bookTitle,
          lastMessage: content.trim(),
          updatedAt: serverTimestamp(),
          createdAt: serverTimestamp(),
          unreadByAuthor: true,
          unreadByReader: false,
          messageType: 'book_feedback',
        };
        const threadRef = await addDoc(collection(db, 'authors', authorId, 'messages'), threadData);
        threadId = threadRef.id;
        // Dual-write reader index
        await setDoc(doc(db, 'readers', currentReader.uid, 'messageThreads', threadId), {
          authorId,
          authorName: myApprovedApps.find(a => a.authorId === authorId)?.authorName || 'Author',
          bookId,
          bookTitle,
          threadType: 'author',
          lastMessage: content.trim(),
          updatedAt: serverTimestamp(),
          createdAt: serverTimestamp(),
          unreadByReader: false,
        });
      }
      // Add message to subcollection
      await addDoc(collection(db, 'authors', authorId, 'messages', threadId, 'messages'), {
        content: content.trim(),
        sender: 'reader',
        createdAt: serverTimestamp(),
      });
      // Update thread metadata
      if (existingThread) {
        await updateDoc(doc(db, 'authors', authorId, 'messages', threadId), {
          lastMessage: content.trim(),
          updatedAt: serverTimestamp(),
          unreadByAuthor: true,
          unreadByReader: false,
        });
        await updateDoc(doc(db, 'readers', currentReader.uid, 'messageThreads', threadId), {
          lastMessage: content.trim(),
          updatedAt: serverTimestamp(),
          unreadByReader: false,
        }).catch(() => {});
      }
    } catch (err) {
      alert('Error sending message: ' + err.message);
      throw err;
    }
  };

  /* Send platform support message (Path B) */
  const handleSendPlatformMessage = async (messageType, relatedAuthorId, content) => {
    console.log('[Platform msg] called:', { messageType, relatedAuthorId, hasReader: !!currentReader, hasReaderData: !!readerData, contentLen: content?.length });
    if (!currentReader || !readerData || !content.trim()) {
      console.warn('[Platform msg] early return — missing:', { currentReader: !!currentReader, readerData: !!readerData, content: !!content?.trim() });
      return;
    }
    try {
      const threadData = {
        readerId: currentReader.uid,
        readerName: readerData.firstName || readerData.name || currentReader.email,
        readerEmail: currentReader.email,
        messageType, // 'general' | 'author_related'
        relatedAuthorId: relatedAuthorId || null,
        lastMessage: content.trim(),
        updatedAt: serverTimestamp(),
        createdAt: serverTimestamp(),
        unreadByAdmin: true,
        unreadByReader: false,
      };
      console.log('[Platform msg] writing thread to adminMessages:', threadData);
      const threadRef = await addDoc(collection(db, 'adminMessages'), threadData);
      const threadId = threadRef.id;
      console.log('[Platform msg] thread created:', threadId);
      // Add message
      await addDoc(collection(db, 'adminMessages', threadId, 'messages'), {
        content: content.trim(),
        sender: 'reader',
        createdAt: serverTimestamp(),
      });
      console.log('[Platform msg] message added to subcollection');
      // Dual-write reader index
      await setDoc(doc(db, 'readers', currentReader.uid, 'messageThreads', threadId), {
        threadType: 'admin',
        messageType,
        readerName: readerData.firstName || readerData.name || currentReader.email,
        relatedAuthorId: relatedAuthorId || null,
        lastMessage: content.trim(),
        updatedAt: serverTimestamp(),
        createdAt: serverTimestamp(),
        unreadByReader: false,
      });
      console.log('[Platform msg] reader index written — complete');
    } catch (err) {
      console.error('[Platform msg] FAILED:', err);
      alert('Error sending message: ' + err.message);
      throw err;
    }
  };

  /* Handle compose submit */
  const handleComposeSubmit = async () => {
    if (!composeText.trim()) return;
    setIsSendingCompose(true);
    try {
      if (composeType === 'book_feedback') {
        const app = myApprovedApps.find(a => a.bookId === composeBookId);
        if (!app) { alert('Could not find author for this book.'); return; }
        await handleSendBookMessage(app.authorId, app.bookId, app.bookTitle, composeText);
      } else {
        await handleSendPlatformMessage(composePlatformType, composePlatformAuthorId, composeText);
      }
      // Reset compose flow
      setComposeStep(null);
      setComposeType(null);
      setComposeBookId(null);
      setComposePlatformType('general');
      setComposePlatformAuthorId(null);
      setComposeText('');
    } catch (err) {
      // Error already shown
    } finally {
      setIsSendingCompose(false);
    }
  };

  /* Reply to existing thread */
  const handleThreadReply = async () => {
    if (!dmInput.trim() || !selectedThreadId || !currentReader) return;
    const thread = myThreads.find(t => t.id === selectedThreadId);
    if (!thread) return;
    try {
      const content = dmInput.trim();
      if (thread.threadType === 'admin') {
        await addDoc(collection(db, 'adminMessages', selectedThreadId, 'messages'), {
          content, sender: 'reader', createdAt: serverTimestamp(),
        });
        await updateDoc(doc(db, 'adminMessages', selectedThreadId), {
          lastMessage: content, updatedAt: serverTimestamp(),
          unreadByAdmin: true, unreadByReader: false,
        });
      } else {
        await addDoc(collection(db, 'authors', thread.authorId, 'messages', selectedThreadId, 'messages'), {
          content, sender: 'reader', createdAt: serverTimestamp(),
        });
        await updateDoc(doc(db, 'authors', thread.authorId, 'messages', selectedThreadId), {
          lastMessage: content, updatedAt: serverTimestamp(),
          unreadByAuthor: true, unreadByReader: false,
        });
      }
      await updateDoc(doc(db, 'readers', currentReader.uid, 'messageThreads', selectedThreadId), {
        lastMessage: content, updatedAt: serverTimestamp(), unreadByReader: false,
      }).catch(() => {});
      setDmInput('');
    } catch (err) { alert('Error sending message: ' + err.message); }
  };

  /* ── Delete thread handlers ── */
  const handleDeleteForMe = async (threadId) => {
    if (!threadId || !currentReader) return;
    const thread = myThreads.find(t => t.id === threadId);
    if (!thread) return;
    try {
      if (thread.threadType === 'admin') {
        await updateDoc(doc(db, 'adminMessages', threadId), { deletedFor: arrayUnion(currentReader.uid) });
      } else {
        await updateDoc(doc(db, 'authors', thread.authorId, 'messages', threadId), { deletedFor: arrayUnion(currentReader.uid) });
      }
      await updateDoc(doc(db, 'readers', currentReader.uid, 'messageThreads', threadId), { deletedFor: arrayUnion(currentReader.uid) }).catch(() => {});
      if (selectedThreadId === threadId) setSelectedThreadId(null);
    } catch (err) { console.error('Error deleting thread for me:', err); }
    setDeleteThreadId(null);
    setShowDeleteMenu(null);
  };

  const handleDeleteForEveryone = async (threadId) => {
    if (!threadId || !currentReader) return;
    const thread = myThreads.find(t => t.id === threadId);
    if (!thread) return;
    try {
      if (thread.threadType === 'admin') {
        const msgsSnap = await getDocs(collection(db, 'adminMessages', threadId, 'messages'));
        for (const m of msgsSnap.docs) await deleteDoc(m.ref);
        await deleteDoc(doc(db, 'adminMessages', threadId));
      } else {
        const msgsSnap = await getDocs(collection(db, 'authors', thread.authorId, 'messages', threadId, 'messages'));
        for (const m of msgsSnap.docs) await deleteDoc(m.ref);
        await deleteDoc(doc(db, 'authors', thread.authorId, 'messages', threadId));
      }
      await deleteDoc(doc(db, 'readers', currentReader.uid, 'messageThreads', threadId)).catch(() => {});
      if (selectedThreadId === threadId) setSelectedThreadId(null);
    } catch (err) { console.error('Error deleting thread for everyone:', err); }
    setDeleteThreadId(null);
    setShowDeleteMenu(null);
  };

  /* Helpers */
  const toggleGenre = (genre) => setProfileForm(prev => ({
    ...prev,
    preferences: {
      ...prev.preferences,
      favoriteGenres: prev.preferences.favoriteGenres.includes(genre)
        ? prev.preferences.favoriteGenres.filter(g => g !== genre)
        : [...prev.preferences.favoriteGenres, genre]
    }
  }));

  const toggleAudience = (a) => setTargetAudience(prev => prev.includes(a) ? prev.filter(x => x !== a) : [...prev, a]);

  const formatTimeAgo = (ts) => {
    if (!ts) return 'Just now';
    const d = ts.toDate(), now = new Date(), diff = now - d;
    if (diff < 60000)   return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff/60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff/3600000)}h ago`;
    if (diff < 604800000) return `${Math.floor(diff/86400000)}d ago`;
    return d.toLocaleDateString();
  };

  const getDaysUntil = (dateStr) => {
    if (!dateStr) return null;
    const target = new Date(dateStr);
    const today = new Date();
    today.setHours(0,0,0,0);
    const diff = Math.round((target - today) / 86400000);
    return diff;
  };

  /* ══════════════════════════════════════════════════
     CALENDAR HELPERS
  ══════════════════════════════════════════════════ */

  const buildCalendarEvents = () => {
    const eventsMap = {};
    const add = (dateStr, event) => {
      if (!dateStr) return;
      // Normalise: accept YYYY-MM-DD or any ISO string
      const key = dateStr.toString().slice(0, 10);
      if (!eventsMap[key]) eventsMap[key] = [];
      eventsMap[key].push(event);
    };

    // Books from booksReceived array AND books with approved arcRequests
    const approvedBookIds = arcRequests
      .filter(r => r.status === 'approved')
      .map(r => r.bookId);
    const relevantBookIds = new Set([
      ...(readerData?.booksReceived || []),
      ...approvedBookIds,
    ]);
    const relevantBooks = filterByActiveAuthor(
      allBooks.filter(b => relevantBookIds.has(b.id))
    );

    relevantBooks.forEach(book => {
      // Try multiple possible field names for launch date
      const launchDate = book.goLiveDate || book.launchDate || book.releaseDate || book.publishDate;
      const reviewDate = book.reviewReleaseDate || book.reviewDeadline || book.reviewDate;
      const streetDate = book.streetTeamDate;
      if (launchDate)  add(launchDate,  { title: `${book.title} — Launch Day 🚀`, type: 'launch',           source: 'auto', bookId: book.id });
      if (reviewDate)  add(reviewDate,  { title: `${book.title} — Review Deadline`, type: 'review-deadline', source: 'auto', bookId: book.id });
      if (streetDate)  add(streetDate,  { title: `${book.title} — Street Team Day`, type: 'street-team',    source: 'auto', bookId: book.id });
    });

    manualEvents.forEach(ev => add(ev.date, { ...ev, source: 'manual' }));
    return eventsMap;
  };

  /* ══════════════════════════════════════════════════
     FOR YOU — DATABASE-FIRST HELPERS
  ══════════════════════════════════════════════════ */

  // Load reader preferences
  useEffect(() => {
    if (!readerData?.id) return;
    const loadPrefs = async () => {
      try {
        const snap = await getDoc(doc(db, 'readers', readerData.id, 'preferences', 'bookRecommendations'));
        if (snap.exists()) {
          const data = snap.data();
          setForYouPrefs(data);
          setForYouPrefsForm(data);
        }
      } catch (e) { console.error('Failed to load prefs:', e); }
    };
    loadPrefs();
  }, [readerData?.id]);

  const saveForYouPrefs = useCallback(async (prefs) => {
    if (!readerData?.id) return;
    setForYouSavingPrefs(true);
    try {
      const toSave = { ...prefs, updatedAt: new Date().toISOString() };
      await setDoc(doc(db, 'readers', readerData.id, 'preferences', 'bookRecommendations'), toSave);
      setForYouPrefs(toSave);
      setForYouShowPrefs(false);
    } catch (e) {
      setForYouError('Failed to save preferences.');
    }
    setForYouSavingPrefs(false);
  }, [readerData?.id]);

  const toggleForYouSection = (section) => setForYouExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));

  // Database-first recommendations: query bookDatabase, then Claude fallback
  const getForYouResults = useCallback(async () => {
    setForYouLoading(true);
    setForYouError('');
    setForYouResults([]);
    setForYouAiResults([]);
    setForYouDbCount(0);
    try {
      // Gather wants/avoids from prefs
      const wants = [], avoids = [];
      for (const section of BOOK_TAXONOMY) {
        const sectionData = forYouPrefs?.[section.key] || {};
        for (const [item, val] of Object.entries(sectionData)) {
          if (val === 'want') wants.push({ section: section.key, sectionTitle: section.title, item });
          if (val === 'avoid') avoids.push({ section: section.key, sectionTitle: section.title, item });
        }
      }

      // Query all books from bookDatabase
      const dbSnap = await getDocs(collection(db, 'bookDatabase'));
      const allDbBooks = [];
      dbSnap.forEach(d => allDbBooks.push({ id: d.id, ...d.data() }));
      setForYouAllBooks(allDbBooks);

      // Load reader's reading history to exclude already-read books
      const readBookIds = new Set();
      const readBookTitles = [];
      const highlyRated = []; // 4-5 star books for weighting
      if (readerData?.id) {
        try {
          const histSnap = await getDocs(collection(db, 'readers', readerData.id, 'readingHistory'));
          histSnap.forEach(d => {
            readBookIds.add(d.id);
            const data = d.data();
            if (data.title) readBookTitles.push(data.title);
            if (data.rating >= 4) highlyRated.push(data);
          });
        } catch (e) {
          console.log('[ForYou] Could not load reading history:', e.message);
        }
      }

      // Filter & rank
      const scored = [];
      for (const book of allDbBooks) {
        // Skip books already in reader's reading history
        if (readBookIds.has(book.id)) continue;

        const resolved = book.resolvedTags || {};
        let matchCount = 0, totalWants = wants.length, excluded = false;

        // Check avoids — hard exclude
        for (const a of avoids) {
          const tagVal = resolved[a.section]?.[a.item];
          if (tagVal && tagVal.value === 'present' && (tagVal.source === 'admin' || tagVal.source === 'community')) {
            excluded = true;
            break;
          }
        }
        if (excluded) continue;

        // Check wants — count matches (only verified tags)
        for (const w of wants) {
          const tagVal = resolved[w.section]?.[w.item];
          if (tagVal && tagVal.value === 'present' && (tagVal.source === 'admin' || tagVal.source === 'community')) {
            matchCount++;
          }
        }

        if (totalWants === 0 || matchCount > 0) {
          const matchScore = totalWants > 0 ? Math.round((matchCount / totalWants) * 100) : 50;
          // Build match/difference lists
          const matches = [], differences = [];
          for (const w of wants) {
            const tagVal = resolved[w.section]?.[w.item];
            if (tagVal && tagVal.value === 'present' && (tagVal.source === 'admin' || tagVal.source === 'community')) {
              matches.push({ item: w.item, source: tagVal.source, count: tagVal.count });
            } else {
              differences.push(w.item);
            }
          }
          scored.push({ ...book, matchScore, matchCount, matches, differences });
        }
      }

      // Sort by match score descending
      scored.sort((a, b) => b.matchScore - a.matchScore || b.matchCount - a.matchCount);
      const dbResults = scored.slice(0, 20);
      setForYouResults(dbResults);
      setForYouDbCount(scored.length);

      // Claude fallback if fewer than 3 DB results
      if (dbResults.length < 3 && process.env.REACT_APP_ANTHROPIC_API_KEY) {
        try {
          const wantStr = wants.map(w => `${w.sectionTitle}: ${w.item}`).join('\n');
          const avoidStr = avoids.map(a => `${a.sectionTitle}: ${a.item}`).join('\n');
          const excludeTitles = [...dbResults.map(b => b.title), ...readBookTitles.slice(0, 50)].join(', ');
          const prompt = `You are a romance book recommendation expert. Recommend up to ${5 - dbResults.length} books matching these preferences.

HARD REQUIREMENTS:
${wantStr || 'No specific requirements — recommend popular romantasy'}

MUST AVOID:
${avoidStr || 'None'}

DO NOT RECOMMEND THESE (already shown): ${excludeTitles || 'None'}

CONTEXT: Reader is an ARC reader for "Born from the Broken" by Julieanna Mac — a romantasy with fae, court intrigue, slow burn enemies-to-lovers.

Return ONLY a JSON array: [{"title":"exact title","author":"exact author","reason":"why it matches"}]`;

          const raw = await callClaude({ prompt, systemPrompt: 'Return only valid JSON array.', maxTokens: 1024 });
          const cleaned = raw.replace(/```json\s*/gi, '').replace(/```\s*/gi, '').trim();
          const suggestions = JSON.parse(cleaned);
          const aiBooks = Array.isArray(suggestions) ? suggestions : (suggestions.books || []);

          // Validate via Google Books
          const validated = [];
          for (const book of aiBooks.slice(0, 5)) {
            try {
              const q = encodeURIComponent(`intitle:${book.title}+inauthor:${book.author}`);
              const resp = await fetch(`https://www.googleapis.com/books/v1/volumes?q=${q}&maxResults=1`);
              const data = await resp.json();
              if (data.totalItems > 0) {
                const vol = data.items[0].volumeInfo;
                validated.push({
                  title: book.title, author: book.author, reason: book.reason, source: 'ai',
                  cover: vol.imageLinks?.thumbnail?.replace('http:', 'https:') || null,
                  description: (vol.description || '').substring(0, 180) + (vol.description?.length > 180 ? '…' : ''),
                  rating: vol.averageRating || null,
                  infoLink: vol.infoLink || null,
                  googleBooksId: data.items[0].id || null,
                });
              }
            } catch (_) { /* skip failed lookups */ }
          }
          setForYouAiResults(validated);
        } catch (e) { console.error('[ForYou] AI fallback error:', e); }
      }
    } catch (e) {
      console.error('[ForYou] Error:', e);
      setForYouError('Something went wrong loading recommendations.');
    }
    setForYouLoading(false);
  }, [forYouPrefs]);

  /* ══════════════════════════════════════════════════
     TAG A BOOK — READER SEARCH & TAG FLOW
  ══════════════════════════════════════════════════ */

  const searchBooksForTagging = useCallback(async (q) => {
    if (!q || q.length < 2) { setTagSearchResults([]); return; }
    setTagSearching(true);
    try {
      // Search Firestore bookDatabase first
      const dbSnap = await getDocs(collection(db, 'bookDatabase'));
      const dbBooks = [];
      dbSnap.forEach(d => dbBooks.push({ id: d.id, ...d.data(), source: 'database' }));
      const filtered = dbBooks.filter(b =>
        b.title?.toLowerCase().includes(q.toLowerCase()) ||
        b.author?.toLowerCase().includes(q.toLowerCase())
      );

      // Also search Google Books
      const resp = await fetch(`https://www.googleapis.com/books/v1/volumes?q=${encodeURIComponent(q)}&maxResults=5`);
      const data = await resp.json();
      const gbBooks = (data.items || []).map(item => ({
        id: item.id,
        googleBooksId: item.id,
        title: item.volumeInfo.title,
        author: (item.volumeInfo.authors || []).join(', '),
        cover: item.volumeInfo.imageLinks?.thumbnail?.replace('http:', 'https:') || null,
        pageCount: item.volumeInfo.pageCount || null,
        publishedDate: item.volumeInfo.publishedDate || null,
        description: (item.volumeInfo.description || '').substring(0, 200),
        googleBooksLink: item.volumeInfo.infoLink || null,
        source: 'google',
      })).filter(gb => !filtered.some(f => f.id === gb.googleBooksId));

      setTagSearchResults([...filtered, ...gbBooks]);
    } catch (e) { console.error('Tag search error:', e); }
    setTagSearching(false);
  }, []);

  const openTagEditor = useCallback(async (book) => {
    setTagMessage('');
    setTagMySubmission(null);
    setTagForm({});
    setTagExpandedSections({});

    if (book.source === 'google') {
      // Auto-add to bookDatabase if not already there
      try {
        const existing = await getDoc(doc(db, 'bookDatabase', book.googleBooksId));
        if (!existing.exists()) {
          await setDoc(doc(db, 'bookDatabase', book.googleBooksId), {
            title: book.title, author: book.author, googleBooksId: book.googleBooksId,
            cover: book.cover, description: book.description, pageCount: book.pageCount,
            publishedDate: book.publishedDate, googleBooksLink: book.googleBooksLink,
            adminTags: {}, resolvedTags: {}, submissionCount: 0,
            dateAdded: new Date().toISOString(), addedBy: 'reader',
          });
        }
      } catch (e) { console.error('Failed to add book to database:', e); }
      setTagBookId(book.googleBooksId);
      setTagBookData(book);
    } else {
      setTagBookId(book.id);
      setTagBookData(book);
    }

    // Load existing submission
    if (readerData?.id && (book.id || book.googleBooksId)) {
      try {
        const subSnap = await getDoc(doc(db, 'bookDatabase', book.source === 'google' ? book.googleBooksId : book.id, 'submissions', readerData.id));
        if (subSnap.exists()) {
          setTagMySubmission(subSnap.data());
          setTagForm(subSnap.data());
        }
      } catch (_) { /* no existing submission */ }
    }
  }, [readerData?.id]);

  const toggleTagSection = (section) => setTagExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));

  const handleSubmitTags = useCallback(async () => {
    if (!readerData?.id || !tagBookId) return;
    setTagSubmitting(true);
    setTagMessage('');
    try {
      const submission = {};
      for (const section of BOOK_TAXONOMY) {
        if (tagForm[section.key] && Object.keys(tagForm[section.key]).length > 0) {
          submission[section.key] = tagForm[section.key];
        }
      }
      submission.submittedAt = new Date().toISOString();
      submission.readerName = readerData.name || readerData.firstName || 'Anonymous';

      const isNew = !tagMySubmission;
      await setDoc(doc(db, 'bookDatabase', tagBookId, 'submissions', readerData.id), submission);
      setTagMySubmission(submission);

      // Recalculate resolved tags
      const subsSnap = await getDocs(collection(db, 'bookDatabase', tagBookId, 'submissions'));
      const subs = [];
      subsSnap.forEach(d => subs.push(d.data()));

      const bookSnap = await getDoc(doc(db, 'bookDatabase', tagBookId));
      const adminTags = bookSnap.exists() ? (bookSnap.data().adminTags || {}) : {};
      const resolved = resolveBookTags(adminTags, subs);

      await updateDoc(doc(db, 'bookDatabase', tagBookId), {
        resolvedTags: resolved,
        submissionCount: subs.length,
      });

      // Award 10 points for first submission on this book
      if (isNew) {
        await updateDoc(doc(db, 'readers', readerData.id), { totalPoints: increment(10) });
        await addDoc(collection(db, 'pointsHistory'), {
          readerId: readerData.id,
          amount: 10,
          type: 'book_tagging',
          reason: `Tagged book: ${tagBookData?.title || tagBookId}`,
          createdAt: serverTimestamp(),
          createdBy: 'system'
        });
        setTagMessage('Thanks for tagging! +10 points added.');
      } else {
        setTagMessage('Tags updated successfully!');
      }
    } catch (e) {
      console.error('Failed to submit tags:', e);
      setTagMessage('Failed to save tags — please try again.');
    }
    setTagSubmitting(false);
  }, [readerData?.id, tagBookId, tagForm, tagMySubmission, tagBookData?.title]);

  /* ══════════════════════════════════════════════════
     LOADING / AUTH SCREENS
  ══════════════════════════════════════════════════ */

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-50 via-white to-slate-50/30">
        <div className="text-center">
          <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-slate-100 border-t-teal-600 mx-auto" />
          <p className="text-slate-600">Loading your portal…</p>
        </div>
      </div>
    );
  }

  if (!currentReader) {
    // Pre-auth landing page — show before any login prompt
    if (!landingIntent && !showPasswordReset) {
      const blurb = stripBlurbEmoji(landingBook?.blurb || '');
      const coverSrc = landingBook?.coverUrl || FALLBACK_COVER;
      const displayTitle = stripBookSuffix(landingBook?.title || 'Born from the Broken');
      return (
        <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-white">
          {/* Hero section */}
          <div className="mx-auto max-w-4xl px-4 py-12 sm:py-20">
            <div className="flex flex-col items-center gap-10 sm:flex-row sm:items-start sm:gap-14">
              {/* Book cover */}
              <div className="shrink-0">
                <img src={coverSrc} alt={displayTitle} className="h-80 w-56 rounded-xl object-cover shadow-2xl shadow-teal-900/30 ring-1 ring-white/10" />
              </div>

              {/* Book info */}
              <div className="flex-1 text-center sm:text-left">
                <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-teal-400">Narratoria ARC Program</p>
                <h1 className="text-3xl font-bold leading-tight sm:text-4xl">
                  {displayTitle}
                </h1>
                <p className="mt-1 text-sm text-slate-400">
                  {landingBook?.series ? `${landingBook.series} — ` : ''}by {landingAuthorName}
                </p>

                {blurb && (
                  <p className="mt-5 text-sm leading-relaxed text-slate-300 max-w-lg">
                    {blurb.length > 350 ? blurb.slice(0, 350).replace(/\s+\S*$/, '') + '...' : blurb}
                  </p>
                )}

                {/* Three action buttons */}
                <div className="mt-8 flex flex-col gap-3 sm:max-w-sm">
                  <button
                    onClick={() => { setLandingIntent('signup'); setIsSignup(true); }}
                    className="w-full rounded-xl bg-gradient-to-r from-teal-500 to-teal-600 px-6 py-4 text-center font-bold text-white shadow-lg shadow-teal-600/20 transition-all hover:from-teal-400 hover:to-teal-500 hover:shadow-teal-500/30"
                  >
                    <span className="flex items-center justify-center gap-2">
                      <BookOpen className="h-5 w-5" /> Request an ARC
                    </span>
                    <span className="mt-0.5 block text-xs font-normal text-teal-100">Get a free advance copy to read and review</span>
                  </button>
                  <button
                    onClick={() => { setLandingIntent('signup'); setIsSignup(true); }}
                    className="w-full rounded-xl border border-slate-600 bg-slate-800/80 px-6 py-4 text-center font-bold text-white transition-all hover:border-teal-500 hover:bg-slate-700"
                  >
                    <span className="flex items-center justify-center gap-2">
                      <Mail className="h-5 w-5 text-teal-400" /> Join the Mailing List
                    </span>
                    <span className="mt-0.5 block text-xs font-normal text-slate-400">Get updates, launch news, and exclusive content</span>
                  </button>
                  <button
                    onClick={landingHasProducts ? () => { pendingBuyIntentRef.current = true; setLandingIntent('buy'); setIsSignup(true); } : undefined}
                    disabled={!landingHasProducts}
                    aria-disabled={!landingHasProducts}
                    className={
                      landingHasProducts
                        ? "w-full rounded-xl border border-slate-600 bg-slate-800/80 px-6 py-4 text-center font-bold text-white transition-all hover:border-teal-500 hover:bg-slate-700"
                        : "w-full rounded-xl border border-slate-700 bg-slate-800/40 px-6 py-4 text-center font-bold text-slate-500 cursor-not-allowed opacity-60"
                    }
                  >
                    <span className="flex items-center justify-center gap-2">
                      <ShoppingCart className={`h-5 w-5 ${landingHasProducts ? 'text-teal-400' : 'text-slate-600'}`} /> Buy Now
                    </span>
                    <span className={`mt-0.5 block text-xs font-normal ${landingHasProducts ? 'text-slate-400' : 'text-slate-600'}`}>
                      {landingHasProducts ? 'Available in eBook, paperback, and hardcover' : 'Not yet available for purchase'}
                    </span>
                  </button>
                </div>

                {/* Already have an account */}
                <button
                  onClick={() => { setLandingIntent('login'); setIsSignup(false); }}
                  className="mt-6 text-sm text-slate-400 hover:text-teal-400 transition-colors"
                >
                  Already have an account? Log in →
                </button>
              </div>
            </div>

            {/* Footer */}
            <div className="mt-16 border-t border-slate-800 pt-6 text-center">
              <p className="text-xs text-slate-500">
                Need help? <a href="mailto:julieanna.mac.author@gmail.com" className="text-teal-500 hover:text-teal-400">julieanna.mac.author@gmail.com</a>
              </p>
              <p className="mt-2 text-xs text-slate-600">
                Are you an author? <a href="mailto:admin@narratoria.com?subject=Author%20access%20request" className="text-teal-500 hover:text-teal-400">Apply to publish on Narratoria</a>
              </p>
            </div>
          </div>
        </div>
      );
    }

    // Auth form (login or signup)
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center p-4">
        <div className="w-full max-w-md rounded-2xl border border-slate-700 bg-slate-800/90 p-8 shadow-2xl">
          <div className="mb-6 text-center">
            <button onClick={() => { setLandingIntent(null); setShowPasswordReset(false); setAuthError(''); }}
              className="mb-4 text-sm text-slate-400 hover:text-teal-400 transition-colors">← Back to book</button>
            <div className="mb-4 flex justify-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-teal-500 to-teal-700">
                <BookOpen className="h-7 w-7 text-white" />
              </div>
            </div>
            <h1 className="text-xl font-bold text-white">{isSignup ? 'Create Your Account' : 'Welcome Back'}</h1>
            <p className="text-sm text-slate-400 mt-1">{isSignup ? 'Join the Narratoria reader community' : 'Log in to your Reader Portal'}</p>
          </div>

          {authError && <div className="mb-4 rounded-lg border border-rose-500/30 bg-rose-950/50 p-3 text-sm text-rose-300">{authError}</div>}

          {showPasswordReset ? (
            <div>
              <h2 className="mb-1 text-lg font-bold text-white">Reset Password</h2>
              <p className="mb-4 text-sm text-slate-400">Enter your email and we'll send you a reset link.</p>
              {resetMessage && <div className="mb-4 rounded-lg border border-green-500/30 bg-green-950/50 p-3 text-sm text-green-300">{resetMessage}</div>}
              {resetError   && <div className="mb-4 rounded-lg border border-rose-500/30 bg-rose-950/50 p-3 text-sm text-rose-300">{resetError}</div>}
              <form onSubmit={handlePasswordReset} className="space-y-4">
                <Input type="email" value={resetEmail} onChange={e => setResetEmail(e.target.value)} required placeholder="your@email.com"
                  className="border-slate-600 bg-slate-700 text-white placeholder:text-slate-400" />
                <Button type="submit" className="w-full bg-gradient-to-r from-teal-500 to-teal-600 px-4 py-3 font-semibold text-white transition-all hover:from-teal-400 hover:to-teal-500">Send Reset Link</Button>
              </form>
              <Button variant="ghost" onClick={() => { setShowPasswordReset(false); setResetEmail(''); setResetError(''); setResetMessage(''); }} className="mt-4 w-full text-center text-sm text-slate-400 hover:text-teal-400">Back to login</Button>
            </div>

          ) : isSignup && signupMode === 'guest' ? (
            /* ── Guest signup form ── */
            <div>
              <button onClick={() => { setSignupMode(null); setAuthError(''); }} className="mb-4 text-sm text-slate-400 hover:text-teal-400">Back</button>
              <h2 className="mb-1 text-lg font-bold text-white">Continue as Guest</h2>
              <p className="mb-4 text-sm text-slate-400">Browse and purchase books without a full portal account.</p>
              <form onSubmit={handleGuestSignup} className="space-y-3">
                <div>
                  <Label className="mb-1 block text-slate-300">Name *</Label>
                  <Input type="text" value={guestForm.name} onChange={e => setGuestForm(f => ({ ...f, name: e.target.value }))} required placeholder="Your full name"
                    className="border-slate-600 bg-slate-700 text-white placeholder:text-slate-400" />
                </div>
                <div>
                  <Label className="mb-1 block text-slate-300">Email *</Label>
                  <Input type="email" value={guestForm.email} onChange={e => setGuestForm(f => ({ ...f, email: e.target.value }))} required placeholder="your@email.com"
                    className="border-slate-600 bg-slate-700 text-white placeholder:text-slate-400" />
                </div>
                <div>
                  <Label className="mb-1 block text-slate-300">Password *</Label>
                  <Input type="password" value={guestForm.password} onChange={e => setGuestForm(f => ({ ...f, password: e.target.value }))} required placeholder="Min 6 characters" minLength={6}
                    className="border-slate-600 bg-slate-700 text-white placeholder:text-slate-400" />
                </div>
                <div className="rounded-lg border border-slate-600 bg-slate-700/50 p-3 space-y-2">
                  <h3 className="text-sm font-semibold text-slate-300">Shipping Address</h3>
                  <Input type="text" value={guestForm.shippingAddress.line1} onChange={e => setGuestForm(f => ({ ...f, shippingAddress: { ...f.shippingAddress, line1: e.target.value } }))} placeholder="Address Line 1" className="border-slate-600 bg-slate-700 text-white placeholder:text-slate-400" />
                  <Input type="text" value={guestForm.shippingAddress.line2} onChange={e => setGuestForm(f => ({ ...f, shippingAddress: { ...f.shippingAddress, line2: e.target.value } }))} placeholder="Address Line 2" className="border-slate-600 bg-slate-700 text-white placeholder:text-slate-400" />
                  <div className="grid grid-cols-2 gap-2">
                    <Input type="text" value={guestForm.shippingAddress.city} onChange={e => setGuestForm(f => ({ ...f, shippingAddress: { ...f.shippingAddress, city: e.target.value } }))} placeholder="City" className="border-slate-600 bg-slate-700 text-white placeholder:text-slate-400" />
                    <Input type="text" value={guestForm.shippingAddress.postalCode} onChange={e => setGuestForm(f => ({ ...f, shippingAddress: { ...f.shippingAddress, postalCode: e.target.value } }))} placeholder="Postcode" className="border-slate-600 bg-slate-700 text-white placeholder:text-slate-400" />
                  </div>
                  <Input type="text" value={guestForm.shippingAddress.country} onChange={e => setGuestForm(f => ({ ...f, shippingAddress: { ...f.shippingAddress, country: e.target.value } }))} placeholder="Country" className="border-slate-600 bg-slate-700 text-white placeholder:text-slate-400" />
                </div>
                <Button type="submit" className="w-full bg-gradient-to-r from-teal-500 to-teal-600 px-4 py-3 font-semibold text-white transition-all hover:from-teal-400 hover:to-teal-500">
                  Continue as Guest
                </Button>
              </form>
            </div>

          ) : (
            /* ── Login / Register form ── */
            <>
              <form onSubmit={isSignup ? handleSignup : handleLogin} className="space-y-4">
                {isSignup && (
                  <div>
                    <Label className="mb-1 block text-slate-300">Name</Label>
                    <Input type="text" value={name} onChange={e => setName(e.target.value)} required placeholder="Your full name"
                      className="border-slate-600 bg-slate-700 text-white placeholder:text-slate-400" />
                  </div>
                )}
                <div>
                  <Label className="mb-1 block text-slate-300">Email</Label>
                  <Input type="email" value={email} onChange={e => setEmail(e.target.value)} required placeholder="your@email.com"
                    className="border-slate-600 bg-slate-700 text-white placeholder:text-slate-400" />
                </div>
                <div>
                  <Label className="mb-1 block text-slate-300">Password</Label>
                  <div className="relative">
                    <Input type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)}
                      required placeholder={isSignup ? 'Create a password (min 6 chars)' : 'Your password'} minLength={isSignup ? 6 : undefined}
                      className="pr-16 border-slate-600 bg-slate-700 text-white placeholder:text-slate-400" />
                    <Button type="button" variant="ghost" size="sm" onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 px-2 py-1 text-xs text-slate-400 hover:text-teal-400">
                      {showPassword ? 'Hide' : 'Show'}
                    </Button>
                  </div>
                </div>
                <Button type="submit" className="w-full bg-gradient-to-r from-teal-500 to-teal-600 px-4 py-3 font-semibold text-white transition-all hover:from-teal-400 hover:to-teal-500">
                  {isSignup ? 'Create Account' : 'Log In'}
                </Button>
              </form>
              <Button variant="ghost" onClick={() => { setIsSignup(!isSignup); setAuthError(''); }} className="mt-4 w-full text-center text-sm text-slate-400 hover:text-teal-400">
                {isSignup ? 'Already have an account? Log in' : "Don't have an account? Sign up"}
              </Button>
              {!isSignup && (
                <Button variant="link" onClick={() => { setShowPasswordReset(true); setAuthError(''); setResetEmail(email); }} className="mt-2 w-full text-center text-sm text-teal-400 hover:underline">
                  Forgot your password?
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  /* ── EMAIL VERIFICATION GATE ── */
  /* Disabled until email delivery is configured — uncomment to enforce verification
  if (currentReader && !emailVerified) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-50 via-white to-slate-100/30 p-4">
        <Card className="w-full max-w-lg border-slate-200 shadow-lg">
          <div className="p-8 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-teal-100">
              <Mail className="h-8 w-8 text-teal-600" />
            </div>
            <h1 className="mb-2 text-2xl font-bold text-slate-900">Check your email</h1>
            <p className="mb-2 text-slate-600">
              We've sent a verification link to <strong>{currentReader.email}</strong>.
            </p>
            <p className="mb-6 text-sm text-slate-500">
              Click the link in the email to verify your account. This page will update automatically once verified.
            </p>
            <div className="flex flex-col items-center gap-3">
              <Button variant="outline" className="gap-2" onClick={handleCheckVerification}>
                <RefreshCw className={`h-4 w-4 ${verifyCheckCount > 0 ? '' : ''}`} />
                I've verified — check now
              </Button>
              <button
                onClick={handleResendVerification}
                disabled={resendStatus === 'sending' || resendStatus === 'sent'}
                className="text-sm text-teal-600 hover:text-teal-700 disabled:text-slate-400"
              >
                {resendStatus === 'sending' ? 'Sending...' :
                 resendStatus === 'sent' ? 'Verification email sent!' :
                 resendStatus === 'error' ? 'Failed to send — try again' :
                 'Resend verification email'}
              </button>
              <button onClick={handleLogout} className="mt-3 text-sm text-slate-400 hover:text-slate-600">
                Sign out
              </button>
            </div>
          </div>
        </Card>
      </div>
    );
  }
  */

  /* ── GUEST HANDLING — auto-upgrade if on /reader, otherwise redirect to /store ── */
  if (isGuestUser) {
    const params = new URLSearchParams(window.location.search);
    if (params.get('signup') === 'true' || params.get('upgrade') === 'true') {
      // Auto-upgrade guest to reader — migrate all data
      if (!upgrading) {
        handleUpgradeToReader();
      }
      return (
        <div className="flex min-h-screen items-center justify-center bg-slate-50">
          <div className="text-center">
            <div className="mb-4 h-10 w-10 animate-spin rounded-full border-4 border-slate-200 border-t-teal-600 mx-auto" />
            <p className="text-sm text-slate-600">Setting up your portal account...</p>
          </div>
        </div>
      );
    }
    // Default: redirect guests to storefront
    window.location.href = '/store';
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-slate-200 border-t-teal-600" />
      </div>
    );
  }

  /* ══════════════════════════════════════════════════
     SERIES PICKER GATE
  ══════════════════════════════════════════════════ */

  const handleSelectSeries = async (authorId) => {
    if (!authorId) return;
    try {
      if (readerData?.id) {
        await updateDoc(doc(db, 'readers', readerData.id), { selectedAuthorId: authorId });
      }
    } catch (err) {
      console.error('Failed to set selected series:', err);
    }
    setShowSeriesSwitcher(false);
    // The URL ?authorId= param takes precedence over readerData.selectedAuthorId,
    // so always navigate to the target author's portal URL to ensure the switch
    // takes effect regardless of how the portal was loaded.
    try {
      const currentAuthor = new URLSearchParams(window.location.search).get('authorId');
      if (currentAuthor !== authorId) {
        window.location.href = `/reader/portal?authorId=${encodeURIComponent(authorId)}`;
      }
    } catch { /* ignore */ }
  };

  // Full-screen picker — replaces the rest of the portal until the reader picks
  // a series. Only shown for fully signed-in (non-guest) readers with reader doc
  // loaded and access to more than one author's content.
  if (needsSeriesPicker) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50/60 via-white to-slate-50/30">
        <header className="sticky top-0 z-40 border-b border-slate-100 bg-white/90 backdrop-blur-sm">
          <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-3 sm:px-6">
            <div>
              <h1 className="font-bold text-slate-900">🌙 Reader Portal</h1>
              <p className="text-xs text-slate-500">
                Welcome, {readerData?.firstName || readerData?.name || currentReader.email}
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              className="flex items-center gap-1.5 text-sm text-slate-600 hover:border-teal-300 hover:text-teal-700"
            >
              <LogOut className="h-4 w-4" /> Logout
            </Button>
          </div>
        </header>
        <SeriesPicker
          options={seriesOptions}
          onSelect={handleSelectSeries}
          title="Choose your series"
          subtitle="Pick the series you’d like to explore. You can switch any time from the header."
        />
      </div>
    );
  }

  /* ══════════════════════════════════════════════════
     LOGGED-IN APP SHELL
  ══════════════════════════════════════════════════ */

  const allNavItems = [
    { id: 'home',      label: '🏠 Home',             view: 'home' },
    { id: 'profile',   label: '👤 Profile',          view: 'profile' },
    { id: 'arcs',      label: '📖 Available ARCs',   view: 'available-arcs', guestHidden: true },
    { id: 'library',   label: '📕 My Library',       view: 'library' },
    { id: 'orders',    label: '📦 Orders',            view: 'orders', guestHidden: true },
    { id: 'calendar',  label: '📅 Calendar',         view: 'calendar', guestHidden: true },
    { id: 'messages',  label: '✉️ Messages',         view: 'messages', badge: dmUnreadCount, guestHidden: true },
  ];
  const navItems = isGuestUser ? allNavItems.filter(n => !n.guestHidden) : allNavItems;

  const calendarEventsMap = buildCalendarEvents();

  /* ── CALENDAR RENDER HELPER ── */
  const renderCalendar = () => {
    const year  = calendarDate.getFullYear();
    const month = calendarDate.getMonth();
    const firstDow = new Date(year, month, 1).getDay(); // 0=Sun
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const today = new Date();
    const cells = [];

    for (let i = 0; i < firstDow; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);

    const selectedDayEvents = selectedDay
      ? (calendarEventsMap[`${year}-${String(month+1).padStart(2,'0')}-${String(selectedDay).padStart(2,'0')}`] || [])
      : [];

    return (
      <div className="space-y-6">
        {/* Month nav */}
        <div className="flex items-center justify-between">
          <button onClick={() => setCalendarDate(new Date(year, month - 1, 1))} className="rounded-lg border border-slate-200 bg-white p-2 hover:border-teal-300 hover:text-teal-600 transition-colors">
            <ChevronLeft className="h-5 w-5" />
          </button>
          <h3 className="text-xl font-bold text-slate-900">{MONTH_NAMES[month]} {year}</h3>
          <button onClick={() => setCalendarDate(new Date(year, month + 1, 1))} className="rounded-lg border border-slate-200 bg-white p-2 hover:border-teal-300 hover:text-teal-600 transition-colors">
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>

        <div className="rounded-2xl border border-slate-100 bg-white overflow-hidden shadow-sm">
          {/* Day headers */}
          <div className="grid grid-cols-7 border-b border-slate-100 bg-slate-50">
            {DAY_NAMES.map(d => <div key={d} className="py-2 text-center text-xs font-semibold text-slate-800">{d}</div>)}
          </div>

          {/* Day grid */}
          <div className="grid grid-cols-7">
            {cells.map((day, i) => {
              if (!day) return <div key={`empty-${i}`} className="min-h-[60px] border-b border-r border-slate-50" />;
              const key = `${year}-${String(month+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
              const dayEvents = calendarEventsMap[key] || [];
              const isToday = today.getDate() === day && today.getMonth() === month && today.getFullYear() === year;
              const isSelected = selectedDay === day;
              return (
                <div key={day} onClick={() => setSelectedDay(isSelected ? null : day)}
                  className={`min-h-[60px] border-b border-r border-slate-100 p-1.5 cursor-pointer transition-colors ${isSelected ? 'bg-slate-50' : 'hover:bg-slate-50'}`}>
                  <div className={`mb-1 flex h-6 w-6 items-center justify-center rounded-full text-sm font-medium ${isToday ? 'bg-slate-500 text-white' : isSelected ? 'bg-slate-100 text-slate-800' : 'text-slate-700'}`}>
                    {day}
                  </div>
                  <div className="flex flex-wrap gap-0.5">
                    {dayEvents.slice(0, 3).map((ev, ei) => {
                      const col = EVENT_COLORS[ev.type] || EVENT_COLORS.other;
                      return <span key={ei} className={`h-1.5 w-1.5 rounded-full ${col.dot}`} />;
                    })}
                    {dayEvents.length > 3 && <span className="text-[9px] text-slate-400">+{dayEvents.length - 3}</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Selected day events */}
        {selectedDay && (
          <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
            <h4 className="mb-3 font-bold text-slate-900">{selectedDay} {MONTH_NAMES[month]}</h4>
            {selectedDayEvents.length === 0
              ? <p className="text-sm text-slate-400">No events this day. <button onClick={() => { setNewEvent(ev => ({ ...ev, date: `${year}-${String(month+1).padStart(2,'0')}-${String(selectedDay).padStart(2,'0')}` })); setShowAddEvent(true); }} className="text-teal-600 hover:underline">Add one?</button></p>
              : <div className="space-y-2">
                  {selectedDayEvents.map((ev, i) => {
                    const col = EVENT_COLORS[ev.type] || EVENT_COLORS.other;
                    return (
                      <div key={i} className="flex items-start justify-between gap-3 rounded-lg border border-slate-100 p-3">
                        <div className="flex items-start gap-2">
                          <span className={`mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full ${col.dot}`} />
                          <div>
                            <p className="text-sm font-semibold text-slate-900">{ev.title}</p>
                            {ev.note && <p className="text-xs text-slate-500">{ev.note}</p>}
                            <span className={`mt-1 inline-block rounded-full px-2 py-0.5 text-xs font-medium ${col.badge}`}>{col.label}</span>
                          </div>
                        </div>
                        {ev.source === 'manual' && (
                          <button onClick={() => handleDeleteCalendarEvent(ev.id)} className="text-slate-300 hover:text-red-500 transition-colors">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
            }
          </div>
        )}

        {/* Legend */}
        <div className="flex flex-wrap gap-3 text-xs">
          {Object.entries(EVENT_COLORS).map(([type, col]) => (
            <span key={type} className="flex items-center gap-1.5">
              <span className={`h-2.5 w-2.5 rounded-full ${col.dot}`} />
              <span className="text-slate-600">{col.label}</span>
            </span>
          ))}
        </div>
      </div>
    );
  };

  /* ══════════════════════════════════════════════════
     MAIN RENDER
  ══════════════════════════════════════════════════ */
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50/60 via-white to-slate-50/30">

      {/* ── HEADER ── */}
      <header className="sticky top-0 z-40 border-b border-slate-100 bg-white/90 backdrop-blur-sm">
        <div className="mx-auto max-w-5xl px-4 sm:px-6">
          <div className="flex items-center justify-between py-3">
            <div className="flex items-center gap-3">
              {activeView === 'book-detail' && (
                <Button variant="outline" size="sm" onClick={() => { setActiveView('home'); setSelectedBookId(null); }}
                  className="flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:border-teal-300 hover:text-teal-700">
                  <ArrowLeft className="h-4 w-4" /> Back
                </Button>
              )}
              <div>
                <h1 className="font-bold text-slate-900">
                  {activeView === 'book-detail' && selectedBookObj
                    ? <span className="text-teal-700">{stripBookSuffix(selectedBookObj.title)}</span>
                    : '🌙 Reader Portal'}
                </h1>
                <p className="text-xs text-slate-500">Welcome, {isGuestUser ? (guestData?.name || currentReader.email) : (readerData?.firstName || readerData?.name || currentReader.email)}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {!isGuestUser && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { window.location.href = '/reader/select-author'; }}
                  className="flex items-center gap-1.5 text-sm text-slate-600 hover:border-teal-300 hover:text-teal-700"
                  title="Switch author"
                >
                  <BookOpen className="h-4 w-4 text-teal-600" />
                  <span className="hidden sm:inline">Switch Author</span>
                </Button>
              )}
              {!isGuestUser && (
                <div className="hidden sm:block text-right">
                  <p className="text-xs text-slate-400">Points</p>
                  <p className="text-lg font-bold text-teal-600">{readerData?.totalPoints || 0}</p>
                </div>
              )}
              {hasAuthorRole && (
                <Button variant="outline" size="sm" onClick={() => { window.location.href = '/author/dashboard'; }}
                  className="flex items-center gap-1.5 text-sm text-slate-600 hover:border-teal-300 hover:text-teal-700">
                  <BookOpen className="h-4 w-4" /> Author Dashboard
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={handleLogout} className="flex items-center gap-1.5 text-sm text-slate-600 hover:border-teal-300 hover:text-teal-700">
                <LogOut className="h-4 w-4" /> Logout
              </Button>
            </div>
          </div>

          {/* Navigation tabs (hidden in book-detail on mobile for space) */}
          {activeView !== 'book-detail' && (
            <nav className="flex gap-0.5 overflow-x-auto pb-0">
              {navItems.map(item => (
                <Button key={item.id} variant="ghost" onClick={() => setActiveView(item.view)}
                  className={`shrink-0 rounded-none border-b-2 px-4 py-2.5 text-sm font-medium transition-colors relative ${
                    activeView === item.view
                      ? 'border-teal-600 text-teal-700'
                      : 'border-transparent text-slate-600 hover:border-teal-300 hover:text-teal-700'
                  }`}>
                  {item.label}
                  {item.badge > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[9px] font-bold text-white">{item.badge}</span>
                  )}
                </Button>
              ))}
            </nav>
          )}

          {/* Book detail — no sub-tabs, single scrollable page */}
        </div>
      </header>

      {/* ── MAIN CONTENT ── */}
      <main className="mx-auto max-w-5xl px-4 py-6 sm:px-6">

        {/* ══ HOME VIEW ══ */}
        {activeView === 'home' && (
          <div className="space-y-8">

            {/* Welcome banner / profile status */}
            {!isGuestUser && (
              profileComplete ? (
                <div className="flex items-center gap-2 rounded-lg bg-green-50 border border-green-200 px-4 py-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span className="text-sm font-medium text-green-700">Profile complete ✓</span>
                </div>
              ) : (
                <div className="rounded-2xl border-2 border-teal-200 bg-gradient-to-br from-teal-50/60 to-white p-5 shadow-sm">
                  <div className="flex items-start gap-4">
                    <User className="h-6 w-6 shrink-0 text-teal-600 mt-0.5" />
                    <div className="flex-1">
                      <h3 className="font-bold text-slate-900">Welcome to Narratoria!</h3>
                      <p className="mt-1 text-sm text-slate-600">To get the most out of your ARC experience, please complete your profile — we need your address for physical book orders and your social media handles for upcoming ARC campaigns.</p>
                      <Button onClick={() => setActiveView('profile')}
                        className="mt-3 bg-gradient-to-r from-slate-500 to-teal-600 text-white text-sm font-semibold hover:from-teal-600 hover:to-teal-700">
                        Complete your profile →
                      </Button>
                    </div>
                  </div>
                </div>
              )
            )}

            {/* Announcement — small dismissible notice */}
            {!announcementDismissed && latestAnnouncement && (
              <div className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
                <Bell className="h-4 w-4 shrink-0 text-teal-600 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-900">{latestAnnouncement.title}</p>
                  {latestAnnouncement.message && <p className="mt-0.5 text-xs text-slate-600 line-clamp-2">{latestAnnouncement.message}</p>}
                </div>
                <button onClick={() => setAnnouncementDismissed(true)} className="text-slate-300 hover:text-slate-500"><X className="h-4 w-4" /></button>
              </div>
            )}

            {/* Author announcements — small dismissible notices */}
            {filterByActiveAuthor(authorAnnouncements).length > 0 && (
              <div className="space-y-2">
                {filterByActiveAuthor(authorAnnouncements).slice(0, 3).map(ann => (
                  <div key={ann.id} className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
                    <Bell className="h-4 w-4 shrink-0 text-teal-600 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-teal-600 font-medium">From {ann.authorName}</p>
                      <p className="text-sm font-semibold text-slate-900">{ann.title}</p>
                      <p className="mt-0.5 text-xs text-slate-600 line-clamp-2 whitespace-pre-wrap">{ann.body}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Your ARC Books */}
            <div>
              <div className="mb-4 flex items-center justify-between">
                <h2 className="text-xl font-bold text-slate-900">Your ARC Books</h2>
                {myBooks.length > 0 && (
                  <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-800">{myBooks.length} assigned</span>
                )}
              </div>

              {myBooks.length === 0 && purchasableBooks.length === 0 ? (
                <div className="rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50/50 p-10 text-center">
                  <BookOpen className="mx-auto mb-3 h-12 w-12 text-teal-300" />
                  <p className="text-slate-600 font-medium">No ARC books assigned yet</p>
                  <p className="text-sm text-slate-400 mt-1">When your ARC is approved, your book will appear here.</p>
                </div>
              ) : (
                <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                  {myBooks.map(book => {
                    const daysToLaunch  = getDaysUntil(book.goLiveDate);
                    const daysToReview  = getDaysUntil(book.reviewReleaseDate);
                    const existingReview = myReviews.find(r => r.bookId === book.id);
                    return (
                      <div key={book.id} className="group relative rounded-2xl border-2 border-slate-100 bg-white shadow-sm hover:border-teal-300 hover:shadow-md transition-all overflow-hidden flex flex-col">
                        {/* Cover */}
                        <div className="relative h-44 bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center overflow-hidden">
                          <img src={book.coverUrl || FALLBACK_COVER} alt={stripBookSuffix(book.title)} className="h-full w-full object-cover" />
                          <div className="absolute top-2 left-2 rounded-full bg-teal-500 px-2.5 py-1 text-[11px] font-bold text-white shadow-sm flex items-center gap-1">
                            <CheckCircle className="h-3 w-3" /> Accepted
                          </div>
                          {existingReview && (
                            <div className="absolute top-2 right-2 rounded-full bg-green-500 px-2 py-0.5 text-xs font-bold text-white shadow-sm">
                              ✓ Reviewed
                            </div>
                          )}
                        </div>

                        {/* Info */}
                        <div className="flex flex-col flex-1 p-4">
                          <h3 className="font-bold text-slate-900 leading-tight">{stripBookSuffix(book.title)}</h3>
                          {book.series && <p className="text-xs text-teal-700 mt-0.5">{book.series}</p>}
                          {(() => {
                            const app = myApprovedApps.find(a => a.bookId === book.id);
                            return app?.authorName ? <p className="text-xs text-slate-500 mt-0.5">by {app.authorName}</p> : null;
                          })()}

                          {/* Date badges */}
                          <div className="mt-3 space-y-1.5">
                            {daysToLaunch !== null && (
                              <div className={`flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-xs font-medium ${daysToLaunch <= 0 ? 'bg-green-50 text-green-800' : daysToLaunch <= 14 ? 'bg-slate-50 text-slate-800' : 'bg-slate-50 text-slate-700'}`}>
                                <span>🚀</span>
                                {daysToLaunch <= 0 ? 'Launched!' : daysToLaunch === 1 ? 'Launches tomorrow!' : `Launches in ${daysToLaunch} days`}
                                <span className="ml-auto text-[10px] opacity-70">{book.goLiveDate}</span>
                              </div>
                            )}
                            {daysToReview !== null && !existingReview && (
                              <div className={`flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-xs font-medium ${daysToReview < 0 ? 'bg-slate-50 text-slate-500' : daysToReview <= 7 ? 'bg-red-50 text-red-800' : 'bg-slate-50 text-slate-700'}`}>
                                <span>📝</span>
                                {daysToReview < 0 ? 'Review deadline passed' : daysToReview === 0 ? 'Review due today!' : `Review in ${daysToReview} days`}
                                <span className="ml-auto text-[10px] opacity-70">{book.reviewReleaseDate}</span>
                              </div>
                            )}
                          </div>

                          <Button
                            onClick={() => { setSelectedBookId(book.id); setActiveView('book-detail'); setBookDetailTab('overview'); }}
                            className="mt-4 w-full bg-gradient-to-r from-slate-500 to-teal-600 py-2 text-sm font-semibold text-white hover:from-teal-600 hover:to-teal-700">
                            Open Book →
                          </Button>
                        </div>
                      </div>
                    );
                  })}

                  {/* Purchase-variant cards: launched books from the active
                      series the reader doesn't have approved access to. */}
                  {purchasableBooks.map(book => {
                    const status = getRequestStatus(book.id);
                    const norm = normaliseStatus(status);
                    const isPending = norm === 'pending';
                    return (
                      <div key={`purchase-${book.id}`} className="group relative rounded-2xl border-2 border-slate-100 bg-white shadow-sm hover:border-teal-300 hover:shadow-md transition-all overflow-hidden flex flex-col">
                        {/* Cover */}
                        <div className="relative h-44 bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center overflow-hidden">
                          <img src={book.coverUrl || FALLBACK_COVER} alt={stripBookSuffix(book.title)} className="h-full w-full object-cover" />
                          {isPending && (
                            <div className="absolute top-2 right-2 rounded-full bg-amber-500 px-2 py-0.5 text-xs font-bold text-white shadow-sm flex items-center gap-1">
                              <Clock className="h-3 w-3" /> Pending
                            </div>
                          )}
                          {!isPending && (
                            <div className="absolute top-2 right-2 rounded-full bg-teal-600 px-2 py-0.5 text-xs font-bold text-white shadow-sm">
                              Available
                            </div>
                          )}
                        </div>

                        {/* Info */}
                        <div className="flex flex-col flex-1 p-4">
                          <h3 className="font-bold text-slate-900 leading-tight">{stripBookSuffix(book.title)}</h3>
                          {book.series && <p className="text-xs text-teal-700 mt-0.5">{book.series}</p>}
                          {book.blurb && (
                            <p className="mt-2 text-xs text-slate-600 line-clamp-2">{stripBlurbEmoji(book.blurb)}</p>
                          )}

                          <div className="mt-auto pt-4">
                            {isPending ? (
                              <>
                                <div className="mb-2 flex items-center gap-2 rounded-lg bg-amber-50 px-2.5 py-1.5 text-xs font-medium text-amber-800">
                                  <Clock className="h-3 w-3" />
                                  ARC application pending — we'll notify you when it's reviewed.
                                </div>
                                <Button
                                  variant="outline"
                                  onClick={() => { setSelectedBookId(book.id); setActiveView('book-detail'); setBookDetailTab('overview'); }}
                                  className="w-full py-2 text-sm font-semibold text-slate-700 border-slate-200 hover:border-teal-300 hover:text-teal-700">
                                  View Details
                                </Button>
                              </>
                            ) : (
                              <Button
                                onClick={() => { setSelectedBookId(book.id); setActiveView('book-detail'); setBookDetailTab('shop'); }}
                                className="w-full bg-gradient-to-r from-slate-500 to-teal-600 py-2 text-sm font-semibold text-white hover:from-teal-600 hover:to-teal-700">
                                <ShoppingCart className="mr-1.5 h-4 w-4" /> Buy this Book
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Available ARCs mini-grid — only upcoming / pre-launch books;
                launched books are now shown above in "Your ARC Books" with a
                Buy CTA for non-approved readers. */}
            {availableArcs.filter(b => b.status !== 'launched' && !(readerData?.booksReceived || []).includes(b.id)).length > 0 && (
              <div>
                <h2 className="mb-4 text-xl font-bold text-slate-900">Available ARCs</h2>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {availableArcs.filter(b => b.status !== 'launched' && !(readerData?.booksReceived || []).includes(b.id)).map(book => {
                    const status = getRequestStatus(book.id);
                    const isPending = normaliseStatus(status) === 'pending';
                    const isPendingBook = ['upcoming', 'note_interest', 'draft'].includes(book.status || '');
                    return (
                      <div key={book.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm hover:border-slate-200 transition-colors">
                        <div className="flex items-start justify-between gap-2">
                          <h3 className="font-semibold text-slate-900">{stripBookSuffix(book.title)}</h3>
                          {isPendingBook && <Badge className="bg-blue-100 text-blue-700 shrink-0">Coming Soon</Badge>}
                        </div>
                        {book.series && <p className="text-xs text-slate-500">{book.series}</p>}
                        {book.blurb && <p className="mt-2 text-xs text-slate-600 line-clamp-2">{stripBlurbEmoji(book.blurb)}</p>}
                        <div className="mt-3">
                          {isPendingBook ? (
                            <Button size="sm" onClick={() => { setSelectBookModal(book); setSelectBookOption(2); setSelectBookMessage(''); setSelectBookSuccess(null); }}
                              className="bg-gradient-to-r from-slate-500 to-teal-600 px-4 py-1.5 text-xs font-semibold text-white hover:from-teal-600 hover:to-teal-700">
                              <Mail className="mr-1 h-3 w-3" /> Join Mailing List
                            </Button>
                          ) : (
                            <>
                              {isPending && (
                                <div className="flex items-center gap-2">
                                  <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-800"><Clock className="h-3 w-3" /> Pending</span>
                                  <Button size="sm" variant="outline" onClick={() => { setSelectedBookId(book.id); setActiveView('book-detail'); setBookDetailTab('overview'); }}
                                    className="text-xs px-3 py-1">View Status</Button>
                                </div>
                              )}
                              {normaliseStatus(status) === 'approved' && <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-3 py-1 text-xs font-semibold text-green-800"><CheckCircle className="h-3 w-3" /> Approved!</span>}
                              {normaliseStatus(status) === 'declined' && <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-3 py-1 text-xs font-semibold text-red-800">Declined</span>}
                              {normaliseStatus(status) === 'waitlisted' && <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-800">Waitlisted</span>}
                              {!status && (
                                <Button size="sm" onClick={() => { setSelectBookModal(book); setSelectBookOption(null); setSelectBookMessage(''); setSelectBookSuccess(null); }}
                                  className="bg-gradient-to-r from-slate-500 to-teal-600 px-4 py-1.5 text-xs font-semibold text-white hover:from-teal-600 hover:to-teal-700">
                                  Select this Book
                                </Button>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Upcoming dates mini-preview */}
            {(() => {
              const upcoming = Object.entries(calendarEventsMap)
                .filter(([date]) => date >= new Date().toISOString().slice(0,10))
                .sort(([a],[b]) => a.localeCompare(b))
                .slice(0, 4);
              if (upcoming.length === 0) return null;
              return (
                <div>
                  <div className="mb-3 flex items-center justify-between">
                    <h2 className="text-xl font-bold text-slate-900">Upcoming Dates</h2>
                    <button onClick={() => setActiveView('calendar')} className="text-sm font-medium text-teal-600 hover:text-slate-800 hover:underline">View calendar →</button>
                  </div>
                  <div className="space-y-2">
                    {upcoming.map(([date, events]) => (
                      events.map((ev, i) => {
                        const col = EVENT_COLORS[ev.type] || EVENT_COLORS.other;
                        return (
                          <div key={`${date}-${i}`} className="flex items-center gap-3 rounded-xl border border-slate-100 bg-white px-4 py-3 shadow-sm">
                            <span className={`h-3 w-3 shrink-0 rounded-full ${col.dot}`} />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-slate-900 truncate">{ev.title}</p>
                              <p className="text-xs text-slate-400">{new Date(date + 'T00:00:00').toLocaleDateString('en-GB', { weekday:'short', day:'numeric', month:'short' })}</p>
                            </div>
                            <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${col.badge}`}>{col.label}</span>
                          </div>
                        );
                      })
                    ))}
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {/* ══ ORDERS VIEW ══ */}
        {activeView === 'orders' && (
          <ReaderAllOrders currentReader={currentReader} myApprovedApps={filteredApprovedApps} />
        )}

        {/* ══ MY LIBRARY VIEW ══ */}
        {activeView === 'library' && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-slate-900">My Library</h2>

            {/* Sub-tabs */}
            <div className="flex gap-1 rounded-xl bg-slate-100 p-1">
              {[
                { id: 'current-arcs', label: 'Current ARCs' },
                { id: 'bookshelf', label: 'Bookshelf' },
                { id: 'for-you', label: 'For You' },
              ].map(tab => (
                <button key={tab.id} onClick={() => setLibrarySubTab(tab.id)}
                  className={`flex-1 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${librarySubTab === tab.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Current ARCs sub-tab */}
            {librarySubTab === 'current-arcs' && (
              <ReaderLibrary currentReader={currentReader} />
            )}

            {/* Bookshelf sub-tab */}
            {librarySubTab === 'bookshelf' && (
              <div className="space-y-6">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <p className="text-sm text-slate-500">Your personal reading library across all platforms</p>
                  <div className="flex flex-wrap gap-2">
                    <Button onClick={() => setForYouShowImport(!forYouShowImport)}
                      className="flex items-center gap-2 rounded-xl border border-teal-200 bg-white px-4 py-2 text-sm font-semibold text-teal-700 hover:bg-teal-50">
                      <Download className="h-4 w-4" /> Import Books
                    </Button>
                    <Button onClick={() => setShowResume(!showResume)}
                      className="flex items-center gap-2 rounded-xl border border-teal-200 bg-white px-4 py-2 text-sm font-semibold text-teal-700 hover:bg-teal-50">
                      <FileText className="h-4 w-4" /> My Reading Résumé
                    </Button>
                  </div>
                </div>

                {forYouShowImport && (
                  <Card className="overflow-hidden rounded-2xl border border-slate-100 shadow-sm p-4">
                    <ReadingHistoryImport
                      readerData={readerData}
                      readerId={readerData?.id}
                      existingBooks={forYouAllBooks}
                      isAdmin={false}
                      onComplete={() => {
                        getDocs(collection(db, 'bookDatabase')).then(snap => {
                          const books = [];
                          snap.forEach(d => books.push({ id: d.id, ...d.data() }));
                          setForYouAllBooks(books);
                        });
                        setBookshelfRefreshKey(k => k + 1);
                      }}
                    />
                  </Card>
                )}

                {showResume && (
                  <Card className="overflow-hidden rounded-2xl border border-slate-100 shadow-sm p-4">
                    <ReaderResume readerData={readerData} />
                  </Card>
                )}

                <ReaderBookshelf
                  readerData={readerData}
                  arcBookIds={new Set([
                    ...(readerData?.booksReceived || []),
                    ...arcRequests.filter(r => r.status === 'approved').map(r => r.bookId),
                    ...myApprovedApps.map(a => a.bookId),
                  ])}
                  onTagBook={(bookId) => {
                    setTagBookId(bookId);
                    setTagBookData(null);
                    setActiveView('tag-book');
                  }}
                  refreshKey={bookshelfRefreshKey}
                />
              </div>
            )}

            {/* For You sub-tab — placeholder */}
            {librarySubTab === 'for-you' && (
              <div className="rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50/50 p-10 text-center">
                <Sparkles className="mx-auto mb-3 h-12 w-12 text-teal-300" />
                <p className="text-slate-600 font-medium">Personalised recommendations coming soon</p>
                <p className="text-sm text-slate-400 mt-1">We're working on curated book suggestions just for you based on your reading preferences.</p>
              </div>
            )}
          </div>
        )}

        {/* ══ AVAILABLE ARCS VIEW ══ */}
        {activeView === 'available-arcs' && (
          <div className="space-y-10">
            <AvailableArcs currentReader={currentReader} readerData={readerData} filterAuthorId={null} />

            {/* ARC History (formerly ARC Log tab) */}
            <div className="space-y-6">
              <div className="border-t border-slate-200 pt-8">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <h2 className="text-2xl font-bold text-slate-900">Your ARC History</h2>
                    <p className="text-sm text-slate-500 mt-0.5">Track every ARC you've read and reviewed — generate a certificate to share with publishers</p>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => setShowAddArc(true)}
                      className="flex items-center gap-2 rounded-xl border border-teal-300 bg-slate-50 px-4 py-2 text-sm font-semibold text-slate-800 hover:bg-slate-100 transition-colors">
                      <Plus className="h-4 w-4" /> Add Manual ARC
                    </button>
                    <button onClick={handlePrintCertificate}
                      className="flex items-center gap-2 rounded-xl bg-gradient-to-r from-slate-500 to-teal-600 px-4 py-2 text-sm font-semibold text-white hover:from-teal-600 hover:to-teal-700 transition-all shadow-sm">
                      <Printer className="h-4 w-4" /> Print Certificate
                    </button>
                  </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6">
                  {[
                    { label: 'Total ARCs',      value: myReviews.length + arcLogEntries.length },
                    { label: 'From Narratoria', value: myReviews.length },
                    { label: 'Other Authors',   value: arcLogEntries.length },
                    { label: 'Points Earned',   value: readerData?.totalPoints || 0 },
                  ].map(s => (
                    <div key={s.label} className="rounded-xl border border-slate-100 bg-white p-4 text-center shadow-sm">
                      <p className="text-2xl font-bold text-teal-700">{s.value}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{s.label}</p>
                    </div>
                  ))}
                </div>

                {/* Narratoria ARCs */}
                <div className="mt-6">
                  <h3 className="mb-3 font-bold text-slate-700 flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-slate-500" /> Narratoria ARCs ({myReviews.length})
                  </h3>
                  {myReviews.length === 0
                    ? <p className="rounded-xl border border-dashed border-slate-200 bg-slate-50/50 p-6 text-center text-sm text-slate-400">No reviews submitted yet</p>
                    : (
                      <div className="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
                        <table className="w-full text-sm">
                          <thead className="bg-slate-50 border-b border-slate-200">
                            <tr>{['Book','Submitted','Platforms','Status'].map(h => <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">{h}</th>)}</tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {myReviews.map(r => (
                              <tr key={r.id} className="hover:bg-slate-50/30 transition-colors">
                                <td className="px-4 py-3 font-medium text-slate-900">{r.bookTitle}</td>
                                <td className="px-4 py-3 text-slate-500">{r.submittedAt?.toDate?.().toLocaleDateString('en-GB') || '—'}</td>
                                <td className="px-4 py-3">
                                  <div className="flex flex-wrap gap-1">
                                    {Object.keys(r.reviewLinks || {}).filter(p => r.reviewLinks[p]).map(p => (
                                      <span key={p} className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-800">{p}</span>
                                    ))}
                                  </div>
                                </td>
                                <td className="px-4 py-3">
                                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${r.status === 'approved' ? 'bg-green-100 text-green-800' : r.status === 'rejected' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}`}>
                                    {r.status || 'pending'}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )
                  }
                </div>

                {/* Manual ARC log entries */}
                <div className="mt-6">
                  <h3 className="mb-3 font-bold text-slate-700 flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-blue-400" /> Other Authors ({arcLogEntries.length})
                  </h3>
                  {arcLogEntries.length === 0
                    ? <p className="rounded-xl border border-dashed border-blue-200 bg-blue-50/30 p-6 text-center text-sm text-slate-400">No manual entries yet — add ARCs from other authors to build your full portfolio</p>
                    : (
                      <div className="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
                        <table className="w-full text-sm">
                          <thead className="bg-slate-50 border-b border-slate-200">
                            <tr>{['Book','Author','ISBN','Received Via','Reviewed On','Platforms',''].map((h,i) => <th key={i} className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">{h}</th>)}</tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {arcLogEntries.map(e => (
                              <tr key={e.id} className="hover:bg-blue-50/20 transition-colors">
                                <td className="px-4 py-3 font-medium text-slate-900">{e.bookTitle}</td>
                                <td className="px-4 py-3 text-slate-500">{e.author || '—'}</td>
                                <td className="px-4 py-3 text-slate-400 text-xs">{e.isbn || '—'}</td>
                                <td className="px-4 py-3 text-slate-500 text-xs">{e.receivedVia || '—'}</td>
                                <td className="px-4 py-3 text-slate-500">{e.reviewedOn || '—'}</td>
                                <td className="px-4 py-3">
                                  <div className="flex flex-wrap gap-1">
                                    {(e.platforms || []).map(p => <span key={p} className="rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-medium text-blue-800">{p}</span>)}
                                  </div>
                                </td>
                                <td className="px-4 py-3">
                                  <button onClick={() => handleDeleteArcLogEntry(e.id)} className="text-slate-300 hover:text-red-500 transition-colors"><Trash2 className="h-3.5 w-3.5" /></button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )
                  }
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ══ MESSAGES VIEW ══ */}
        {activeView === 'messages' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-slate-900">Messages</h2>
              {!composeStep && (
                <Button
                  onClick={() => { setComposeStep('type'); setSelectedThreadId(null); }}
                  className="bg-teal-600 hover:bg-teal-700 text-white"
                >
                  <Plus className="mr-1.5 h-4 w-4" /> New Message
                </Button>
              )}
            </div>

            {/* Compose flow */}
            {composeStep && (
              <Card className="p-5 space-y-4 border-teal-200 bg-teal-50/30">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-slate-900">
                    {composeStep === 'type' && 'What would you like to message about?'}
                    {composeStep === 'book' && 'Which book?'}
                    {composeStep === 'platform' && 'What type of support?'}
                    {composeStep === 'compose' && 'Write your message'}
                  </h3>
                  <button onClick={() => { setComposeStep(null); setComposeType(null); setComposeBookId(null); setComposeText(''); }}
                    className="text-slate-400 hover:text-slate-600">
                    <X className="h-4 w-4" />
                  </button>
                </div>

                {/* Step 1: Type selector */}
                {composeStep === 'type' && (
                  <div className="grid gap-3 sm:grid-cols-2">
                    <button
                      onClick={() => { setComposeType('book_feedback'); setComposeStep('book'); }}
                      className="flex items-center gap-3 rounded-xl border-2 border-slate-200 bg-white p-4 text-left hover:border-teal-400 transition-colors"
                    >
                      <BookOpen className="h-8 w-8 text-teal-600" />
                      <div>
                        <p className="font-semibold text-slate-900">Message about a book</p>
                        <p className="text-xs text-slate-500">Send feedback to the author</p>
                      </div>
                    </button>
                    <button
                      onClick={() => { setComposeType('platform_support'); setComposeStep('platform'); }}
                      className="flex items-center gap-3 rounded-xl border-2 border-slate-200 bg-white p-4 text-left hover:border-teal-400 transition-colors"
                    >
                      <Mail className="h-8 w-8 text-slate-500" />
                      <div>
                        <p className="font-semibold text-slate-900">Platform/system question</p>
                        <p className="text-xs text-slate-500">Get help from the Narratoria team</p>
                      </div>
                    </button>
                  </div>
                )}

                {/* Step 2a: Book selector */}
                {composeStep === 'book' && (
                  <div className="space-y-3">
                    {filteredApprovedApps.length === 0 && myBooks.length === 0 ? (
                      <p className="text-sm text-slate-500">You don't have any approved ARCs yet. Apply for ARCs first to message authors.</p>
                    ) : (
                      <>
                        <p className="text-sm text-slate-500">Select the book you'd like to message about:</p>
                        <div className="grid gap-2 sm:grid-cols-2">
                          {filteredApprovedApps.map(app => (
                            <button
                              key={`${app.authorId}-${app.bookId}`}
                              onClick={() => { setComposeBookId(app.bookId); setComposeStep('compose'); }}
                              className={`flex items-center gap-3 rounded-lg border-2 p-3 text-left transition-colors ${
                                composeBookId === app.bookId ? 'border-teal-500 bg-teal-50' : 'border-slate-200 bg-white hover:border-teal-300'
                              }`}
                            >
                              <BookOpen className="h-5 w-5 text-teal-600 shrink-0" />
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-slate-900 truncate">{app.bookTitle}</p>
                                <p className="text-xs text-slate-500 truncate">by {app.authorName}</p>
                              </div>
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                    <Button variant="outline" size="sm" onClick={() => setComposeStep('type')}>
                      <ChevronLeft className="mr-1 h-3 w-3" /> Back
                    </Button>
                  </div>
                )}

                {/* Step 2b: Platform support type */}
                {composeStep === 'platform' && (
                  <div className="space-y-3">
                    <div className="grid gap-2 sm:grid-cols-2">
                      <button
                        onClick={() => { setComposePlatformType('general'); setComposePlatformAuthorId(null); setComposeStep('compose'); }}
                        className="flex items-center gap-3 rounded-lg border-2 border-slate-200 bg-white p-3 text-left hover:border-teal-300 transition-colors"
                      >
                        <MessageCircle className="h-5 w-5 text-slate-500 shrink-0" />
                        <div>
                          <p className="text-sm font-medium text-slate-900">General question</p>
                          <p className="text-xs text-slate-500">About the platform, your account, etc.</p>
                        </div>
                      </button>
                      <button
                        onClick={() => { setComposePlatformType('author_related'); setComposeStep('compose'); }}
                        className="flex items-center gap-3 rounded-lg border-2 border-slate-200 bg-white p-3 text-left hover:border-teal-300 transition-colors"
                      >
                        <User className="h-5 w-5 text-slate-500 shrink-0" />
                        <div>
                          <p className="text-sm font-medium text-slate-900">About a specific author/ARC</p>
                          <p className="text-xs text-slate-500">Issue with a specific book or author</p>
                        </div>
                      </button>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => setComposeStep('type')}>
                      <ChevronLeft className="mr-1 h-3 w-3" /> Back
                    </Button>
                  </div>
                )}

                {/* Step 3: Compose message */}
                {composeStep === 'compose' && (
                  <div className="space-y-3">
                    {composeType === 'book_feedback' && composeBookId && (
                      <div className="flex items-center gap-2 rounded-lg bg-white border border-slate-200 px-3 py-2">
                        <BookOpen className="h-4 w-4 text-teal-600" />
                        <span className="text-sm text-slate-700">
                          {myApprovedApps.find(a => a.bookId === composeBookId)?.bookTitle}
                          <span className="text-slate-400"> by {myApprovedApps.find(a => a.bookId === composeBookId)?.authorName}</span>
                        </span>
                      </div>
                    )}
                    {composeType === 'platform_support' && composePlatformType === 'author_related' && (
                      <div className="space-y-1">
                        <label className="text-xs font-medium text-slate-600">Related author/ARC (optional)</label>
                        <select
                          value={composePlatformAuthorId || ''}
                          onChange={e => setComposePlatformAuthorId(e.target.value || null)}
                          className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
                        >
                          <option value="">Select an author...</option>
                          {[...new Map(filteredApprovedApps.map(a => [a.authorId, a])).values()].map(a => (
                            <option key={a.authorId} value={a.authorId}>{a.authorName}</option>
                          ))}
                        </select>
                      </div>
                    )}
                    <textarea
                      value={composeText}
                      onChange={e => setComposeText(e.target.value)}
                      placeholder="Write your message..."
                      rows={4}
                      className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-teal-400 focus:ring-2 focus:ring-teal-100 outline-none"
                    />
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => setComposeStep(composeType === 'book_feedback' ? 'book' : 'platform')}>
                        <ChevronLeft className="mr-1 h-3 w-3" /> Back
                      </Button>
                      <Button
                        onClick={handleComposeSubmit}
                        disabled={!composeText.trim() || isSendingCompose}
                        className="bg-teal-600 hover:bg-teal-700 text-white"
                      >
                        <Send className="mr-1.5 h-4 w-4" /> Send Message
                      </Button>
                    </div>
                  </div>
                )}
              </Card>
            )}

            {/* Thread list / inbox */}
            {!composeStep && (
              <div className="grid gap-4 md:grid-cols-[280px_1fr]">
                {/* Thread list */}
                <div className="space-y-1">
                  {(() => {
                    const visibleThreads = myThreads
                      .filter(t => !(t.deletedFor || []).includes(currentReader?.uid))
                      .filter(t => !activeAuthorId || t.threadType === 'admin' || t.authorId === activeAuthorId);
                    return visibleThreads.length === 0 ? (
                    <Card className="border-dashed p-8 text-center">
                      <Inbox className="mx-auto mb-3 h-10 w-10 text-slate-200" />
                      <p className="text-sm text-slate-500">No messages yet</p>
                      <p className="text-xs text-slate-400 mt-1">Start a conversation using the button above</p>
                    </Card>
                  ) : (
                    <Card className="divide-y divide-slate-50 overflow-hidden">
                      {visibleThreads.map(thread => (
                        <div key={thread.id} className="relative">
                          <button
                            onClick={() => { setSelectedThreadId(thread.id); setDmInput(''); setShowDeleteMenu(null); }}
                            className={`flex w-full items-center gap-3 p-3 pr-10 text-left hover:bg-slate-50 transition-colors ${
                              selectedThreadId === thread.id ? 'bg-teal-50' : ''
                            }`}
                          >
                            <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${
                              thread.threadType === 'admin'
                                ? 'bg-gradient-to-br from-slate-400 to-slate-500'
                                : 'bg-gradient-to-br from-slate-500 to-teal-600'
                            } text-white text-sm font-bold`}>
                              {thread.threadType === 'admin' ? 'N' : (thread.authorName || 'A')[0].toUpperCase()}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between">
                                <p className="text-sm font-medium text-slate-900 truncate">
                                  {thread.threadType === 'admin' ? 'Narratoria Support' : thread.authorName || 'Author'}
                                </p>
                                {thread.unreadByReader && (
                                  <span className="flex h-2.5 w-2.5 rounded-full bg-teal-600" />
                                )}
                              </div>
                              {thread.bookTitle && (
                                <p className="text-[11px] text-teal-600 truncate">re: {thread.bookTitle}</p>
                              )}
                              <p className="text-xs text-slate-500 truncate">{thread.lastMessage || 'No messages'}</p>
                            </div>
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); setShowDeleteMenu(showDeleteMenu === thread.id ? null : thread.id); }}
                            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md hover:bg-slate-200 text-slate-400 hover:text-slate-600 transition-colors"
                          >
                            <MoreVertical className="h-4 w-4" />
                          </button>
                          {showDeleteMenu === thread.id && (
                            <div className="absolute right-2 top-full z-20 mt-0.5 w-48 rounded-lg border border-slate-200 bg-white py-1 shadow-lg">
                              <button
                                onClick={(e) => { e.stopPropagation(); setDeleteThreadId(thread.id); setShowDeleteMenu(null); }}
                                className="flex w-full items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
                              >
                                <Trash2 className="h-3.5 w-3.5" /> Delete conversation…
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                    </Card>
                  );
                  })()}
                </div>

                {/* Thread messages */}
                {selectedThreadId ? (() => {
                  const thread = myThreads.find(t => t.id === selectedThreadId);
                  if (!thread) return null;
                  const isAdmin = thread.threadType === 'admin';
                  return (
                    <Card className="flex flex-col overflow-hidden" style={{ height: '500px' }}>
                      <div className="border-b border-slate-100 px-4 py-3 bg-slate-50 flex items-center gap-3">
                        <div className={`h-8 w-8 rounded-full flex items-center justify-center text-white text-sm font-bold ${
                          isAdmin ? 'bg-gradient-to-br from-slate-400 to-slate-500' : 'bg-gradient-to-br from-slate-500 to-teal-600'
                        }`}>
                          {isAdmin ? 'N' : (thread.authorName || 'A')[0].toUpperCase()}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-semibold text-slate-900 text-sm">
                            {isAdmin ? 'Narratoria Support' : thread.authorName || 'Author'}
                          </p>
                          {thread.bookTitle && (
                            <p className="text-xs text-slate-500">re: {thread.bookTitle}</p>
                          )}
                        </div>
                        <div className="relative ml-auto">
                          <button
                            onClick={() => setShowDeleteMenu(showDeleteMenu === `header-${thread.id}` ? null : `header-${thread.id}`)}
                            className="p-1 rounded-md hover:bg-slate-200 text-slate-400 hover:text-slate-600 transition-colors"
                          >
                            <MoreVertical className="h-4 w-4" />
                          </button>
                          {showDeleteMenu === `header-${thread.id}` && (
                            <div className="absolute right-0 top-full z-20 mt-1 w-48 rounded-lg border border-slate-200 bg-white py-1 shadow-lg">
                              <button
                                onClick={() => { setDeleteThreadId(thread.id); setShowDeleteMenu(null); }}
                                className="flex w-full items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
                              >
                                <Trash2 className="h-3.5 w-3.5" /> Delete conversation…
                              </button>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="flex-1 overflow-y-auto p-4 space-y-3">
                        {selectedThreadMessages.length === 0 && (
                          <div className="flex h-full items-center justify-center text-slate-400">
                            <p className="text-sm">Loading messages...</p>
                          </div>
                        )}
                        {selectedThreadMessages.map(msg => {
                          const isFromReader = msg.sender === 'reader';
                          return (
                            <div key={msg.id} className={`flex ${isFromReader ? 'justify-end' : 'justify-start'}`}>
                              <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 ${
                                isFromReader
                                  ? 'rounded-br-sm bg-slate-500 text-white'
                                  : 'rounded-bl-sm bg-slate-100 text-slate-900'
                              }`}>
                                <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                                <p className={`mt-1 text-[10px] ${isFromReader ? 'text-slate-100' : 'text-slate-400'}`}>
                                  {msg.createdAt?.toDate?.().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                                  {' · '}
                                  {msg.createdAt?.toDate?.().toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      <div className="border-t border-slate-100 p-3 flex gap-2">
                        <input
                          type="text"
                          placeholder="Type a reply..."
                          value={dmInput}
                          onChange={e => setDmInput(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleThreadReply(); }}}
                          className="flex-1 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
                        />
                        <button onClick={handleThreadReply} disabled={!dmInput.trim()}
                          className="flex items-center gap-2 rounded-xl bg-gradient-to-r from-slate-500 to-teal-600 px-4 py-2.5 text-sm font-semibold text-white hover:from-teal-600 hover:to-teal-700 disabled:opacity-40 transition-all shadow-sm">
                          <Send className="h-4 w-4" />
                        </button>
                      </div>
                    </Card>
                  );
                })() : (
                  myThreads.length > 0 && (
                    <Card className="flex items-center justify-center border-dashed" style={{ height: '500px' }}>
                      <div className="text-center text-slate-400">
                        <MessageCircle className="mx-auto mb-2 h-10 w-10 text-slate-200" />
                        <p className="text-sm">Select a conversation</p>
                      </div>
                    </Card>
                  )
                )}
              </div>
            )}

            {/* Delete conversation confirmation dialog */}
            <Dialog open={!!deleteThreadId} onOpenChange={(open) => { if (!open) setDeleteThreadId(null); }}>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>Delete conversation</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-2">
                  <p className="text-sm text-slate-600">This will remove the conversation from your inbox. The other party can still see it.</p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={() => setDeleteThreadId(null)}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="destructive"
                      className="bg-red-600 hover:bg-red-700"
                      onClick={() => handleDeleteForMe(deleteThreadId)}
                    >
                      <Trash2 className="mr-2 h-4 w-4" /> Delete conversation
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        )}

        {/* ══ CALENDAR VIEW ══ */}
        {activeView === 'calendar' && (() => {
          // Build full sorted event list for table view
          const allCalEvents = [];
          const approvedBookIds = arcRequests.filter(r => r.status === 'approved').map(r => r.bookId);
          const relevantBookIds = new Set([...(readerData?.booksReceived || []), ...approvedBookIds]);
          const relevantBooks = filterByActiveAuthor(allBooks.filter(b => relevantBookIds.has(b.id)));

          relevantBooks.forEach(book => {
            const streetTeamReview = myReviews.find(r => r.bookId === book.id && r.readerPath === 'street-team');
            const isStreetTeamAccepted = !!streetTeamReview;
            if (book.goLiveDate || book.launchDate || book.releaseDate)
              allCalEvents.push({ date: book.goLiveDate || book.launchDate || book.releaseDate, title: book.title, type: 'launch', source: 'auto', bookId: book.id, isStreetTeam: isStreetTeamAccepted });
            if (book.reviewReleaseDate || book.reviewDeadline)
              allCalEvents.push({ date: book.reviewReleaseDate || book.reviewDeadline, title: book.title, type: 'review-deadline', source: 'auto', bookId: book.id, isStreetTeam: isStreetTeamAccepted, reviewed: !!myReviews.find(r => r.bookId === book.id) });
            if (book.streetTeamDate)
              allCalEvents.push({ date: book.streetTeamDate, title: book.title, type: 'street-team', source: 'auto', bookId: book.id, isStreetTeamAccepted });
          });
          manualEvents.forEach(ev => allCalEvents.push({ ...ev, source: 'manual' }));
          allCalEvents.sort((a, b) => (a.date || '').localeCompare(b.date || ''));

          const filtered = calendarFilter === 'all' ? allCalEvents : allCalEvents.filter(e => e.type === calendarFilter);
          const today = new Date().toISOString().slice(0, 10);
          const upcoming = filtered.filter(e => (e.date || '') >= today);
          const past = filtered.filter(e => (e.date || '') < today);

          const FILTER_OPTS = [
            { id: 'all', label: 'All Events', emoji: '📋' },
            { id: 'launch', label: 'Launch Days', emoji: '🚀' },
            { id: 'review-deadline', label: 'Review Deadlines', emoji: '📝' },
            { id: 'street-team', label: 'Street Team', emoji: '⭐' },
            { id: 'other', label: 'Notes', emoji: '📌' },
          ];

          const EventRow = ({ ev }) => {
            const col = EVENT_COLORS[ev.type] || EVENT_COLORS.other;
            const daysUntil = getDaysUntil(ev.date);
            const isPast = daysUntil !== null && daysUntil < 0;
            const isToday = daysUntil === 0;
            const isSoon = daysUntil !== null && daysUntil >= 0 && daysUntil <= 7;
            return (
              <tr className={`border-b border-slate-100 transition-colors ${isPast ? 'opacity-50' : 'hover:bg-slate-50/30'}`}>
                <td className="px-4 py-3">
                  <div className="flex flex-col">
                    <span className="text-sm font-semibold text-slate-900">{ev.date ? new Date(ev.date + 'T00:00:00').toLocaleDateString('en-GB', { weekday:'short', day:'numeric', month:'short', year:'numeric' }) : '—'}</span>
                    {isToday && <span className="text-[10px] font-bold text-teal-600 uppercase tracking-wide">Today!</span>}
                    {isSoon && !isToday && <span className="text-[10px] text-red-500">{daysUntil}d away</span>}
                    {isPast && <span className="text-[10px] text-slate-400">Passed</span>}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold ${col.badge}`}>
                    {col.label}
                  </span>
                </td>
                <td className="px-4 py-3 font-medium text-slate-900 text-sm">{ev.bookTitle || ev.title || '—'}</td>
                <td className="px-4 py-3 text-center">
                  {ev.type === 'review-deadline' && (
                    ev.reviewed
                      ? <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-bold text-green-800">✓ Done</span>
                      : isPast
                        ? <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-bold text-red-700">Overdue</span>
                        : <span className="inline-flex items-center gap-1 rounded-full bg-yellow-100 px-2 py-0.5 text-[10px] font-bold text-yellow-700">Pending</span>
                  )}
                  {ev.type === 'street-team' && (
                    ev.isStreetTeamAccepted
                      ? <span className="inline-flex items-center gap-1 rounded-full bg-purple-100 px-2 py-0.5 text-[10px] font-bold text-purple-800">⭐ Accepted</span>
                      : <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] text-slate-500">Not applied</span>
                  )}
                  {ev.type === 'launch' && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-800">{isPast ? 'Launched 🎉' : '🚀 Upcoming'}</span>
                  )}
                </td>
                <td className="px-4 py-3 text-xs text-slate-400">{ev.note || '—'}</td>
                {ev.source === 'manual' && (
                  <td className="px-2 py-3 text-right">
                    <button onClick={() => handleDeleteCalendarEvent(ev.id)} className="text-slate-200 hover:text-red-400 transition-colors"><Trash2 className="h-3.5 w-3.5" /></button>
                  </td>
                )}
                {ev.source !== 'manual' && <td />}
              </tr>
            );
          };

          return (
            <div className="space-y-6">
              {/* Header */}
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2 className="text-2xl font-bold text-slate-900">Calendar</h2>
                  <p className="text-sm text-slate-500 mt-0.5">Your book dates, review deadlines & street team schedule</p>
                </div>
                <div className="flex items-center gap-2">
                  {/* View toggle */}
                  <div className="flex rounded-xl border border-slate-200 bg-slate-50 p-0.5">
                    <button onClick={() => setCalendarViewMode('calendar')}
                      className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-all ${calendarViewMode === 'calendar' ? 'bg-white text-teal-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
                      📅 Calendar
                    </button>
                    <button onClick={() => setCalendarViewMode('table')}
                      className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-all ${calendarViewMode === 'table' ? 'bg-white text-teal-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
                      📋 Table
                    </button>
                  </div>
                  <button onClick={() => setShowAddEvent(true)}
                    className="flex items-center gap-2 rounded-xl bg-gradient-to-r from-slate-500 to-teal-600 px-4 py-2 text-sm font-semibold text-white hover:from-teal-600 hover:to-teal-700 transition-all shadow-sm">
                    <Plus className="h-4 w-4" /> Add Event
                  </button>
                </div>
              </div>

              {/* Summary stat pills */}
              {(() => {
                const todayStr = new Date().toISOString().slice(0, 10);
                const urgentReviews = allCalEvents.filter(e => e.type === 'review-deadline' && !e.reviewed && e.date >= todayStr && getDaysUntil(e.date) <= 7);
                const upcomingLaunches = allCalEvents.filter(e => e.type === 'launch' && e.date >= todayStr);
                const streetTeamCount = allCalEvents.filter(e => e.type === 'street-team' && e.isStreetTeamAccepted).length;
                if (!urgentReviews.length && !upcomingLaunches.length && !streetTeamCount) return null;
                return (
                  <div className="flex flex-wrap gap-3">
                    {urgentReviews.length > 0 && <div className="flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-2 text-sm font-semibold text-red-800">
                      📝 {urgentReviews.length} review{urgentReviews.length > 1 ? 's' : ''} due within 7 days
                    </div>}
                    {upcomingLaunches.length > 0 && <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-4 py-2 text-sm font-semibold text-slate-800">
                      🚀 {upcomingLaunches.length} upcoming launch{upcomingLaunches.length > 1 ? 'es' : ''}
                    </div>}
                    {streetTeamCount > 0 && <div className="flex items-center gap-2 rounded-xl border border-purple-200 bg-purple-50 px-4 py-2 text-sm font-semibold text-purple-800">
                      ⭐ Street team member for {streetTeamCount} book{streetTeamCount > 1 ? 's' : ''}
                    </div>}
                  </div>
                );
              })()}

              {/* Calendar grid view */}
              {calendarViewMode === 'calendar' && renderCalendar()}

              {/* Table view */}
              {calendarViewMode === 'table' && (
                <div className="space-y-4">
                  {/* Filter pills */}
                  <div className="flex flex-wrap gap-2">
                    {FILTER_OPTS.map(f => (
                      <button key={f.id} onClick={() => setCalendarFilter(f.id)}
                        className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-all border ${calendarFilter === f.id ? 'border-teal-400 bg-slate-500 text-white shadow-sm' : 'border-slate-200 bg-white text-slate-600 hover:border-teal-300'}`}>
                        {f.emoji} {f.label}
                      </button>
                    ))}
                  </div>

                  {/* Upcoming events table */}
                  {upcoming.length > 0 && (
                    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden shadow-sm">
                      <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200">
                        <p className="text-xs font-bold uppercase tracking-wide text-slate-600">Upcoming ({upcoming.length})</p>
                      </div>
                      <table className="w-full text-sm">
                        <thead className="border-b border-slate-100">
                          <tr>
                            {['Date','Type','Book / Event','Status','Notes',''].map((h,i) => (
                              <th key={i} className="px-4 py-2.5 text-left text-[11px] font-semibold text-slate-400 uppercase tracking-wide">{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {upcoming.map((ev, i) => <EventRow key={i} ev={ev} />)}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Past events (collapsible) */}
                  {past.length > 0 && (
                    <details className="group rounded-xl border border-slate-100 bg-white overflow-hidden shadow-sm">
                      <summary className="flex cursor-pointer items-center justify-between px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wide select-none hover:bg-slate-50">
                        <span>Past Events ({past.length})</span>
                        <span className="group-open:rotate-180 transition-transform">▾</span>
                      </summary>
                      <table className="w-full text-sm">
                        <thead className="border-b border-slate-100 border-t">
                          <tr>
                            {['Date','Type','Book / Event','Status','Notes',''].map((h,i) => (
                              <th key={i} className="px-4 py-2.5 text-left text-[11px] font-semibold text-slate-400 uppercase tracking-wide">{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {past.slice().reverse().map((ev, i) => <EventRow key={i} ev={ev} />)}
                        </tbody>
                      </table>
                    </details>
                  )}

                  {filtered.length === 0 && (
                    <p className="rounded-xl border border-dashed border-slate-200 bg-slate-50/30 p-8 text-center text-sm text-slate-400">
                      No events for this filter yet
                    </p>
                  )}
                </div>
              )}
            </div>
          );
        })()}

        {/* ══ ARC LOG VIEW ══ */}
        {activeView === 'arc-log' && (
          <div className="space-y-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <h2 className="text-2xl font-bold text-slate-900">My ARC Reading Log</h2>
                <p className="text-sm text-slate-500 mt-0.5">Track every ARC you've read and reviewed — generate a certificate to share with publishers</p>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setShowAddArc(true)}
                  className="flex items-center gap-2 rounded-xl border border-teal-300 bg-slate-50 px-4 py-2 text-sm font-semibold text-slate-800 hover:bg-slate-100 transition-colors">
                  <Plus className="h-4 w-4" /> Add Manual ARC
                </button>
                <button onClick={handlePrintCertificate}
                  className="flex items-center gap-2 rounded-xl bg-gradient-to-r from-slate-500 to-teal-600 px-4 py-2 text-sm font-semibold text-white hover:from-teal-600 hover:to-teal-700 transition-all shadow-sm">
                  <Printer className="h-4 w-4" /> Print Certificate
                </button>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[
                { label: 'Total ARCs',      value: myReviews.length + arcLogEntries.length },
                { label: 'From Narratoria', value: myReviews.length },
                { label: 'Other Authors',   value: arcLogEntries.length },
                { label: 'Points Earned',   value: readerData?.totalPoints || 0 },
              ].map(s => (
                <div key={s.label} className="rounded-xl border border-slate-100 bg-white p-4 text-center shadow-sm">
                  <p className="text-2xl font-bold text-teal-700">{s.value}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{s.label}</p>
                </div>
              ))}
            </div>

            {/* Narratoria ARCs */}
            <div>
              <h3 className="mb-3 font-bold text-slate-700 flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-slate-500" /> Narratoria ARCs ({myReviews.length})
              </h3>
              {myReviews.length === 0
                ? <p className="rounded-xl border border-dashed border-slate-200 bg-slate-50/50 p-6 text-center text-sm text-slate-400">No reviews submitted yet</p>
                : (
                  <div className="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>{['Book','Submitted','Platforms','Status'].map(h => <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">{h}</th>)}</tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {myReviews.map(r => (
                          <tr key={r.id} className="hover:bg-slate-50/30 transition-colors">
                            <td className="px-4 py-3 font-medium text-slate-900">{r.bookTitle}</td>
                            <td className="px-4 py-3 text-slate-500">{r.submittedAt?.toDate?.().toLocaleDateString('en-GB') || '—'}</td>
                            <td className="px-4 py-3">
                              <div className="flex flex-wrap gap-1">
                                {Object.keys(r.reviewLinks || {}).filter(p => r.reviewLinks[p]).map(p => (
                                  <span key={p} className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-800">{p}</span>
                                ))}
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${r.status === 'approved' ? 'bg-green-100 text-green-800' : r.status === 'rejected' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}`}>
                                {r.status || 'pending'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )
              }
            </div>

            {/* Manual ARC log entries */}
            <div>
              <h3 className="mb-3 font-bold text-slate-700 flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-blue-400" /> Other Authors ({arcLogEntries.length})
              </h3>
              {arcLogEntries.length === 0
                ? <p className="rounded-xl border border-dashed border-blue-200 bg-blue-50/30 p-6 text-center text-sm text-slate-400">No manual entries yet — add ARCs from other authors to build your full portfolio</p>
                : (
                  <div className="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>{['Book','Author','ISBN','Received Via','Reviewed On','Platforms',''].map((h,i) => <th key={i} className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">{h}</th>)}</tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {arcLogEntries.map(e => (
                          <tr key={e.id} className="hover:bg-blue-50/20 transition-colors">
                            <td className="px-4 py-3 font-medium text-slate-900">{e.bookTitle}</td>
                            <td className="px-4 py-3 text-slate-500">{e.author || '—'}</td>
                            <td className="px-4 py-3 text-slate-400 text-xs">{e.isbn || '—'}</td>
                            <td className="px-4 py-3 text-slate-500 text-xs">{e.receivedVia || '—'}</td>
                            <td className="px-4 py-3 text-slate-500">{e.reviewedOn || '—'}</td>
                            <td className="px-4 py-3">
                              <div className="flex flex-wrap gap-1">
                                {(e.platforms || []).map(p => <span key={p} className="rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-medium text-blue-800">{p}</span>)}
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <button onClick={() => handleDeleteArcLogEntry(e.id)} className="text-slate-300 hover:text-red-500 transition-colors"><Trash2 className="h-3.5 w-3.5" /></button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )
              }
            </div>
          </div>
        )}

        {/* ══ MY BOOKSHELF ══ */}
        {activeView === 'bookshelf' && (
          <div className="space-y-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <h2 className="text-2xl font-bold text-slate-900">📖 My Bookshelf</h2>
                <p className="text-sm text-slate-500">Your personal reading library across all platforms</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button onClick={() => setForYouShowImport(!forYouShowImport)}
                  className="flex items-center gap-2 rounded-xl border border-teal-200 bg-white px-4 py-2 text-sm font-semibold text-teal-700 hover:bg-teal-50">
                  <Download className="h-4 w-4" /> Import Books
                </Button>
                <Button onClick={() => setShowResume(!showResume)}
                  className="flex items-center gap-2 rounded-xl border border-teal-200 bg-white px-4 py-2 text-sm font-semibold text-teal-700 hover:bg-teal-50">
                  <FileText className="h-4 w-4" /> My Reading Résumé
                </Button>
              </div>
            </div>

            {forYouShowImport && (
              <Card className="overflow-hidden rounded-2xl border border-slate-100 shadow-sm p-4">
                <ReadingHistoryImport
                  readerData={readerData}
                  readerId={readerData?.id}
                  existingBooks={forYouAllBooks}
                  isAdmin={false}
                  onComplete={() => {
                    getDocs(collection(db, 'bookDatabase')).then(snap => {
                      const books = [];
                      snap.forEach(d => books.push({ id: d.id, ...d.data() }));
                      setForYouAllBooks(books);
                    });
                    setBookshelfRefreshKey(k => k + 1);
                  }}
                />
              </Card>
            )}

            {showResume && (
              <Card className="overflow-hidden rounded-2xl border border-slate-100 shadow-sm p-4">
                <ReaderResume readerData={readerData} />
              </Card>
            )}

            <ReaderBookshelf
              readerData={readerData}
              arcBookIds={new Set([
                ...(readerData?.booksReceived || []),
                ...arcRequests.filter(r => r.status === 'approved').map(r => r.bookId),
              ])}
              onTagBook={(bookId) => {
                setTagBookId(bookId);
                setTagBookData(null);
                setActiveView('tag-book');
              }}
              refreshKey={bookshelfRefreshKey}
            />
          </div>
        )}

        {/* ══ FOR YOU — DATABASE-FIRST RECOMMENDATIONS ══ */}
        {activeView === 'recommend' && (
          <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <h2 className="text-2xl font-bold text-slate-900">✨ For You</h2>
                <p className="text-sm text-slate-500">Recommendations from our tagged book database</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button onClick={() => { setTagBookId(null); setTagBookData(null); setTagSearchQuery(''); setTagSearchResults([]); setActiveView('tag-book'); }}
                  className="flex items-center gap-2 rounded-xl border border-teal-200 bg-white px-4 py-2 text-sm font-semibold text-teal-700 hover:bg-teal-50">
                  <Tag className="h-4 w-4" /> Tag a Book
                </Button>
                <Button onClick={() => setForYouShowImport(!forYouShowImport)}
                  className="flex items-center gap-2 rounded-xl border border-teal-200 bg-white px-4 py-2 text-sm font-semibold text-teal-700 hover:bg-teal-50">
                  <Download className="h-4 w-4" /> Import Reading History
                </Button>
                {(forYouResults.length > 0 || forYouAiResults.length > 0) && (
                  <Button onClick={getForYouResults} disabled={forYouLoading}
                    className="flex items-center gap-2 rounded-xl bg-gradient-to-r from-slate-500 to-teal-600 px-4 py-2 text-sm font-semibold text-white hover:from-teal-600 hover:to-teal-700">
                    <RefreshCw className={`h-4 w-4 ${forYouLoading ? 'animate-spin' : ''}`} /> Refresh
                  </Button>
                )}
              </div>
            </div>

            {/* Reading History Import */}
            {forYouShowImport && (
              <Card className="overflow-hidden rounded-2xl border border-slate-100 shadow-sm p-4">
                <ReadingHistoryImport
                  readerData={readerData}
                  readerId={readerData?.id}
                  existingBooks={forYouAllBooks}
                  isAdmin={false}
                  onComplete={(result) => {
                    // Refresh bookDatabase
                    getDocs(collection(db, 'bookDatabase')).then(snap => {
                      const books = [];
                      snap.forEach(d => books.push({ id: d.id, ...d.data() }));
                      setForYouAllBooks(books);
                    });
                    // Trigger bookshelf refresh
                    setBookshelfRefreshKey(k => k + 1);
                  }}
                />
              </Card>
            )}

            {/* Preferences card */}
            <Card className="overflow-hidden rounded-2xl border border-slate-100 shadow-sm">
              <div className="flex items-center justify-between bg-gradient-to-r from-slate-500 to-teal-600 px-5 py-3">
                <div>
                  <p className="text-sm font-bold text-white">Your Reading Preferences</p>
                  <p className="text-xs text-slate-100">{Object.keys(forYouPrefs).length > 0 ? 'Saved — edit anytime' : 'Set your preferences to get started'}</p>
                </div>
                <Button variant="ghost" onClick={() => { setForYouShowPrefs(!forYouShowPrefs); if (!forYouShowPrefs) setForYouPrefsForm(forYouPrefs || {}); }}
                  className="flex items-center gap-1.5 rounded-xl bg-white/20 px-3 py-1.5 text-xs font-semibold text-white hover:bg-white/30">
                  {forYouShowPrefs ? 'Close' : Object.keys(forYouPrefs).length > 0 ? 'Edit preferences' : 'Set preferences'}
                  {forYouShowPrefs ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                </Button>
              </div>

              {/* Preferences summary */}
              {Object.keys(forYouPrefs).length > 0 && !forYouShowPrefs && (() => {
                const wants = [], avoids = [];
                for (const s of BOOK_TAXONOMY) {
                  for (const [item, val] of Object.entries(forYouPrefs[s.key] || {})) {
                    if (val === 'want') wants.push(item);
                    if (val === 'avoid') avoids.push(item);
                  }
                }
                return (wants.length > 0 || avoids.length > 0) ? (
                  <div className="flex flex-wrap gap-1.5 px-5 py-3">
                    {wants.slice(0, 8).map(w => <Badge key={w} className="bg-green-100 text-green-700 border-0">✓ {w}</Badge>)}
                    {avoids.slice(0, 4).map(a => <Badge key={a} className="bg-red-100 text-red-600 border-0">✗ {a}</Badge>)}
                    {(wants.length + avoids.length) > 12 && <Badge className="bg-slate-50 text-slate-400 border-0">+{wants.length + avoids.length - 12} more</Badge>}
                  </div>
                ) : null;
              })()}

              {/* Preferences form */}
              {forYouShowPrefs && (
                <div className="space-y-3 px-5 py-4">
                  <div className="flex items-center gap-4 rounded-lg bg-slate-50 px-3 py-2 text-[11px] text-slate-500">
                    <span className="flex items-center gap-1"><span className="inline-block h-3 w-3 rounded border border-slate-200 bg-white" /> No preference</span>
                    <span className="flex items-center gap-1"><span className="inline-block h-3 w-3 rounded bg-green-500 text-white text-center leading-3">✓</span> I want this</span>
                    <span className="flex items-center gap-1"><span className="inline-block h-3 w-3 rounded bg-red-500 text-white text-center leading-3">✗</span> I want to avoid</span>
                  </div>
                  {BOOK_TAXONOMY.map(section => {
                    const isOpen = forYouExpandedSections[section.key];
                    const sectionData = forYouPrefsForm[section.key] || {};
                    const wc = Object.values(sectionData).filter(v => v === 'want').length;
                    const ac = Object.values(sectionData).filter(v => v === 'avoid').length;
                    return (
                      <div key={section.key} className="rounded-xl border border-slate-200 overflow-hidden">
                        <button onClick={() => toggleForYouSection(section.key)}
                          className="flex w-full items-center justify-between px-4 py-2.5 text-left hover:bg-slate-50 transition-colors">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold text-slate-800">{section.title}</span>
                            {(wc > 0 || ac > 0) && (
                              <span className="flex items-center gap-1 text-[10px]">
                                {wc > 0 && <span className="rounded-full bg-green-100 px-1.5 py-0.5 font-semibold text-green-700">{wc} ✓</span>}
                                {ac > 0 && <span className="rounded-full bg-red-100 px-1.5 py-0.5 font-semibold text-red-600">{ac} ✗</span>}
                              </span>
                            )}
                          </div>
                          {isOpen ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
                        </button>
                        {isOpen && (
                          <div className="border-t border-slate-100 px-4 py-3 space-y-3">
                            {section.groups.map(group => (
                              <div key={group.label}>
                                <p className="mb-1.5 text-xs font-semibold uppercase tracking-widest text-slate-400">{group.label}</p>
                                <div className="flex flex-wrap gap-1.5">
                                  {group.items.map(item => {
                                    const val = sectionData[item];
                                    return (
                                      <button key={item} onClick={() => {
                                        setForYouPrefsForm(p => {
                                          const sec = { ...(p[section.key] || {}) };
                                          const next = cycleTagToggle(sec[item]);
                                          if (next === 'present') sec[item] = 'want';
                                          else if (next === 'absent') sec[item] = 'avoid';
                                          else delete sec[item];
                                          return { ...p, [section.key]: sec };
                                        });
                                      }}
                                        className={`flex items-center gap-1 rounded-lg border px-2.5 py-1 text-xs font-medium transition-all ${
                                          val === 'want' ? 'border-green-400 bg-green-50 text-green-700'
                                        : val === 'avoid' ? 'border-red-300 bg-red-50 text-red-600'
                                        : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
                                        {val === 'want' && <CheckCircle className="h-3 w-3" />}
                                        {val === 'avoid' && <X className="h-3 w-3" />}
                                        {item}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  <div className="flex gap-3 pt-2">
                    <Button onClick={() => saveForYouPrefs(forYouPrefsForm)} disabled={forYouSavingPrefs}
                      className="flex-1 rounded-xl bg-gradient-to-r from-slate-500 to-teal-600 py-2.5 text-sm font-bold text-white hover:from-teal-600 hover:to-teal-700">
                      {forYouSavingPrefs ? 'Saving…' : 'Save Preferences'}
                    </Button>
                    <Button variant="outline" onClick={() => setForYouShowPrefs(false)}
                      className="rounded-xl border-slate-200 px-6 py-2.5 text-sm font-semibold text-slate-600">Cancel</Button>
                  </div>
                </div>
              )}
            </Card>

            {/* Get Recommendations button */}
            {Object.keys(forYouPrefs).length > 0 && forYouResults.length === 0 && forYouAiResults.length === 0 && !forYouLoading && !forYouShowPrefs && !forYouError && (
              <div className="rounded-2xl border border-dashed border-teal-200 bg-teal-50/30 p-8 text-center">
                <Sparkles className="mx-auto mb-3 h-8 w-8 text-teal-500" />
                <p className="mb-4 text-sm font-semibold text-slate-700">Ready to discover your next favourite book?</p>
                <Button onClick={getForYouResults}
                  className="rounded-xl bg-gradient-to-r from-slate-500 to-teal-600 px-6 py-2.5 text-sm font-bold text-white hover:from-teal-600 hover:to-teal-700">
                  <Sparkles className="mr-2 h-4 w-4" /> Get Recommendations
                </Button>
              </div>
            )}

            {/* Loading */}
            {forYouLoading && (
              <div className="space-y-4">
                {[1, 2, 3].map(i => (
                  <div key={i} className="flex animate-pulse gap-4 rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
                    <div className="h-36 w-24 shrink-0 rounded-xl bg-slate-200" />
                    <div className="flex-1 space-y-3 py-1"><div className="h-4 w-2/3 rounded bg-slate-200" /><div className="h-3 w-1/3 rounded bg-slate-200" /><div className="h-3 w-full rounded bg-slate-100" /></div>
                  </div>
                ))}
                <p className="text-center text-xs text-slate-400">Searching our book database…</p>
              </div>
            )}

            {/* Error */}
            {forYouError && !forYouLoading && (
              <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-center">
                <p className="text-sm text-rose-700">{forYouError}</p>
                <Button variant="ghost" onClick={getForYouResults} className="mt-2 text-xs text-rose-600 hover:text-rose-800">Try again</Button>
              </div>
            )}

            {/* DB match count */}
            {forYouDbCount > 0 && !forYouLoading && (
              <p className="text-sm font-medium text-slate-600">{forYouDbCount} book{forYouDbCount !== 1 ? 's' : ''} in our database match your filters</p>
            )}

            {/* Database results */}
            {forYouResults.length > 0 && (
              <div className="space-y-4">
                {forYouResults.map(book => (
                  <Card key={book.id} className="overflow-hidden rounded-2xl border border-slate-100 shadow-sm">
                    <div className="flex gap-4 p-4">
                      {book.cover ? (
                        <img src={book.cover} alt={book.title} className="h-40 w-[105px] shrink-0 rounded-xl object-cover shadow-sm" />
                      ) : (
                        <div className="flex h-40 w-[105px] shrink-0 items-center justify-center rounded-xl bg-slate-100"><BookOpen className="h-8 w-8 text-slate-300" /></div>
                      )}
                      <div className="min-w-0 flex-1">
                        <h3 className="text-base font-bold text-slate-900 leading-snug">{book.title}</h3>
                        <p className="text-sm text-slate-500">{book.author}</p>
                        {book.matchScore > 0 && (
                          <div className="mt-2 flex items-center gap-2">
                            <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-100">
                              <div className="h-full rounded-full bg-gradient-to-r from-teal-400 to-teal-600 transition-all" style={{ width: `${book.matchScore}%` }} />
                            </div>
                            <span className="text-xs font-bold text-teal-600">{book.matchScore}%</span>
                          </div>
                        )}
                        {/* Verified match tags */}
                        {book.matches?.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {book.matches.map(m => (
                              <span key={m.item} className={`inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-[10px] font-medium ${
                                m.source === 'admin' ? 'bg-slate-800 text-white' : 'bg-green-100 text-green-700'
                              }`}>
                                {m.source === 'admin' ? '🔒' : '✓'} {m.item}
                              </span>
                            ))}
                            {book.differences?.map(d => (
                              <span key={d} className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-400">✗ {d}</span>
                            ))}
                          </div>
                        )}
                        {book.description && <p className="mt-1.5 text-xs text-slate-500 leading-relaxed">{book.description}</p>}
                        <div className="mt-3 flex flex-wrap gap-2">
                          {book.googleBooksLink && (
                            <a href={book.googleBooksLink} target="_blank" rel="noopener noreferrer"
                              className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-teal-700 transition-colors">
                              <ExternalLink className="h-3 w-3" /> Find this book
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}

            {/* AI fallback results */}
            {forYouAiResults.length > 0 && (
              <div className="space-y-4">
                <p className="text-xs font-semibold uppercase tracking-widest text-slate-400">AI Suggested — not yet in our database</p>
                {forYouAiResults.map(book => (
                  <Card key={book.title} className="overflow-hidden rounded-2xl border border-dashed border-slate-200 shadow-sm">
                    <div className="flex gap-4 p-4">
                      {book.cover ? (
                        <img src={book.cover} alt={book.title} className="h-36 w-24 shrink-0 rounded-xl object-cover shadow-sm" />
                      ) : (
                        <div className="flex h-36 w-24 shrink-0 items-center justify-center rounded-xl bg-slate-100"><BookOpen className="h-8 w-8 text-slate-300" /></div>
                      )}
                      <div className="min-w-0 flex-1">
                        <h3 className="text-base font-bold text-slate-900 leading-snug">{book.title}</h3>
                        <p className="text-sm text-slate-500">{book.author}</p>
                        <p className="mt-1 text-xs italic text-teal-600">{book.reason}</p>
                        {book.description && <p className="mt-1 text-xs text-slate-500 leading-relaxed">{book.description}</p>}
                        <div className="mt-3 flex flex-wrap gap-2">
                          {book.infoLink && (
                            <a href={book.infoLink} target="_blank" rel="noopener noreferrer"
                              className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-teal-700 transition-colors">
                              <ExternalLink className="h-3 w-3" /> Find this book
                            </a>
                          )}
                          <Button variant="ghost" size="sm" onClick={() => {
                            if (book.googleBooksId) openTagEditor({ ...book, source: 'google', googleBooksId: book.googleBooksId });
                            setActiveView('tag-book');
                          }}
                            className="flex items-center gap-1 rounded-lg border border-teal-200 px-2.5 py-1.5 text-xs text-teal-600 hover:bg-teal-50">
                            <Tag className="h-3 w-3" /> Tag this book
                          </Button>
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ══ TAG A BOOK VIEW ══ */}
        {activeView === 'tag-book' && (
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <Button variant="ghost" onClick={() => setActiveView('library')} className="p-1"><ArrowLeft className="h-5 w-5 text-slate-600" /></Button>
              <h2 className="text-2xl font-bold text-slate-900">🏷️ Tag a Book</h2>
            </div>

            {!tagBookId ? (
              <>
                <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
                  <p className="mb-3 text-sm text-slate-600">Search for a book to tag. Help other readers discover great books!</p>
                  <Input value={tagSearchQuery} onChange={e => { setTagSearchQuery(e.target.value); searchBooksForTagging(e.target.value); }}
                    placeholder="Search by title or author…" className="text-sm" />
                </div>
                {tagSearching && <p className="text-center text-xs text-slate-400">Searching…</p>}
                {tagSearchResults.length > 0 && (
                  <div className="space-y-2">
                    {tagSearchResults.map(book => (
                      <div key={book.id} className="flex items-center gap-4 rounded-xl border border-slate-100 bg-white p-3 shadow-sm hover:border-teal-200 transition-colors">
                        {book.cover ? (
                          <img src={book.cover} alt={book.title} className="h-16 w-11 shrink-0 rounded object-cover" />
                        ) : (
                          <div className="flex h-16 w-11 shrink-0 items-center justify-center rounded bg-slate-100"><BookOpen className="h-5 w-5 text-slate-300" /></div>
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-bold text-slate-900 truncate">{book.title}</p>
                          <p className="text-xs text-slate-500">{book.author}</p>
                          {book.source === 'database' && <Badge className="mt-1 bg-teal-100 text-teal-700 border-0 text-[10px]">In database</Badge>}
                        </div>
                        <Button size="sm" onClick={() => openTagEditor(book)}
                          className="shrink-0 rounded-lg bg-teal-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-teal-700">
                          Tag
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <>
                {/* Tag editor for selected book */}
                <Card className="overflow-hidden rounded-2xl border border-slate-100 shadow-sm">
                  <div className="flex gap-4 bg-gradient-to-r from-slate-50 to-white p-4">
                    {tagBookData?.cover ? (
                      <img src={tagBookData.cover} alt={tagBookData.title} className="h-24 w-16 shrink-0 rounded-lg object-cover shadow-sm" />
                    ) : (
                      <div className="flex h-24 w-16 shrink-0 items-center justify-center rounded-lg bg-slate-100"><BookOpen className="h-6 w-6 text-slate-300" /></div>
                    )}
                    <div>
                      <h3 className="text-lg font-bold text-slate-900">{tagBookData?.title}</h3>
                      <p className="text-sm text-slate-500">{tagBookData?.author}</p>
                      <p className="mt-1 text-xs text-slate-400">Tag what's in this book to help others find it</p>
                    </div>
                  </div>
                </Card>

                <div className="flex items-center gap-4 rounded-lg bg-slate-50 px-3 py-2 text-[11px] text-slate-500">
                  <span className="flex items-center gap-1"><span className="inline-block h-3 w-3 rounded border border-slate-200 bg-white" /> Unverified</span>
                  <span className="flex items-center gap-1"><span className="inline-block h-3 w-3 rounded bg-green-500 text-white text-center leading-3">✓</span> Present</span>
                  <span className="flex items-center gap-1"><span className="inline-block h-3 w-3 rounded bg-red-500 text-white text-center leading-3">✗</span> Absent</span>
                </div>

                {BOOK_TAXONOMY.map(section => {
                  const isOpen = tagExpandedSections[section.key];
                  const sectionData = tagForm[section.key] || {};
                  const pc = Object.values(sectionData).filter(v => v === 'present').length;
                  const ac = Object.values(sectionData).filter(v => v === 'absent').length;
                  return (
                    <div key={section.key} className="rounded-xl border border-slate-200 overflow-hidden">
                      <button onClick={() => toggleTagSection(section.key)}
                        className="flex w-full items-center justify-between px-4 py-2.5 text-left hover:bg-slate-50 transition-colors">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-slate-800">{section.title}</span>
                          {(pc > 0 || ac > 0) && (
                            <span className="flex items-center gap-1 text-[10px]">
                              {pc > 0 && <span className="rounded-full bg-green-100 px-1.5 py-0.5 font-semibold text-green-700">{pc} ✓</span>}
                              {ac > 0 && <span className="rounded-full bg-red-100 px-1.5 py-0.5 font-semibold text-red-600">{ac} ✗</span>}
                            </span>
                          )}
                        </div>
                        {isOpen ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
                      </button>
                      {isOpen && (
                        <div className="border-t border-slate-100 px-4 py-3 space-y-3">
                          {section.groups.map(group => (
                            <div key={group.label}>
                              <p className="mb-1.5 text-xs font-semibold uppercase tracking-widest text-slate-400">{group.label}</p>
                              <div className="flex flex-wrap gap-1.5">
                                {group.items.map(item => {
                                  const val = sectionData[item];
                                  return (
                                    <button key={item} onClick={() => {
                                      setTagForm(p => {
                                        const sec = { ...(p[section.key] || {}) };
                                        const next = cycleTagToggle(sec[item]);
                                        if (next) sec[item] = next; else delete sec[item];
                                        return { ...p, [section.key]: sec };
                                      });
                                    }}
                                      className={`flex items-center gap-1 rounded-lg border px-2.5 py-1 text-xs font-medium transition-all ${
                                        val === 'present' ? 'border-green-400 bg-green-50 text-green-700'
                                      : val === 'absent' ? 'border-red-300 bg-red-50 text-red-600'
                                      : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
                                      {val === 'present' && <CheckCircle className="h-3 w-3" />}
                                      {val === 'absent' && <X className="h-3 w-3" />}
                                      {item}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}

                {tagMessage && (
                  <div className={`rounded-xl border p-3 text-center text-sm font-medium ${
                    tagMessage.includes('points') || tagMessage.includes('updated') ? 'border-green-200 bg-green-50 text-green-700' : 'border-rose-200 bg-rose-50 text-rose-700'
                  }`}>{tagMessage}</div>
                )}
                <Button onClick={handleSubmitTags} disabled={tagSubmitting}
                  className="w-full rounded-xl bg-gradient-to-r from-slate-500 to-teal-600 py-2.5 text-sm font-bold text-white hover:from-teal-600 hover:to-teal-700">
                  {tagSubmitting ? 'Saving…' : tagMySubmission ? 'Update My Tags' : 'Submit Tags (+10 points)'}
                </Button>
                <Button variant="outline" onClick={() => { setTagBookId(null); setTagBookData(null); }}
                  className="w-full rounded-xl border-slate-200 py-2 text-sm text-slate-600">Back to search</Button>
              </>
            )}
          </div>
        )}

        {/* ══ PROFILE VIEW ══ */}
        {activeView === 'profile' && isGuestUser && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-slate-900">Guest Profile</h2>
            <Card className="rounded-2xl border-slate-100 p-6 shadow-sm space-y-4">
              <h3 className="font-bold text-slate-900">Your Details</h3>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label className="mb-1 block text-slate-600">Name</Label>
                  <p className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-700">{guestData?.name || '—'}</p>
                </div>
                <div>
                  <Label className="mb-1 block text-slate-600">Email</Label>
                  <p className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-700">{currentReader?.email || '—'}</p>
                </div>
              </div>
            </Card>
            <Card className="rounded-2xl border-slate-100 p-6 shadow-sm space-y-4">
              <h3 className="font-bold text-slate-900">Shipping Address</h3>
              <div className="space-y-3">
                {[['line1','Address Line 1'],['line2','Address Line 2']].map(([field, label]) => (
                  <div key={field}>
                    <Label className="mb-1 block text-slate-600">{label}</Label>
                    <Input type="text" value={guestData?.shippingAddress?.[field] || ''}
                      onChange={async (e) => {
                        const newAddr = { ...guestData?.shippingAddress, [field]: e.target.value };
                        setGuestData(d => ({ ...d, shippingAddress: newAddr }));
                        await updateDoc(doc(db, 'guests', currentReader.uid), { shippingAddress: newAddr });
                      }} />
                  </div>
                ))}
                <div className="grid gap-3 sm:grid-cols-3">
                  {[['city','City'],['postalCode','Postcode'],['country','Country']].map(([field, label]) => (
                    <div key={field}>
                      <Label className="mb-1 block text-slate-600">{label}</Label>
                      <Input type="text" value={guestData?.shippingAddress?.[field] || ''}
                        onChange={async (e) => {
                          const newAddr = { ...guestData?.shippingAddress, [field]: e.target.value };
                          setGuestData(d => ({ ...d, shippingAddress: newAddr }));
                          await updateDoc(doc(db, 'guests', currentReader.uid), { shippingAddress: newAddr });
                        }} />
                    </div>
                  ))}
                </div>
              </div>
            </Card>
            <Card className="rounded-2xl border-slate-100 p-6 shadow-sm space-y-4">
              <h3 className="font-bold text-slate-900">Device Emails</h3>
              <p className="text-sm text-slate-500">Add your Kindle or Kobo email to receive eBooks directly on your device after purchase.</p>
              <div>
                <Label className="mb-1 block text-slate-600">Kindle Email</Label>
                <Input type="email" value={deviceEmails.kindleEmail} onChange={e => setDeviceEmails({...deviceEmails, kindleEmail: e.target.value})}
                  placeholder="yourname@kindle.com" />
              </div>
              <div>
                <Label className="mb-1 block text-slate-600">Kobo Email</Label>
                <Input type="email" value={deviceEmails.koboEmail} onChange={e => setDeviceEmails({...deviceEmails, koboEmail: e.target.value})}
                  placeholder="yourname@kobo.com" />
              </div>
              <Button onClick={async () => {
                try {
                  // kindleEmail goes to readers/{id}.eReaderEmail (canonical).
                  // koboEmail and display fields stay on readerProfiles.
                  // Guests without a readers/ doc fall through to the
                  // readerProfiles-only path below.
                  if (readerData?.id) {
                    await updateDoc(doc(db, 'readers', readerData.id), {
                      eReaderEmail: deviceEmails.kindleEmail.trim().toLowerCase(),
                      lastUpdated: serverTimestamp(),
                    });
                  }
                  await setDoc(doc(db, 'readerProfiles', currentReader.uid), {
                    ...(readerData?.id ? {} : { kindleEmail: deviceEmails.kindleEmail.trim().toLowerCase() }),
                    koboEmail: deviceEmails.koboEmail.trim().toLowerCase(),
                    displayName: guestData?.name || '',
                    email: currentReader.email,
                    lastUpdated: serverTimestamp()
                  }, { merge: true });
                  setSaveMessage('Device emails saved!');
                  setTimeout(() => setSaveMessage(''), 3000);
                } catch (err) { setSaveMessage(`Error: ${err.message}`); }
              }} className="bg-gradient-to-r from-slate-500 to-teal-600 text-white hover:from-teal-600 hover:to-teal-700">
                Save Device Emails
              </Button>
              {saveMessage && <p className="text-sm text-center text-green-600">{saveMessage}</p>}
            </Card>
            <div className="rounded-2xl border-2 border-teal-200 bg-teal-50 p-6 text-center">
              <h3 className="font-bold text-slate-900 mb-2">Want full portal access?</h3>
              <p className="text-sm text-slate-600 mb-4">Upgrade to access ARCs, community features, reviews, rewards, and exclusive signed editions.</p>
              <Button onClick={() => setShowUpgradeModal(true)} className="bg-teal-600 text-white hover:bg-teal-700">
                Upgrade to Full Portal
              </Button>
            </div>
          </div>
        )}

        {activeView === 'profile' && !isGuestUser && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-slate-900">My Profile</h2>
            <form onSubmit={handleProfileSave} className="space-y-6">

              <Card className="rounded-2xl border-slate-100 p-6 shadow-sm space-y-4">
                <h3 className="font-bold text-slate-900">Personal Details</h3>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <Label className="mb-1 block text-slate-600">First Name</Label>
                    <Input type="text" value={profileForm.firstName} onChange={e => setProfileForm({...profileForm, firstName: e.target.value})}
                      placeholder="Jane" />
                  </div>
                  <div>
                    <Label className="mb-1 block text-slate-600">Last Name</Label>
                    <Input type="text" value={profileForm.lastName} onChange={e => setProfileForm({...profileForm, lastName: e.target.value})}
                      placeholder="Smith" />
                  </div>
                </div>
                <div>
                  <Label className="mb-1 flex items-center gap-1.5 text-slate-600">
                    Kindle Email
                    <span className="relative group">
                      <Info className="h-3.5 w-3.5 text-slate-400 cursor-help" />
                      <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 w-56 rounded-md bg-slate-800 px-3 py-2 text-xs font-normal text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100">
                        This is your Send to Kindle email address, found in Amazon under Manage Your Content and Devices, Preferences, Personal Document Settings
                      </span>
                    </span>
                  </Label>
                  <Input type="email" value={deviceEmails.kindleEmail} onChange={e => setDeviceEmails({...deviceEmails, kindleEmail: e.target.value})}
                    placeholder="yourname@kindle.com" />
                  <p className="mt-1 text-xs text-slate-400">Find your Kindle email in your Amazon account under Manage Your Content and Devices</p>
                </div>
                <div>
                  <Label className="mb-1 block text-slate-600">Kobo Email</Label>
                  <Input type="email" value={deviceEmails.koboEmail} onChange={e => setDeviceEmails({...deviceEmails, koboEmail: e.target.value})}
                    placeholder="yourname@kobo.com" />
                  <p className="mt-1 text-xs text-slate-400">Find your Kobo email in your Kobo account settings under My Kobo</p>
                </div>
              </Card>

              <Card className="rounded-2xl border-slate-100 p-6 shadow-sm space-y-4">
                <h3 className="font-bold text-slate-900">Shipping Address</h3>
                <div className="space-y-3">
                  {[['line1','Address Line 1','123 Main Street'],['line2','Address Line 2','Apt 4']].map(([field, label, placeholder]) => (
                    <div key={field}>
                      <Label className="mb-1 block text-slate-600">{label}</Label>
                      <Input type="text" value={profileForm.shippingAddress[field]} placeholder={placeholder}
                        onChange={e => setProfileForm({...profileForm, shippingAddress: {...profileForm.shippingAddress, [field]: e.target.value}})} />
                    </div>
                  ))}
                  <div className="grid gap-3 sm:grid-cols-3">
                    {[['city','City','London'],['postalCode','Postcode','SW1A 1AA'],['country','Country','United Kingdom']].map(([field, label, placeholder]) => (
                      <div key={field}>
                        <Label className="mb-1 block text-slate-600">{label}</Label>
                        <Input type="text" value={profileForm.shippingAddress[field]} placeholder={placeholder}
                          onChange={e => setProfileForm({...profileForm, shippingAddress: {...profileForm.shippingAddress, [field]: e.target.value}})} />
                      </div>
                    ))}
                  </div>
                  <div>
                    <Label className="mb-1 block text-slate-600">Phone Number</Label>
                    <Input type="tel" value={profileForm.phoneNumber} placeholder="+44 7700 900000"
                      onChange={e => setProfileForm({...profileForm, phoneNumber: e.target.value})} />
                    <p className="mt-1 text-xs text-slate-400">Optional — used for delivery notifications</p>
                  </div>
                </div>
              </Card>

              <div className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm space-y-4">
                <h3 className="font-bold text-slate-900">Social Media & Review Platforms</h3>
                <div className="grid gap-4 sm:grid-cols-2">
                  {[
                    ['instagram','📷 Instagram','@username'],
                    ['tiktok','📱 TikTok','@username'],
                    ['goodreads','📚 Goodreads','Display name'],
                    ['amazon','📦 Amazon','Review name'],
                  ].map(([field, label, placeholder]) => (
                    <div key={field}>
                      <label className="mb-1 block text-sm font-medium text-slate-600">{label}</label>
                      <input type="text" value={profileForm.socialMedia[field]} placeholder={placeholder}
                        onChange={e => setProfileForm({...profileForm, socialMedia: {...profileForm.socialMedia, [field]: e.target.value}})}
                        className="w-full rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-slate-900 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200" />
                    </div>
                  ))}
                  <div className="sm:col-span-2">
                    <label className="mb-1 block text-sm font-medium text-slate-600">📝 Other Platforms</label>
                    <input type="text" value={profileForm.socialMedia.other} placeholder="BookBub, Storygraph, blog URL…"
                      onChange={e => setProfileForm({...profileForm, socialMedia: {...profileForm.socialMedia, other: e.target.value}})}
                      className="w-full rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-slate-900 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200" />
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm space-y-4">
                <h3 className="font-bold text-slate-900">Reading Preferences</h3>
                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-600">Favourite Genres</label>
                  <div className="flex flex-wrap gap-2">
                    {['Fantasy','Romance','Dark','Contemporary','Mystery','Sci-Fi','Romantasy','Thriller'].map(genre => (
                      <button key={genre} type="button" onClick={() => toggleGenre(genre)}
                        className={`rounded-full px-3 py-1 text-sm font-medium transition-all ${profileForm.preferences.favoriteGenres.includes(genre) ? 'bg-slate-500 text-white' : 'border border-slate-200 bg-white text-slate-600 hover:border-teal-300'}`}>
                        {genre}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-600">Favourite Tropes</label>
                  <textarea rows={2} value={profileForm.preferences.favoriteTropes} placeholder="Enemies to lovers, slow burn, found family…"
                    onChange={e => setProfileForm({...profileForm, preferences: {...profileForm.preferences, favoriteTropes: e.target.value}})}
                    className="w-full rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-slate-900 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200" />
                </div>
              </div>

              {saveMessage && (
                <div className={`rounded-lg border p-3 text-center font-medium ${saveMessage.includes('✅') ? 'border-green-200 bg-green-50 text-green-700' : 'border-rose-200 bg-rose-50 text-rose-700'}`}>
                  {saveMessage}
                </div>
              )}
              <Button type="submit" disabled={!readerData}
                className="w-full bg-gradient-to-r from-slate-500 to-teal-600 py-3 text-base font-semibold text-white hover:from-teal-600 hover:to-teal-700">
                💾 Save Profile
              </Button>
            </form>
          </div>
        )}

        {/* ══ BOOK DETAIL VIEW ══ */}
        {activeView === 'book-detail' && selectedBookObj && (() => {
          const bookApp = myApprovedApps.find(a => a.bookId === selectedBookId);
          const detailAuthorId = bookApp?.authorId || selectedBookObj?.authorId;
          if (!detailAuthorId) {
            return (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 p-6 text-center">
                <p className="font-semibold text-rose-900">Cannot load book details</p>
                <p className="mt-1 text-sm text-rose-700">No author context available for this book. Please refresh, or select an author from the menu.</p>
              </div>
            );
          }
          const existingReview = myReviews.find(r => r.bookId === selectedBookId);
          const hasDigitalOrPhysical = bookDetailEbooks.length > 0 || bookDetailAudiobooks.length > 0 || bookDetailProducts.length > 0;
          const isPendingBook = ['upcoming', 'note_interest', 'draft'].includes(selectedBookObj.status || '');

          return (
          <div className="space-y-6">

            {/* Book header — premium dark */}
            <div className="-mx-4 sm:-mx-6 -mt-6 mb-2 bg-slate-900 px-4 sm:px-6 py-8">
              <div className="mx-auto flex max-w-4xl flex-col items-center gap-6 sm:flex-row sm:items-start sm:gap-8">
                <div className="shrink-0">
                  <img
                    src={selectedBookObj.coverUrl || FALLBACK_COVER}
                    alt={stripBookSuffix(selectedBookObj.title)}
                    className="h-[220px] w-[160px] rounded-xl object-cover shadow-2xl shadow-black/40"
                    style={{ border: '2px solid #d4a574' }}
                  />
                </div>
                <div className="flex-1 min-w-0 text-center sm:text-left">
                  <p className="mb-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-yellow-500/90">
                    {selectedBookObj.series || 'Memories of Memnara'} — Book 1
                  </p>
                  <h2 className="text-2xl sm:text-3xl font-bold text-white leading-tight" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>
                    {stripBookSuffix(selectedBookObj.title)}
                  </h2>
                  {selectedBookObj.author && <p className="mt-1 text-sm text-slate-400">by {selectedBookObj.author}</p>}
                  <div className="mt-4 flex flex-wrap justify-center sm:justify-start gap-2">
                    {selectedBookObj.genres?.map(g => {
                      const label = typeof g === 'object' ? g.label : g;
                      return <span key={label} className="rounded-full border border-teal-500/40 bg-slate-800 px-3 py-0.5 text-xs font-medium text-teal-300">{label}</span>;
                    })}
                  </div>
                  {selectedBookObj.goLiveDate && (
                    <div className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-slate-800 px-3 py-1 text-xs font-medium text-slate-300">
                      <CalendarIcon className="h-3 w-3 text-teal-400" /> Launch: {selectedBookObj.goLiveDate}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Book detail tabs — bold style */}
            <div className="flex border-b-2 border-slate-200">
              {(isPendingBook
                ? [{ id: 'overview', label: 'Overview' }]
                : [
                    { id: 'overview', label: 'Overview' },
                    { id: 'review', label: 'Review' },
                    { id: 'shop', label: 'BUY NOW!', highlight: true },
                    { id: 'orders', label: 'Orders' },
                  ]
              ).map(tab => (
                <button key={tab.id} onClick={() => setBookDetailTab(tab.id)}
                  className={`flex-1 px-3 py-3 text-sm font-bold transition-colors border-b-3 -mb-[2px] ${
                    tab.highlight
                      ? bookDetailTab === tab.id
                        ? 'border-teal-600 bg-teal-600 text-white'
                        : 'border-teal-500 bg-teal-50 text-teal-700 hover:bg-teal-100'
                      : bookDetailTab === tab.id
                        ? 'border-teal-600 text-slate-900'
                        : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                  }`}>
                  {tab.label}
                </button>
              ))}
            </div>

            {/* ══ OVERVIEW TAB ══ */}
            {bookDetailTab === 'overview' && (
              <div className="space-y-6">
                {/* Blurb */}
                {(selectedBookObj.blurb || selectedBookObj.description) && (
                  <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
                    <h3 className="mb-3 font-bold text-slate-900 flex items-center gap-2">
                      <BookOpen className="h-4 w-4 text-teal-600" /> About This Book
                    </h3>
                    <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
                      {stripBlurbEmoji(selectedBookObj.blurb || selectedBookObj.description)}
                    </p>
                  </div>
                )}

                {/* Content / Trigger warnings */}
                {selectedBookObj.contentWarnings && (
                  <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
                    <h3 className="mb-3 font-bold text-slate-900 flex items-center gap-2">
                      <Info className="h-4 w-4 text-slate-500" /> Content Warnings
                    </h3>
                    <p className="text-sm text-slate-600 whitespace-pre-wrap leading-relaxed">
                      {selectedBookObj.contentWarnings}
                    </p>
                  </div>
                )}

                {isPendingBook ? (
                  /* Pending book — mailing list CTA only */
                  <div className="rounded-2xl border border-blue-200 bg-blue-50/50 p-6 shadow-sm text-center space-y-4">
                    <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-blue-100">
                      <Mail className="h-7 w-7 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-slate-900">Coming Soon</h3>
                      <p className="mt-1 text-sm text-slate-600">This book isn't available yet. Join the mailing list to be notified when it launches.</p>
                    </div>
                    <Button
                      onClick={() => { setSelectBookModal(selectedBookObj); setSelectBookOption(2); setSelectBookMessage(''); setSelectBookSuccess(null); }}
                      className="bg-gradient-to-r from-slate-500 to-teal-600 px-8 py-2.5 font-semibold text-white hover:from-teal-600 hover:to-teal-700">
                      <Mail className="mr-2 h-4 w-4" /> Join Mailing List
                    </Button>
                  </div>
                ) : (
                  /* Active book — full options */
                  <>
                    {/* ARC Status */}
                    <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
                      <h3 className="mb-4 font-bold text-slate-900 flex items-center gap-2">
                        <User className="h-4 w-4 text-teal-600" /> My Status
                      </h3>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between rounded-lg bg-slate-50 px-4 py-3">
                          <span className="text-sm text-slate-600">ARC Status</span>
                          {bookApp ? (
                            <Badge className="bg-green-100 text-green-700">
                              <CheckCircle className="mr-1 h-3 w-3" /> Approved
                            </Badge>
                          ) : (
                            <span className="text-sm text-slate-400">Not enrolled</span>
                          )}
                        </div>
                        {(readerData?.arcStatus || readerData?.status) && (
                          <div className="flex items-center justify-between rounded-lg bg-slate-50 px-4 py-3">
                            <span className="text-sm text-slate-600">Reader Status</span>
                            <Badge className="bg-purple-100 text-purple-700">{readerData.arcStatus || readerData.status}</Badge>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Author announcements for this book */}
                    {authorAnnouncements.filter(a => a.bookId === selectedBookId || a.bookId === 'all').length > 0 && (
                      <div className="rounded-2xl border border-teal-200 bg-teal-50/50 p-5 shadow-sm space-y-3">
                        <h3 className="font-bold text-slate-900 flex items-center gap-2">
                          <Bell className="h-4 w-4 text-teal-600" /> Author Messages
                        </h3>
                        {authorAnnouncements.filter(a => a.bookId === selectedBookId || a.bookId === 'all').map(ann => (
                          <div key={ann.id} className="rounded-lg bg-white border border-teal-100 p-3">
                            <p className="text-xs font-semibold text-teal-700">{ann.authorName}</p>
                            <p className="font-medium text-slate-900 text-sm">{ann.title}</p>
                            <p className="text-sm text-slate-600 mt-0.5">{ann.body}</p>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Get This Book CTA */}
                    <button
                      onClick={() => setBookDetailTab('shop')}
                      className="w-full rounded-2xl bg-gradient-to-r from-teal-600 to-teal-700 p-5 text-left shadow-lg shadow-teal-600/15 transition-all hover:from-teal-500 hover:to-teal-600 hover:shadow-teal-500/25"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-lg font-bold text-white flex items-center gap-2">
                            <ShoppingCart className="h-5 w-5" /> Get This Book <span className="text-teal-200">→</span>
                          </p>
                          <p className="mt-0.5 text-sm text-teal-100">Own Your Copy — eBook, Paperback & More</p>
                        </div>
                      </div>
                    </button>

                    {/* Quick actions */}
                    <div className="flex gap-3">
                      {existingReview ? (
                        <Button variant="outline" onClick={() => setBookDetailTab('review')}
                          className="flex-1 gap-1.5 border-green-200 text-green-700">
                          <CheckCircle className="h-4 w-4" /> View My Review
                        </Button>
                      ) : (
                        <Button onClick={() => setBookDetailTab('review')}
                          className="flex-1 gap-1.5 bg-gradient-to-r from-slate-500 to-teal-600 text-white hover:from-teal-600 hover:to-teal-700">
                          <Star className="h-4 w-4" /> Leave a Review
                        </Button>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* ══ REVIEW TAB ══ */}
            {bookDetailTab === 'review' && (
              <div className="space-y-6">

            {/* ── Book identity header ── */}
            <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
              <div className="flex items-center gap-4">
                <img
                  src={selectedBookObj.coverUrl || FALLBACK_COVER}
                  alt={stripBookSuffix(selectedBookObj.title)}
                  className="h-24 w-[66px] rounded-lg object-cover shadow-md"
                />
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-bold text-slate-900 leading-tight" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>
                    {stripBookSuffix(selectedBookObj.title)}
                  </h3>
                  {(selectedBookObj.series || selectedBookObj.seriesNumber) && (
                    <p className="mt-0.5 text-sm text-slate-500">
                      {selectedBookObj.series || 'Memories of Memnara'}
                      {selectedBookObj.seriesNumber ? ` — Book ${selectedBookObj.seriesNumber}` : ''}
                    </p>
                  )}
                  {selectedBookObj.seriesNumber && (
                    <div className="mt-2">
                      <span className={`inline-block rounded-full px-3 py-0.5 text-xs font-bold ${
                        Number(selectedBookObj.seriesNumber) === 1
                          ? 'bg-green-100 text-green-700'
                          : Number(selectedBookObj.seriesNumber) === 2
                            ? 'bg-purple-100 text-purple-700'
                            : 'bg-blue-100 text-blue-700'
                      }`}>
                        Book {selectedBookObj.seriesNumber}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* ── My Review (submitted) ── */}
            {selectedBookObj.reviewsEnabled === false && !existingReview ? (
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-8 text-center">
                <Star className="mx-auto mb-3 h-8 w-8 text-slate-300" />
                <p className="text-base font-semibold text-slate-700">Reviews for this book aren't open yet</p>
                <p className="mt-1 text-sm text-slate-500">Check back closer to launch!</p>
              </div>
            ) : existingReview ? (
              <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
                <h3 className="mb-4 font-bold text-slate-900 flex items-center gap-2">
                  <Star className="h-4 w-4 text-teal-600" /> My Review
                </h3>
                <div className="space-y-3">
                  {existingReview.rating && (
                    <div className="flex items-center gap-1">
                      {[1,2,3,4,5].map(star => (
                        <span key={star} className="text-2xl" style={{ color: existingReview.rating >= star ? '#f59e0b' : '#e5e7eb' }}>★</span>
                      ))}
                      <span className="ml-2 text-sm font-semibold text-slate-600">{existingReview.rating}/5</span>
                    </div>
                  )}
                  {existingReview.quickSummary && (
                    <div className="rounded-lg bg-slate-50 p-3">
                      <p className="text-xs font-semibold text-slate-500 mb-1">Summary</p>
                      <p className="text-sm text-slate-700">{existingReview.quickSummary}</p>
                    </div>
                  )}
                  {existingReview.fullReview && (
                    <div className="rounded-lg bg-slate-50 p-3">
                      <p className="text-xs font-semibold text-slate-500 mb-1">Full Review</p>
                      <p className="text-sm text-slate-700 whitespace-pre-wrap">{existingReview.fullReview}</p>
                    </div>
                  )}
                  {existingReview.favoriteQuote && (
                    <div className="rounded-lg bg-teal-50 p-3 border border-teal-100">
                      <p className="text-xs font-semibold text-teal-600 mb-1">Favourite Quote</p>
                      <p className="text-sm italic text-teal-800">"{existingReview.favoriteQuote}"</p>
                    </div>
                  )}
                  {existingReview.reviewLinks && Object.entries(existingReview.reviewLinks).filter(([,v]) => v).length > 0 && (
                    <div className="rounded-lg bg-slate-50 p-3">
                      <p className="text-xs font-semibold text-slate-500 mb-2">Review Links</p>
                      <div className="flex flex-wrap gap-1.5">
                        {Object.entries(existingReview.reviewLinks).filter(([,v]) => v).map(([platform, url]) => (
                          <a key={platform} href={url} target="_blank" rel="noopener noreferrer"
                            className="rounded-full bg-teal-100 px-2.5 py-0.5 text-xs font-medium text-teal-700 hover:bg-teal-200 transition-colors">
                            {platform}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-center text-sm font-medium text-green-800">
                    Review submitted — thank you!
                  </div>
                </div>
              </div>
            ) : (
              /* ── Review submission form ── */
              <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
                <h3 className="mb-4 font-bold text-slate-900 flex items-center gap-2">
                  <Star className="h-4 w-4 text-teal-600" /> Leave a Review
                </h3>
                <div className="space-y-4">

                  {/* Star rating */}
                  <div>
                    <Label className="text-xs font-semibold text-slate-600 mb-1 block">Rating *</Label>
                    <div className="flex items-center gap-1">
                      {[1,2,3,4,5].map(star => (
                        <button key={star} type="button"
                          onClick={() => setReviewRating(star)}
                          className="text-3xl transition-transform hover:scale-110"
                          style={{ color: reviewRating >= star ? '#f59e0b' : '#e5e7eb' }}>
                          ★
                        </button>
                      ))}
                      {reviewRating > 0 && <span className="ml-2 text-sm font-semibold text-slate-600">{reviewRating}/5</span>}
                    </div>
                  </div>

                  {/* Quick summary */}
                  <div>
                    <Label className="text-xs font-semibold text-slate-600 mb-1 block">Quick Summary</Label>
                    <Input value={quickSummary} onChange={e => setQuickSummary(e.target.value)}
                      placeholder="In one sentence, what did you think?" className="text-sm" />
                  </div>

                  {/* What I loved most */}
                  <div>
                    <Label className="text-xs font-semibold text-slate-600 mb-1 block">What I Loved Most</Label>
                    <select value={lovedMost} onChange={e => setLovedMost(e.target.value)}
                      className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-teal-500">
                      <option value="">Select...</option>
                      <option value="characters">The characters</option>
                      <option value="world-building">The world-building</option>
                      <option value="plot">The plot</option>
                      <option value="romance">The romance</option>
                      <option value="writing-style">The writing style</option>
                      <option value="magic-system">The magic system</option>
                      <option value="emotional-depth">The emotional depth</option>
                      <option value="other">Other</option>
                    </select>
                    {lovedMost && (
                      <Textarea value={lovedMostDetails} onChange={e => setLovedMostDetails(e.target.value)}
                        placeholder="Tell us more..." className="mt-2 text-sm" rows={2} />
                    )}
                  </div>

                  {/* Favourite character moment */}
                  <div>
                    <Label className="text-xs font-semibold text-slate-600 mb-1 block">Favourite Character Moment</Label>
                    <Input value={favoriteCharacterMoment} onChange={e => setFavoriteCharacterMoment(e.target.value)}
                      placeholder="A scene or moment that stayed with you" className="text-sm" />
                  </div>

                  {/* Target audience */}
                  <div>
                    <Label className="text-xs font-semibold text-slate-600 mb-1 block">Who would love this book?</Label>
                    <div className="flex flex-wrap gap-2">
                      {['Fantasy fans', 'Romance readers', 'YA/NA crossover', 'Fans of dark themes', 'Mythology lovers', 'Debut author supporters'].map(aud => (
                        <button key={aud} type="button"
                          onClick={() => setTargetAudience(prev => prev.includes(aud) ? prev.filter(a => a !== aud) : [...prev, aud])}
                          className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                            targetAudience.includes(aud)
                              ? 'bg-teal-600 text-white'
                              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                          }`}>
                          {aud}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Full review */}
                  <div>
                    <Label className="text-xs font-semibold text-slate-600 mb-1 block">Full Review</Label>
                    <Textarea value={fullReview} onChange={e => setFullReview(e.target.value)}
                      placeholder="Your detailed thoughts..." className="text-sm" rows={5} />
                  </div>

                  {/* Favourite quote */}
                  <div>
                    <Label className="text-xs font-semibold text-slate-600 mb-1 block">Favourite Quote</Label>
                    <Input value={favoriteQuote} onChange={e => setFavoriteQuote(e.target.value)}
                      placeholder="A line from the book you loved" className="text-sm" />
                  </div>

                  {/* Review links */}
                  <div>
                    <Label className="text-xs font-semibold text-slate-600 mb-2 block">Review Links (paste URLs where you posted)</Label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {['amazon', 'goodreads', 'instagram', 'tiktok', 'storygraph', 'bookbub', 'fable', 'blog', 'netgalley'].map(platform => (
                        <div key={platform} className="flex items-center gap-2">
                          <span className="w-20 text-xs font-medium text-slate-500 capitalize">{platform}</span>
                          <Input value={reviewLinks[platform] || ''} onChange={e => setReviewLinks(prev => ({ ...prev, [platform]: e.target.value }))}
                            placeholder={`${platform} URL`} className="text-xs flex-1" />
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Reader path */}
                  <div>
                    <Label className="text-xs font-semibold text-slate-600 mb-1 block">What best describes you?</Label>
                    <select value={readerPath} onChange={e => setReaderPath(e.target.value)}
                      className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-teal-500">
                      <option value="">Select...</option>
                      <option value="arc-priority">I want to be a priority ARC reader</option>
                      <option value="street-team">I want to join the street team</option>
                      <option value="not-for-me">This book wasn't quite for me</option>
                    </select>
                  </div>

                  {/* Private notes */}
                  <div>
                    <Label className="text-xs font-semibold text-slate-600 mb-1 block">Private Notes (only visible to the author)</Label>
                    <Textarea value={privateNotes} onChange={e => setPrivateNotes(e.target.value)}
                      placeholder="Anything you'd like the author to know privately..." className="text-sm" rows={2} />
                  </div>

                  {/* Submit */}
                  <div className="pt-2">
                    <Button onClick={handleSubmitReview} disabled={!reviewRating}
                      className="w-full bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50">
                      Submit Review (+50 points)
                    </Button>
                    {!reviewRating && <p className="mt-1 text-center text-xs text-slate-400">Please select a star rating to submit</p>}
                  </div>
                </div>
              </div>
            )}

              </div>
            )}

            {/* ══ SHOP TAB ══ */}
            {bookDetailTab === 'shop' && (
              <div className="space-y-6">

            {/* ── Promo code input — top of shop tab ── */}
            {!isGuestUser && (
              <div className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
                <div className="flex items-center gap-3">
                  <Tag className="h-5 w-5 text-teal-600 shrink-0" />
                  {promoApplied ? (
                    <div className="flex flex-1 items-center justify-between rounded-lg border border-teal-200 bg-teal-50 px-3 py-2">
                      <div className="flex items-center gap-2">
                        <code className="text-sm font-bold text-teal-700">{promoApplied.code}</code>
                        <span className="text-xs text-teal-600">
                          {(() => {
                            const mode = promoApplied.discountMode || 'all';
                            const dType = promoApplied.discountType || 'percentage';
                            const fmt = (v) => dType === 'fixed' ? `£${Number(v).toFixed(2)} off` : `${v}% off`;
                            if (mode === 'all') return fmt(promoApplied.discountValue);
                            // per_product — list each category
                            const ppd = promoApplied.perProductDiscounts || {};
                            const parts = [];
                            if (ppd.ebook !== undefined) parts.push(`${fmt(ppd.ebook)} eBooks`);
                            if (ppd.audiobook !== undefined) parts.push(`${fmt(ppd.audiobook)} audiobooks`);
                            const hasOverrides = Object.keys(promoApplied.productOverrides || {}).length > 0;
                            if (ppd.physical !== undefined) parts.push(`${fmt(ppd.physical)} ${hasOverrides ? 'selected physical products' : 'physical'}`);
                            return parts.length > 0 ? parts.join(', ') : fmt(promoApplied.discountValue);
                          })()}
                        </span>
                      </div>
                      <button onClick={removePromoCode} className="text-slate-400 hover:text-slate-600">
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex flex-1 gap-2">
                      <Input
                        value={promoCode}
                        onChange={e => { setPromoCode(e.target.value.toUpperCase()); setPromoError(''); }}
                        placeholder="Got a promo code?"
                        className="font-mono text-sm"
                      />
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => validatePromoCode(detailAuthorId)}
                        disabled={!promoCode.trim() || promoValidating}
                      >
                        {promoValidating ? 'Checking...' : 'Apply'}
                      </Button>
                    </div>
                  )}
                </div>
                {promoError && <p className="mt-1 ml-8 text-xs text-red-500">{promoError}</p>}
              </div>
            )}

            {/* ── Section D: Orders ── */}
            <div id="book-detail-orders">
              <ReaderOrders
                readerData={readerData}
                currentReader={currentReader}
                authorId={detailAuthorId}
                bookId={selectedBookId}
                promoApplied={promoApplied}
              />
            </div>

              </div>
            )}

            {/* ══ ORDERS TAB ══ */}
            {bookDetailTab === 'orders' && (
              <div className="space-y-6">
                <ReaderOrderHistory
                  currentReader={currentReader}
                  authorId={detailAuthorId}
                />
              </div>
            )}

          </div>
          );
        })()}
      </main>

      {/* ══════════════════════════════════════════════════
          MODALS
      ══════════════════════════════════════════════════ */}

{/* Upgrade from Guest Modal */}
      <Dialog open={showUpgradeModal} onOpenChange={(open) => { if (!open) { setShowUpgradeModal(false); setUpgradeError(''); } }}>
        <DialogContent className="max-w-md rounded-2xl p-6">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-slate-900">Upgrade to Full Portal</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600 mb-4">
            Unlock ARCs, community features, reviews, rewards, and more by upgrading your guest account.
            Your purchase history will be preserved.
          </p>
          {upgradeError && <div className="mb-4 rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-600">{upgradeError}</div>}
          <Button
            onClick={handleUpgradeToReader}
            disabled={upgrading}
            className="w-full bg-gradient-to-r from-slate-500 to-teal-600 font-semibold text-white hover:from-teal-600 hover:to-teal-700"
          >
            {upgrading ? 'Upgrading...' : 'Upgrade Now'}
          </Button>
        </DialogContent>
      </Dialog>

      {/* Select this Book — 3-option modal */}
      <Dialog open={!!selectBookModal} onOpenChange={(open) => { if (!open) { setSelectBookModal(null); setSelectBookOption(null); setSelectBookMessage(''); setSelectBookSuccess(null); } }}>
        <DialogContent className="max-w-lg rounded-2xl p-6">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-slate-900">Select this Book</DialogTitle>
          </DialogHeader>
          {selectBookModal && (
            <>
              <div className="mb-4 rounded-lg bg-slate-50 p-4">
                <p className="font-semibold text-slate-900">{selectBookModal.title}</p>
                {selectBookModal.series && <p className="text-sm text-slate-500">{selectBookModal.series}</p>}
              </div>

              {selectBookSuccess ? (
                <div className="rounded-lg border border-green-200 bg-green-50 p-4 text-center">
                  <CheckCircle className="mx-auto mb-2 h-8 w-8 text-green-600" />
                  <p className="font-semibold text-green-800">{selectBookSuccess}</p>
                </div>
              ) : (() => {
                const bookStatus = selectBookModal.status || '';
                const isPendingBook = ['upcoming', 'note_interest', 'draft'].includes(bookStatus);
                return (
                <div className="space-y-3">
                  {isPendingBook && (
                    <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-700">
                      <p>This book is coming soon! Join the mailing list to be notified when it becomes available.</p>
                    </div>
                  )}

                  {/* Option 1 — Request ARC + Join Mailing List (hidden for pending books) */}
                  {!isPendingBook && (campaignsByBook[selectBookModal.id] || []).some(c => c.authorId === selectBookModal.authorId) && (
                    <div
                      onClick={() => setSelectBookOption(selectBookOption === 1 ? null : 1)}
                      className={`cursor-pointer rounded-xl border-2 p-4 transition-all ${selectBookOption === 1 ? 'border-teal-500 bg-teal-50/50' : 'border-slate-200 bg-white hover:border-slate-300'}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-teal-100 text-teal-700">
                          <BookOpen className="h-5 w-5" />
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold text-slate-900">Request ARC + Join Mailing List</p>
                          <p className="text-sm text-slate-500">Get an advance copy and stay updated with author news</p>
                        </div>
                      </div>
                      {selectBookOption === 1 && (
                        <div className="mt-4 space-y-3" onClick={e => e.stopPropagation()}>
                          <div>
                            <Label className="mb-1 block text-sm text-slate-600">Why do you want to read this book? *</Label>
                            <Textarea rows={3} value={selectBookMessage} onChange={e => setSelectBookMessage(e.target.value)}
                              placeholder="Tell the author why you're interested..." />
                          </div>
                          <div className="rounded-lg bg-slate-50 p-3 text-xs text-slate-500">
                            <p className="font-semibold mb-1 text-slate-600">Your Stats:</p>
                            <p>{myReviews.length} reviews submitted &middot; {readerData?.totalPoints || 0} points earned</p>
                          </div>
                          <Button onClick={handleArcAndMailingList}
                            disabled={selectBookSubmitting || !selectBookMessage.trim()}
                            className="w-full bg-gradient-to-r from-slate-500 to-teal-600 py-2.5 font-semibold text-white hover:from-teal-600 hover:to-teal-700">
                            {selectBookSubmitting ? 'Submitting...' : 'Submit Request'}
                          </Button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Option 2 — Join Mailing List Only */}
                  <div
                    onClick={() => !selectBookSubmitting && setSelectBookOption(selectBookOption === (isPendingBook ? 2 : 2) ? null : 2)}
                    className={`cursor-pointer rounded-xl border-2 p-4 transition-all ${selectBookOption === 2 ? 'border-teal-500 bg-teal-50/50' : 'border-slate-200 bg-white hover:border-slate-300'}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-100 text-blue-700">
                        <Mail className="h-5 w-5" />
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-slate-900">Join Mailing List</p>
                        <p className="text-sm text-slate-500">{isPendingBook ? 'Be the first to know when this book is available' : 'Stay updated without requesting an ARC'}</p>
                      </div>
                    </div>
                    {selectBookOption === 2 && (
                      <div className="mt-4" onClick={e => e.stopPropagation()}>
                        <Button onClick={handleMailingListOnly} disabled={selectBookSubmitting}
                          className="w-full bg-gradient-to-r from-slate-500 to-teal-600 py-2.5 font-semibold text-white hover:from-teal-600 hover:to-teal-700">
                          {selectBookSubmitting ? 'Joining...' : 'Join Mailing List'}
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Option 3 — Order / Claim Book (hidden for pending books) */}
                  {!isPendingBook && (
                    <div
                      onClick={handleOrderBook}
                      className="cursor-pointer rounded-xl border-2 border-slate-200 bg-white p-4 transition-all hover:border-slate-300"
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-orange-100 text-orange-700">
                          <ShoppingCart className="h-5 w-5" />
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold text-slate-900">Order / Claim Book</p>
                          <p className="text-sm text-slate-500">Purchase or claim a free copy of this book</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                );
              })()}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Calendar Event Modal */}
      {showAddEvent && (() => {
        const isBookMode = newEvent.bookSelection && newEvent.bookSelection !== 'manual';
        const selectedCalBook = isBookMode ? allBooks.find(b => b.id === newEvent.bookSelection) : null;
        const inputCls = "w-full rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-slate-900 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200";
        const labelCls = "mb-1 block text-sm font-medium text-slate-600";
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4">
            <div className="w-full max-w-lg rounded-2xl border border-slate-100 bg-white shadow-2xl overflow-y-auto max-h-[90vh]">
              <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-100 bg-white px-6 py-4">
                <h3 className="text-xl font-bold text-slate-900">Add Calendar Event</h3>
                <button onClick={() => setShowAddEvent(false)} className="text-slate-400 hover:text-slate-600"><X className="h-5 w-5" /></button>
              </div>
              <form onSubmit={handleAddCalendarEvent} className="p-6 space-y-5">

                {/* Book selector */}
                <div>
                  <label className={labelCls}>Book *</label>
                  <select value={newEvent.bookSelection}
                    onChange={e => {
                      const val = e.target.value;
                      const book = allBooks.find(b => b.id === val);
                      setNewEvent({
                        ...newEvent,
                        bookSelection: val,
                        launchDate: book?.goLiveDate || book?.launchDate || book?.releaseDate || '',
                        reviewDate: book?.reviewReleaseDate || book?.reviewDeadline || '',
                        streetTeamDate: book?.streetTeamDate || '',
                        customTitle: '',
                      });
                    }}
                    className={inputCls} required>
                    <option value="">— Select a book —</option>
                    {myBooks.map(b => <option key={b.id} value={b.id}>{b.title}</option>)}
                    <option value="manual">✏️ Add manually (other book/event)</option>
                  </select>
                </div>

                {/* Manual title entry */}
                {newEvent.bookSelection === 'manual' && (
                  <div>
                    <label className={labelCls}>Event / Book Title *</label>
                    <input type="text" required value={newEvent.customTitle}
                      onChange={e => setNewEvent({...newEvent, customTitle: e.target.value})}
                      placeholder="e.g. A Court of Thorns and Roses — review due"
                      className={inputCls} />
                  </div>
                )}

                {/* Book mode: auto-populated dates */}
                {isBookMode && (
                  <div className="rounded-xl border border-slate-100 bg-slate-50/60 p-4 space-y-4">
                    <p className="text-xs font-semibold text-teal-700 uppercase tracking-wide">Key Dates for {selectedCalBook?.title}</p>

                    <div className="grid gap-3 sm:grid-cols-2">
                      <div>
                        <label className={labelCls}>🚀 Launch Date</label>
                        <input type="date" value={newEvent.launchDate}
                          onChange={e => setNewEvent({...newEvent, launchDate: e.target.value})}
                          className={inputCls} />
                      </div>
                      <div>
                        <label className={labelCls}>📝 Review Deadline</label>
                        <input type="date" value={newEvent.reviewDate}
                          onChange={e => setNewEvent({...newEvent, reviewDate: e.target.value})}
                          className={inputCls} />
                      </div>
                    </div>

                    <div>
                      <label className={labelCls}>⭐ Street Team Post Date (if applicable)</label>
                      <input type="date" value={newEvent.streetTeamDate}
                        onChange={e => setNewEvent({...newEvent, streetTeamDate: e.target.value})}
                        className={inputCls} />
                    </div>

                    {/* Street team status badge */}
                    {(() => {
                      const review = myReviews.find(r => r.bookId === newEvent.bookSelection);
                      const isStreetTeam = review?.readerPath === 'street-team';
                      return isStreetTeam ? (
                        <div className="flex items-center gap-2 rounded-lg bg-purple-50 border border-purple-200 px-3 py-2 text-xs font-semibold text-purple-800">
                          ⭐ You are a confirmed Street Team member for this book!
                        </div>
                      ) : null;
                    })()}
                  </div>
                )}

                {/* Manual mode: single date + type */}
                {newEvent.bookSelection === 'manual' && (
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <label className={labelCls}>Date *</label>
                      <input type="date" required value={newEvent.date}
                        onChange={e => setNewEvent({...newEvent, date: e.target.value})}
                        className={inputCls} />
                    </div>
                    <div>
                      <label className={labelCls}>Type</label>
                      <select value={newEvent.type} onChange={e => setNewEvent({...newEvent, type: e.target.value})} className={inputCls}>
                        <option value="launch">🚀 Book Launch</option>
                        <option value="review-deadline">📝 Review Deadline</option>
                        <option value="street-team">⭐ Street Team</option>
                        <option value="other">📌 Other Note</option>
                      </select>
                    </div>
                  </div>
                )}

                {/* Notes (always visible) */}
                <div>
                  <label className={labelCls}>Notes (optional)</label>
                  <textarea rows={2} value={newEvent.note}
                    onChange={e => setNewEvent({...newEvent, note: e.target.value})}
                    placeholder="Any extra details, reminders, links…"
                    className={inputCls} />
                </div>

                <div className="flex gap-3 pt-1">
                  <button type="submit" className="flex-1 rounded-lg bg-gradient-to-r from-slate-500 to-teal-600 py-2.5 font-semibold text-white hover:from-teal-600 hover:to-teal-700">
                    Save to Calendar
                  </button>
                  <button type="button" onClick={() => setShowAddEvent(false)} className="flex-1 rounded-lg border border-slate-200 bg-white py-2.5 font-semibold text-slate-700 hover:bg-slate-50">
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        );
      })()}

      {/* Add ARC Log Entry Modal */}
      {showAddArc && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4">
          <div className="w-full max-w-md rounded-2xl border border-slate-100 bg-white p-6 shadow-2xl">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-xl font-bold text-slate-900">Add ARC to Log</h3>
              <button onClick={() => setShowAddArc(false)} className="text-slate-400 hover:text-slate-600"><X className="h-5 w-5" /></button>
            </div>
            <form onSubmit={handleAddArcLogEntry} className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-600">Book Title *</label>
                <input type="text" required value={newArcEntry.bookTitle} onChange={e => setNewArcEntry({...newArcEntry, bookTitle: e.target.value})}
                  placeholder="e.g. A Court of Thorns and Roses"
                  className="w-full rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-slate-900 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200" />
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-600">Author</label>
                  <input type="text" value={newArcEntry.author} onChange={e => setNewArcEntry({...newArcEntry, author: e.target.value})}
                    placeholder="Sarah J Maas"
                    className="w-full rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-slate-900 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200" />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-600">ISBN</label>
                  <input type="text" value={newArcEntry.isbn} onChange={e => setNewArcEntry({...newArcEntry, isbn: e.target.value})}
                    placeholder="978-..."
                    className="w-full rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-slate-900 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200" />
                </div>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-600">Date Reviewed</label>
                <input type="date" value={newArcEntry.reviewedOn} onChange={e => setNewArcEntry({...newArcEntry, reviewedOn: e.target.value})}
                  className="w-full rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-slate-900 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200" />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-600">How did you receive the ARC?</label>
                <div className="grid grid-cols-2 gap-2">
                  {['Email (ePub/PDF)', 'NetGalley', 'BookSirens', 'Edelweiss', 'Physical Copy', 'Direct from Author', 'Other'].map(option => (
                    <label key={option} className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-xs font-medium transition-all ${newArcEntry.receivedVia === option ? 'border-teal-400 bg-slate-50 text-slate-800' : 'border-slate-200 text-slate-600 hover:border-slate-200'}`}>
                      <input type="radio" name="receivedVia" value={option} checked={newArcEntry.receivedVia === option}
                        onChange={e => setNewArcEntry({...newArcEntry, receivedVia: e.target.value})}
                        className="sr-only" />
                      {option}
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-600">Where did you post your review?</label>
                <div className="grid grid-cols-3 gap-2">
                  {PLATFORMS.map(p => (
                    <label key={p} className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-xs font-medium transition-all ${newArcEntry.platforms.includes(p) ? 'border-teal-400 bg-slate-50 text-slate-800' : 'border-slate-200 text-slate-600 hover:border-slate-200'}`}>
                      <input type="checkbox" checked={newArcEntry.platforms.includes(p)}
                        onChange={e => setNewArcEntry({...newArcEntry, platforms: e.target.checked ? [...newArcEntry.platforms, p] : newArcEntry.platforms.filter(x => x !== p)})}
                        className="sr-only" />
                      {p}
                    </label>
                  ))}
                </div>
              </div>
              <div className="flex gap-3">
                <button type="submit" className="flex-1 rounded-lg bg-gradient-to-r from-slate-500 to-teal-600 py-2.5 font-semibold text-white hover:from-teal-600 hover:to-teal-700">Add to Log</button>
                <button type="button" onClick={() => setShowAddArc(false)} className="flex-1 rounded-lg border border-slate-200 bg-white py-2.5 font-semibold text-slate-700 hover:bg-slate-50">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create Post Modal */}
      <Dialog open={showCreatePost} onOpenChange={(open) => { if (!open) { setShowCreatePost(false); setNewPostContent(''); } }}>
        <DialogContent className="max-w-lg rounded-2xl p-6">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-slate-900">Share with the Community</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreatePost} className="space-y-4">
            <Textarea rows={6} required value={newPostContent} onChange={e => setNewPostContent(e.target.value)}
              placeholder="Share your thoughts, favourite quotes, reading updates… (No spoilers please!)" />
            <div className="flex gap-3">
              <Button type="submit" disabled={!newPostContent.trim()} className="flex-1 bg-gradient-to-r from-slate-500 to-teal-600 py-2.5 font-semibold text-white hover:from-teal-600 hover:to-teal-700">Post</Button>
              <Button type="button" variant="outline" onClick={() => { setShowCreatePost(false); setNewPostContent(''); }} className="flex-1 py-2.5 font-semibold">Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
